import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import './App.css';

// Custom Components
import { 
  CreateWorkoutPlanDialog, 
  WorkoutPlansTab, 
  MyWorkoutPlansTab,
  WorkoutPlanDetailView
} from './WorkoutPlanComponents';
import { 
  CreateLiveStreamDialog, 
  LiveStreamsTab 
} from './LiveStreamComponents';
import { 
  CreateMealPlanDialog, 
  MealPlansTab, 
  MyMealPlansTab,
  MealPlanDetailView
} from './MealPlanComponents';
import { HowItWorksPage, CreatorsPage } from './InfoPages';
import { GamificationDashboard, ProfileBadgeSection, useActivityLogger, WorkoutCalendar } from './GamificationComponents';
import { DiscoverTab } from './DiscoverTab';
import { CreatorAnalytics } from './CreatorAnalytics';
import { AboutPage, CareersPage, SupportPage, TermsPage, PrivacyPolicyPage, CommunityGuidelinesPage, CopyrightPage, CookiePolicyPage } from './LegalPages';
import InvestPage from './InvestPage';
import { SupportArticlePage } from './SupportCenter';
import { SeoManager } from './seo/SeoManager';
import { NotFoundPage } from './NotFoundPage';
import { PaymentButton, PaymentSuccessPage, PaymentCancelledPage, PaymentSecurityBadge } from './PaymentComponents';
import { ReferralPanelModal } from './ReferralPanel';
import { 
  SubscriptionButton, 
  UserSubscriptions, 
  CreatorSubscribers,
  SubscriptionSuccessPage,
  SubscriptionCancelledPage
} from './SubscriptionComponents';
import { EarlyAdopterPerksModal } from './EarlyAdopterPerks';
import { AuthModal } from './AuthModal';
import AuthCallback from './AuthCallback';
import { MySubscribersPage, MySubscriptionsPage, PurchaseHistoryPage, FollowersFollowingPage } from './AccountPages';
import { 
  TipButton, 
  TipSuccessPage, 
  TipCancelledPage, 
  PostTipStats,
  TipSettingsForm
} from './TippingComponents';
import {
  CreatorPrivacySettingsModal, 
  PrivacySettingsPreview,
  LockedContentOverlay,
  LockedProfileBanner,
  LockedContentCard,
  PrivacyBadge
} from './PrivacyComponents';
import AdminDashboard from './AdminDashboard';
import {
  PostTypeBadge,
  PostTypeSelector,
  PostTypeFilters,
  PostTypeFilterDropdown,
  POST_TYPES,
  getPostTypeConfig,
  NicheBadge,
  NicheSelector
} from './PostTypeComponents';
import {
  WorkoutProgressWidget,
  CompleteWorkoutModal,
  WorkoutProgressDashboard,
  WeeklyGoalSetter,
  WorkoutHistory
} from './WorkoutProgressComponents';
// Note: StripeConnectComponents removed - Connect UI integrated directly into ProfessionalModeModal

// Shadcn UI Components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Icons
import { 
  Heart, 
  MessageCircle, 
  Share2, 
  Plus, 
  User, 
  Settings, 
  LogOut, 
  Dumbbell,
  Calendar,
  Users,
  TrendingUp,
  Play,
  Zap,
  Target,
  Award,
  Star,
  ChevronRight,
  ChevronLeft,
  Camera,
  Video,
  BookOpen,
  Apple,
  Clock,
  DollarSign,
  Trash2,
  Edit,
  ShoppingCart,
  Gift,
  Check,
  X,
  Activity,
  Timer,
  MapPin,
  Flame,
  Trophy,
  PlayCircle,
  Lock,
  Unlock,
  CheckCircle,
  Search,
  UserPlus,
  QrCode,
  Image,
  Eye,
  EyeOff,
  Globe,
  Shield,
  Briefcase,
  GraduationCap,
  Home,
  Phone,
  Mail,
  Link,
  Sparkles,
  Pin,
  Bookmark,
  BookMarked,
  AlertTriangle,
  MoreHorizontal,
  ChevronDown,
  Bell,
  BarChart3,
  Percent,
  Menu,
  FileText,
  Instagram,
  Twitter,
  Youtube,
  ArrowRight,
  Upload,
  CreditCard,
  MessageSquare,
  Gamepad2,
  Crown,
  RefreshCw,
  Loader2,
  AlertCircle,
  ExternalLink,
  ShoppingBag,
  Send,
  Pencil,
  Copy,
  Moon,
  Sun,
  MoreVertical,
  CalendarDays
} from 'lucide-react';

import { BACKEND_URL, API_URL, API } from './config';

// ── Accent theme definitions ──
const ACCENT_THEMES = [
  { id: 'blue', label: 'Ocean Blue', color: '#3b82f6', ring: 'ring-blue-400' },
  { id: 'emerald', label: 'Emerald', color: '#10b981', ring: 'ring-emerald-400' },
  { id: 'purple', label: 'Purple', color: '#8b5cf6', ring: 'ring-violet-400' },
  { id: 'rose', label: 'Rose', color: '#f43f5e', ring: 'ring-rose-400' },
];

const setAccentTheme = (id) => {
  document.documentElement.setAttribute('data-accent', id);
  localStorage.setItem('onlymass-accent', id);
};
// Config diagnostic — always log on load
console.log(`OnlyMass Backend URL resolved to: ${BACKEND_URL}`);

// Detect misconfigured backend URL (empty only — preview URLs are valid in dev)
const BACKEND_MISCONFIGURED = !BACKEND_URL;

// Ensure cookies (HttpOnly session_token) are sent with every request
axios.defaults.withCredentials = true;

// Media URL resolver — handles both /api/uploads/ (legacy) and /api/media/ (persistent) paths
const resolveMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return BACKEND_URL + url;
  return url;
};

// ── Video thumbnail extraction utility ──────────────────────────────────────
// Extracts a JPEG frame from a video file at ~1 second using Canvas.
// Returns a Blob that can be uploaded as an image.
const extractVideoFrame = (file) => {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;

    const cleanup = () => URL.revokeObjectURL(objectUrl);

    video.onloadeddata = () => {
      // Seek to 1 second or 25% of duration, whichever is smaller
      const seekTime = Math.min(1, video.duration * 0.25);
      video.currentTime = seekTime;
    };

    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = Math.min(video.videoWidth, 1280);
        canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth));
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          cleanup();
          resolve(blob);
        }, 'image/jpeg', 0.85);
      } catch {
        cleanup();
        resolve(null);
      }
    };

    video.onerror = () => { cleanup(); resolve(null); };
    // Safety timeout — don't hang if video can't be read
    setTimeout(() => { cleanup(); resolve(null); }, 8000);
  });
};

// Upload a poster thumbnail blob and return the URL
const uploadPosterBlob = async (blob) => {
  if (!blob) return null;
  try {
    const formData = new FormData();
    formData.append('file', new File([blob], 'poster.jpg', { type: 'image/jpeg' }));
    const resp = await axios.post(`${API}/upload/media?category=post`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return resp.data.url;
  } catch {
    return null;
  }
};

// Broken-image fallback: hides the element or shows a placeholder
const handleImgError = (e, hide = false) => {
  if (hide) {
    e.target.parentElement.style.display = 'none';
  } else {
    e.target.style.display = 'none';
  }
};

// Authentication Context
const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Admin/founder check — reuses the existing /api/admin/check convention
// (persisted is_admin flag OR ADMIN_EMAILS). One request per session, shared by
// every consumer via a module-level cached promise.
let _adminCheckPromise = null;
const resetAdminCheckCache = () => { _adminCheckPromise = null; };
const useIsAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!Cookies.get('session_token')) { setIsAdmin(false); return () => {}; }
    if (!_adminCheckPromise) {
      _adminCheckPromise = axios.get(`${API}/admin/check`)
        .then(r => r.data?.is_admin === true)
        .catch(() => false);
    }
    _adminCheckPromise.then(v => { if (!cancelled) setIsAdmin(v); });
    return () => { cancelled = true; };
  }, []);
  return isAdmin;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [sessionToken, setSessionToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN || undefined;
  const cookieOpts = {
    expires: 7,
    sameSite: 'Lax',
    secure: window.location.protocol === 'https:',
    path: '/',
    ...(cookieDomain ? { domain: cookieDomain } : {})
  };

  // Canonical current user: GET /api/me is the single source of truth. The
  // cookie is only a cache for instant first paint — it is always overwritten
  // by the backend response so renamed profiles, changed avatars and live
  // follower/following counts can never go stale.
  const refreshUser = useCallback(async (token) => {
    const authToken = token || Cookies.get('session_token');
    if (!authToken) return null;
    try {
      const { data } = await axios.get(`${API}/me`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(data);
      Cookies.set('user_data', JSON.stringify(data), cookieOpts);
      return data;
    } catch (error) {
      // 401 means the session was revoked (logout elsewhere / token_version
      // bump) — drop the stale identity instead of rendering it.
      if (error.response?.status === 401) {
        setUser(null);
        setSessionToken(null);
        Cookies.remove('session_token', cookieOpts);
        Cookies.remove('user_data', cookieOpts);
      } else {
        console.error('[Auth] Failed to refresh current user:', error.message);
      }
      return null;
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const savedToken = Cookies.get('session_token');
    const savedUser = Cookies.get('user_data');
    if (savedToken) {
      setSessionToken(savedToken);
      if (savedUser) {
        try {
          setUser(JSON.parse(savedUser));
        } catch (error) {
          console.error('Error loading saved user:', error);
          Cookies.remove('user_data', cookieOpts);
        }
      }
      // Always reconcile against the backend.
      refreshUser(savedToken).finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, [refreshUser]); // eslint-disable-line

  const login = (userData, token) => {
    resetAdminCheckCache();
    setUser(userData);
    setSessionToken(token);
    Cookies.set('session_token', token, cookieOpts);
    Cookies.set('user_data', JSON.stringify(userData), cookieOpts);
    // Pull the canonical record so the sidebar never shows a login snapshot.
    refreshUser(token);
    axios.post(`${API}/init-demo-user`, {}, { withCredentials: true }).catch(console.error);
  };

  const logout = () => {
    resetAdminCheckCache();
    setUser(null);
    setSessionToken(null);
    Cookies.remove('session_token', cookieOpts);
    Cookies.remove('user_data', cookieOpts);
    fetch(`${API}/auth/logout`, { method: 'POST', credentials: 'include' }).catch(console.error);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading OnlyMass...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, sessionToken, login, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Landing Page Component
const LandingPage = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showCreators, setShowCreators] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  // Already-authenticated users hitting "/" should go straight to the app.
  React.useEffect(() => {
    if (user) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Phase R1: when a visitor arrives via /signup?ref=<code> (or any URL with
  // ?ref=<code>), auto-open the AuthModal in signup mode so the inviter banner
  // renders and the referral code is captured/persisted by AuthModal itself.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      const onSignupPath = window.location.pathname.startsWith('/signup');
      if (ref || onSignupPath) {
        setAuthMode('signup');
        setShowAuthModal(true);
      }
    } catch (e) { /* noop */ }
  }, []);

  if (showHowItWorks) {
    return <HowItWorksPage onClose={() => setShowHowItWorks(false)} />;
  }

  if (showCreators) {
    return <CreatorsPage onClose={() => setShowCreators(false)} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <AuthModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode={authMode}
        onLoginSuccess={login}
      />
      
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-200/60 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center space-x-2.5 hover:opacity-80 transition-opacity" data-testid="nav-brand">
              <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold text-[#0f1d3d] tracking-tight">OnlyMass</span>
            </button>
            
            <div className="hidden lg:flex items-center space-x-1">
              <button onClick={() => setShowCreators(true)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#0f1d3d] hover:bg-slate-100 rounded-lg transition-colors">
                For Creators
              </button>
              <button onClick={() => setShowHowItWorks(true)} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-[#0f1d3d] hover:bg-slate-100 rounded-lg transition-colors">
                How It Works
              </button>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button 
                onClick={() => { setAuthMode('login'); setShowAuthModal(true); }} 
                variant="ghost" 
                className="text-slate-600 hover:text-[#0f1d3d] font-medium text-sm"
              >
                Log In
              </Button>
              <Button 
                onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} 
                className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white font-semibold px-5 text-sm"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-20 pb-24 overflow-hidden bg-gradient-to-b from-white to-blue-50/50">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-7">
              <div className="inline-flex items-center gap-3" data-testid="hero-brand-lockup">
                <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl" />
                <span className="text-4xl sm:text-5xl font-bold text-[#0f1d3d] tracking-tight">OnlyMass</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
                <span className="text-xs font-medium text-blue-700 tracking-wide">Now open for creators & fans</span>
              </div>
              
              <div className="space-y-4">
                <h1 className="text-5xl lg:text-6xl font-bold leading-[1.08] text-[#0f1d3d] tracking-tight">
                  Your Favorite <br/>
                  <span className="text-blue-600">Trainers.</span> Unlocked.
                </h1>
                <p className="text-lg text-slate-500 leading-relaxed max-w-md">
                  Exclusive workouts, nutrition plans, and coaching from real fitness creators — all in one platform.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} 
                  className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white font-semibold px-7 py-3 text-base"
                >
                  Get Started Free
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  onClick={() => setShowCreators(true)} 
                  variant="outline" 
                  className="border-slate-300 text-slate-700 hover:bg-slate-50 px-7 py-3 text-base"
                >
                  Learn More
                </Button>
              </div>

            </div>

            <div className="relative hidden lg:block">
              <div className="relative z-10 grid grid-cols-2 gap-3">
                <img src="https://images.unsplash.com/photo-1595078475328-1ab05d0a6a0e" alt="Fitness Training" className="rounded-2xl aspect-[4/5] object-cover shadow-lg" />
                <div className="space-y-3">
                  <img src="/landing/live-fitness.jpeg" alt="Train with the best" className="rounded-2xl aspect-square object-cover shadow-lg" />
                  <div className="grid grid-cols-2 gap-3">
                    <img src="https://images.unsplash.com/photo-1534258936925-c58bed479fcb" alt="Community" className="rounded-xl aspect-square object-cover shadow-md" />
                    <img src="https://images.unsplash.com/photo-1543353071-c953d88f7033" alt="Nutrition" className="rounded-xl aspect-square object-cover shadow-md" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '85%', label: 'Creator Earnings' },
              { value: '25+', label: 'Fitness Niches' },
              { value: '100%', label: 'Fitness Focused' },
              { value: '24/7', label: 'Content Access' },
            ].map((stat, i) => (
              <div key={i}>
                <p className="text-2xl font-bold text-[#0f1d3d]">{stat.value}</p>
                <p className="text-xs text-slate-400 mt-1 uppercase tracking-wider font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-white" id="how-it-works">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider">How It Works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1d3d]">Three steps to exclusive fitness</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Upload, title: 'Creators Publish', desc: 'Workout programs, nutrition guides, and exclusive training content — structured and complete.', num: '01' },
              { icon: CreditCard, title: 'Fans Subscribe', desc: "Choose your favorite trainers and get full access to their programs and coaching library.", num: '02' },
              { icon: TrendingUp, title: 'Grow Together', desc: 'Track progress, join challenges, and build real results with direct creator engagement.', num: '03' },
            ].map((step, i) => (
              <div key={i} className="relative p-7 rounded-2xl bg-slate-50 border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all group">
                <span className="text-6xl font-bold text-slate-100 absolute top-3 right-5 group-hover:text-blue-100 transition-colors">{step.num}</span>
                <div className="w-11 h-11 bg-[#0f1d3d] rounded-xl flex items-center justify-center mb-5 shadow-sm">
                  <step.icon className="h-5 w-5 text-blue-300" />
                </div>
                <h3 className="text-lg font-semibold text-[#0f1d3d] mb-2">{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why OnlyMass */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider">Platform Benefits</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1d3d]">Why OnlyMass?</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Flame, title: 'Exclusive Content', desc: "Full programs, not just clips. Content you won't find on Instagram or TikTok.", gradient: 'from-orange-500 to-red-500' },
              { icon: DollarSign, title: 'Creators Keep More', desc: 'Up to 85% earnings. Industry-leading revenue split for fitness creators.', gradient: 'from-emerald-500 to-green-600' },
              { icon: Dumbbell, title: 'Structured Programs', desc: 'Complete workout programs with exercises, sets, reps, and direct coaching.', gradient: 'from-blue-600 to-cyan-500' },
              { icon: Shield, title: 'Safe Environment', desc: 'Fitness-only, professional and safe. Built for health and wellness.', gradient: 'from-violet-500 to-purple-600' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all">
                <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-[#0f1d3d] mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gamification */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-16">
            <p className="text-sm font-semibold text-purple-600 uppercase tracking-wider">Gamification</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1d3d]">Level Up Your Fitness Journey</h2>
            <p className="text-slate-500 max-w-xl mx-auto">Earn badges, track streaks, and compete through our achievement system</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Flame, title: 'Daily Streaks', desc: 'Build momentum with daily workout streaks and earn streak badges.', gradient: 'from-orange-500 to-red-500' },
              { icon: Star, title: 'XP Points', desc: 'Earn points for every workout, post, and interaction. Level up!', gradient: 'from-amber-500 to-orange-500' },
              { icon: Award, title: 'Achievements', desc: 'Collect badges for workout milestones, challenges, and fitness goals.', gradient: 'from-purple-500 to-pink-500' },
              { icon: Target, title: 'Progress Tracking', desc: 'Visual progress bars. Track workouts and watch your stats grow.', gradient: 'from-blue-500 to-purple-500' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-slate-50 border border-slate-100 hover:border-purple-200 hover:shadow-md transition-all">
                <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-[#0f1d3d] mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Creators */}
      <section className="py-24 bg-[#0f1d3d]" id="for-creators">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-14">
            <p className="text-sm font-semibold text-blue-300 uppercase tracking-wider">For Creators</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Monetize Your Expertise</h2>
            <p className="text-blue-200/70 max-w-lg mx-auto">Turn your fitness knowledge into a sustainable business. Start earning today.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {[
              { icon: Percent, title: 'Premium Revenue', desc: 'Keep up to 85% of your earnings. Industry-leading split for fitness creators.' },
              { icon: Star, title: 'Discovery & Growth', desc: 'Get discovered through niche-based search, recommendations, and featured placements.' },
              { icon: BarChart3, title: 'Analytics Dashboard', desc: 'Track followers, subscribers, revenue, engagement, and content performance.' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/[0.06] border border-white/10 hover:border-white/20 transition-colors">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-4">
                  <item.icon className="h-5 w-5 text-blue-300" />
                </div>
                <h3 className="text-base font-semibold text-white mb-1.5">{item.title}</h3>
                <p className="text-sm text-blue-200/60 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} className="bg-white text-[#0f1d3d] hover:bg-blue-50 font-semibold px-7 py-3 text-base">
              Start as Creator
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Revenue Split */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 rounded-2xl border border-slate-200 bg-slate-50">
            <div className="text-center mb-8">
              <h3 className="text-xl font-bold text-[#0f1d3d] mb-1">Creator Revenue Split</h3>
              <p className="text-sm text-slate-500">Industry-leading earnings for fitness creators</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-5 rounded-xl bg-white border border-slate-200">
                <div className="text-3xl font-bold text-[#0f1d3d] mb-1">80/20</div>
                <div className="text-xs font-semibold text-slate-600 mb-0.5">Standard Split</div>
                <p className="text-xs text-slate-400">You keep 80%</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="text-3xl font-bold text-emerald-600 mb-1">85/15</div>
                <div className="text-xs font-semibold text-emerald-700 mb-0.5">Early Adopter</div>
                <p className="text-xs text-slate-500">First 3 months for new creators</p>
              </div>
              <div className="text-center p-5 rounded-xl bg-blue-50 border border-blue-200">
                <div className="text-3xl font-bold text-blue-600 mb-1">85/15</div>
                <div className="text-xs font-semibold text-blue-700 mb-0.5">Founding Creator</div>
                <p className="text-xs text-slate-500">Permanent for early members</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Fans */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-14">
            <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider">For Fans</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f1d3d]">Get Closer to Your Favorite Trainers</h2>
            <p className="text-slate-500 max-w-lg mx-auto">Subscribe for exclusive workouts, nutrition plans, and direct coaching</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
            {[
              { icon: Crown, title: 'Subscriptions', desc: 'Subscribe to your favorite creators with flexible pricing tiers.', gradient: 'from-purple-500 to-pink-500' },
              { icon: Gamepad2, title: 'Challenges', desc: 'Join community challenges and compete with other members.', gradient: 'from-blue-600 to-cyan-500' },
              { icon: MessageSquare, title: 'Direct Access', desc: 'Message creators and join live Q&A sessions.', gradient: 'from-blue-500 to-indigo-500' },
              { icon: BookOpen, title: 'Full Programs', desc: 'Complete workout and nutrition plans, not snippets.', gradient: 'from-cyan-500 to-teal-500' },
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white border border-slate-100 hover:border-blue-200 hover:shadow-md transition-all">
                <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-lg flex items-center justify-center mb-4 shadow-sm`}>
                  <item.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-[#0f1d3d] mb-1.5">{item.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white font-semibold px-7 py-3 text-base">
              Start Exploring
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Challenge Teaser */}
      <section className="py-16 bg-gradient-to-r from-orange-50 to-red-50 border-y border-orange-100/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-5">
            <div className="inline-flex items-center px-4 py-1.5 bg-orange-100 border border-orange-200 rounded-full">
              <Gamepad2 className="h-4 w-4 text-orange-600 mr-2" />
              <span className="text-sm text-orange-700 font-semibold">#MassChallenge30</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f1d3d]">A 30-day fitness challenge to push your limits</h2>
            <p className="text-slate-500">Teasers on social, full program only on OnlyMass.</p>
            <Button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} className="bg-orange-600 hover:bg-orange-500 text-white font-semibold px-7 py-3 shadow-sm">
              Join the Challenge
              <Flame className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-[#0f1d3d]">
        <div className="max-w-3xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Stop Scrolling. Start Training.</h2>
            <p className="text-blue-200/70 text-lg">Join OnlyMass today and train with the best.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={() => { setAuthMode('signup'); setShowAuthModal(true); }} className="bg-white text-[#0f1d3d] hover:bg-blue-50 font-semibold px-7 py-3 text-base">
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={() => setShowCreators(true)} variant="outline" className="border-white/20 text-white hover:bg-white/10 px-7 py-3 text-base">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0b1528] border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-3">
              <div className="flex items-center space-x-2">
                <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-7 h-7 rounded-lg" />
                <span className="text-lg font-bold text-white">OnlyMass</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">The exclusive platform for fitness creators and fans.</p>
            </div>
            <div className="space-y-3">
              <h3 className="text-white font-semibold text-sm">Company</h3>
              <div className="space-y-2 text-sm">
                <RouterLink to="/about" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-about">About</RouterLink>
                <RouterLink to="/careers" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-careers">Careers</RouterLink>
                <RouterLink to="/support" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-support">Support</RouterLink>
                <RouterLink to="/invest" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-investors">Investors</RouterLink>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-white font-semibold text-sm">Legal</h3>
              <div className="space-y-2 text-sm">
                <RouterLink to="/terms" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-terms">Terms of Service</RouterLink>
                <RouterLink to="/privacy" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-privacy">Privacy Policy</RouterLink>
                <RouterLink to="/community-guidelines" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-guidelines">Community Guidelines</RouterLink>
                <RouterLink to="/copyright" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-copyright">Copyright / DMCA</RouterLink>
                <RouterLink to="/cookies" className="text-gray-500 hover:text-gray-300 block transition-colors" data-testid="footer-cookies">Cookie Policy</RouterLink>
              </div>
            </div>
            <div className="space-y-3">
              <h3 className="text-white font-semibold text-sm">Follow Us</h3>
              <div className="flex space-x-3">
                <a href="https://www.instagram.com/onlymass2025/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-instagram">
                  <Instagram className="h-5 w-5" />
                </a>
                <a href="https://x.com/OnlyMass2025" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 transition-colors" data-testid="footer-twitter">
                  <Twitter className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/[0.04] text-center">
            <p className="text-gray-600 text-xs">&copy; {new Date().getFullYear()} OnlyMass. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};


// User Search Component
const UserSearch = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data);
      setShowResults(true);
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchUsers(query);
    }, 300);
  };

  const handleUserClick = (userId) => {
    // Navigate to user profile
    setShowResults(false);
    setSearchQuery('');
    navigate(`/profile/${userId}`);
  };

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search users..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="pl-10 w-48 sm:w-64"
          onFocus={() => searchQuery && setShowResults(true)}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
        />
      </div>

      {showResults && (
        <div className="absolute top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
          {isSearching ? (
            <div className="p-3 text-center text-gray-500">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span className="ml-2">Searching...</span>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="py-2">
              {searchResults.map((user) => (
                <div
                  key={user.id}
                  onClick={() => handleUserClick(user.id)}
                  className="flex items-center space-x-3 px-4 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={resolveMediaUrl(user.profile_picture)} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 text-sm">
                      {user.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                    {user.is_creator && (
                      <p className="text-xs text-gray-500 truncate">Creator</p>
                    )}
                  </div>
                  {user.is_creator && (
                    <Badge className="bg-blue-100 text-blue-700 text-xs">Creator</Badge>
                  )}
                  <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700">
                    <UserPlus className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-3 text-center text-gray-500 text-sm">
              No users found for "{searchQuery}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Notifications Panel Component
const NotificationsPanel = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const panelRef = React.useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    const token = Cookies.get('session_token');
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(data.count || 0);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    const token = Cookies.get('session_token');
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(data || []);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const markAllRead = async () => {
    const token = Cookies.get('session_token');
    if (!token) return;
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setUnreadCount(0);
      setNotifications(ns => ns.map(n => ({ ...n, is_read: true })));
    } catch {}
  };

  const handleClick = async (n) => {
    // Mark single notification read
    const token = Cookies.get('session_token');
    if (token && !n.is_read) {
      axios.post(`${API}/notifications/${n.id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
      setNotifications(ns => ns.map(x => x.id === n.id ? { ...x, is_read: true } : x));
      setUnreadCount(c => Math.max(0, c - 1));
    }
    setOpen(false);
    // Deep-link by target_type + target_id when available; fallback to type-based routing
    const targetType = n.target_type;
    const targetId = n.target_id;
    if (targetType === 'challenge' && targetId) {
      navigate(`/dashboard?openChallenge=${targetId}`);
      return;
    }
    if (targetType === 'workout_plan' && targetId) {
      navigate(`/dashboard?openWorkoutPlan=${targetId}`);
      return;
    }
    if (targetType === 'meal_plan' && targetId) {
      navigate(`/dashboard?openMealPlan=${targetId}`);
      return;
    }
    if (targetType === 'post' && targetId) {
      // Open the exact post in a universal deep-link modal on the dashboard
      navigate(`/dashboard?post=${targetId}`);
      return;
    }
    if (targetType === 'profile' && targetId) {
      navigate(`/profile/${targetId}`);
      return;
    }
    // Fallback based on notification type
    switch (n.type) {
      case 'post_like':
      case 'post_comment':
      case 'comment_reply':
      case 'tip':
        navigate(`/profile/${n.recipient_id}`);
        break;
      case 'follow':
      case 'subscribe':
      case 'plan_purchase':
        if (n.actor_id) navigate(`/profile/${n.actor_id}`);
        else navigate('/dashboard');
        break;
      case 'challenge_joined':
      case 'challenge_submission':
      case 'challenge_approved':
      case 'challenge_rejected':
      case 'challenge_winner':
        navigate('/dashboard');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const NOTIF_ICONS = {
    post_like: { icon: Heart, color: 'text-rose-500 bg-rose-50' },
    post_comment: { icon: MessageCircle, color: 'text-blue-500 bg-blue-50' },
    comment_reply: { icon: MessageCircle, color: 'text-violet-500 bg-violet-50' },
    follow: { icon: UserPlus, color: 'text-emerald-500 bg-emerald-50' },
    subscribe: { icon: Crown, color: 'text-amber-500 bg-amber-50' },
    plan_purchase: { icon: ShoppingCart, color: 'text-teal-500 bg-teal-50' },
    tip: { icon: Gift, color: 'text-pink-500 bg-pink-50' },
    challenge_joined: { icon: Flame, color: 'text-amber-500 bg-amber-50' },
    challenge_submission: { icon: Camera, color: 'text-amber-500 bg-amber-50' },
    challenge_approved: { icon: CheckCircle, color: 'text-emerald-500 bg-emerald-50' },
    challenge_rejected: { icon: X, color: 'text-red-500 bg-red-50' },
    challenge_winner: { icon: Trophy, color: 'text-amber-500 bg-amber-50' },
    // R1 polish (2026-06-26): celebratory mapping for referral reward unlock.
    referral_reward_unlocked: { icon: Gift, color: 'text-emerald-600 bg-emerald-50' },
    // R1 polish (2026-06-27): second+ invitee qualifies while reward is already
    // active. Uses Trophy + emerald to celebrate without implying a 2nd discount.
    referral_invitee_qualified: { icon: Trophy, color: 'text-emerald-600 bg-emerald-50' },
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        data-testid="notifications-bell"
      >
        <Bell className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="unread-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[22rem] max-h-[28rem] bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden" data-testid="notifications-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-[11px] text-blue-600 hover:text-blue-700 font-medium" data-testid="mark-all-read">
                Mark all read
              </button>
            )}
          </div>
          <div className="overflow-y-auto max-h-[24rem]">
            {loading ? (
              <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 px-4">
                <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-0.5">Activity on your content will appear here</p>
              </div>
            ) : (
              <>
                {(showAll ? notifications : notifications.slice(0, 5)).map(n => {
                  const cfg = NOTIF_ICONS[n.type] || { icon: Bell, color: 'text-gray-500 bg-gray-50' };
                  const IconComp = cfg.icon;
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 ${!n.is_read ? 'bg-blue-50/40' : ''}`}
                      data-testid={`notification-${n.id}`}
                    >
                      <div className="relative shrink-0">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={resolveMediaUrl(n.actor_avatar)} />
                          <AvatarFallback className="bg-gray-100 text-gray-600 text-xs">{n.actor_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className={`absolute -bottom-1 -right-1 p-0.5 rounded-full ${cfg.color}`}>
                          <IconComp className="h-2.5 w-2.5" />
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs ${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{n.message}</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">
                          {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                      {!n.is_read && <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />}
                    </button>
                  );
                })}
                {!showAll && notifications.length > 5 && (
                  <button
                    onClick={() => setShowAll(true)}
                    className="w-full py-2.5 text-center text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors border-t border-gray-100"
                    data-testid="show-all-notifications"
                  >
                    Show all ({notifications.length})
                  </button>
                )}
                {showAll && notifications.length > 5 && (
                  <button
                    onClick={() => setShowAll(false)}
                    className="w-full py-2.5 text-center text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors border-t border-gray-100"
                  >
                    Show less
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Profile Dropdown Component
const ProfileDropdown = ({ user, updateUser, onLogout }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showPrivacySettings, setShowPrivacySettings] = useState(false);
  const [showCreatorPrivacySettings, setShowCreatorPrivacySettings] = useState(false);
  const [showProfessionalMode, setShowProfessionalMode] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => setShowProfileMenu(!showProfileMenu)}
        className="relative"
      >
        <Avatar className="h-8 w-8 ring-2 ring-transparent hover:ring-blue-200 transition-all">
          <AvatarImage src={resolveMediaUrl(user?.profile_picture)} />
          <AvatarFallback className="bg-blue-100 text-blue-600">
            {user?.display_name?.charAt(0) || user?.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <ChevronDown className="absolute -bottom-1 -right-1 h-3.5 w-3.5 bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-200 rounded-full border border-gray-200 dark:border-gray-600 p-px" />
      </button>

      {showProfileMenu && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowProfileMenu(false)} />
          <div className="absolute right-0 mt-2 w-[min(20rem,calc(100vw-2rem))] max-h-[calc(100vh-5rem)] overflow-y-auto bg-white dark:bg-[#111a2e] border border-gray-200 dark:border-[#1e2d4a] rounded-lg shadow-lg z-50">
          {/* Profile Header */}
          <div className="p-4 border-b border-gray-100 dark:border-[#162038]">
            <div className="flex items-center space-x-3 min-w-0">
              <Avatar className="h-12 w-12 shrink-0">
                <AvatarImage src={resolveMediaUrl(user?.profile_picture)} />
                <AvatarFallback className="bg-blue-100 text-blue-600 text-lg">
                  {user?.display_name?.charAt(0) || user?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{user?.display_name || user?.name}</h3>
                {user?.bio && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-1">{user.bio}</p>}
                {user?.is_creator && (
                  <Badge className="bg-blue-100 text-blue-700 px-2 py-1 text-xs mt-1">
                    Creator
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Menu Options */}
          <div className="py-2">
            <MenuItem
              icon={User}
              label="My Profile"
              description="View your public profile"
              onClick={() => {
                window.location.href = `/profile/${user?.id}`;
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={Settings}
              label="Account & Profile Settings"
              description="Edit profile, security, and preferences"
              onClick={() => {
                setShowAccountSettings(true);
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={Share2}
              label="Share Profile"
              description="Send your profile link to your audience"
              onClick={() => {
                setShowQRCode(true);
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={Crown}
              label={user?.is_creator ? "My Subscribers" : "My Subscriptions"}
              description={user?.is_creator ? "View your subscriber list" : "Creators you're subscribed to"}
              onClick={() => {
                window.location.href = user?.is_creator ? '/my-subscribers' : '/my-subscriptions';
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={ShoppingBag}
              label="Purchase History"
              description="Your purchases, subscriptions, and tips"
              onClick={() => {
                window.location.href = '/purchase-history';
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={Heart}
              label="Connections"
              description="Followers and people you follow"
              onClick={() => {
                window.location.href = '/connections';
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={Shield}
              label="Privacy Settings"
              description="Control who can see your content"
              onClick={() => {
                if (user?.is_creator) {
                  setShowCreatorPrivacySettings(true);
                } else {
                  setShowPrivacySettings(true);
                }
                setShowProfileMenu(false);
              }}
            />
            {/* Theme Toggle */}
            <MenuItem
              icon={document.documentElement.classList.contains('dark') ? Sun : Moon}
              label={document.documentElement.classList.contains('dark') ? 'Light Mode' : 'Dark Mode'}
              description="Switch appearance"
              onClick={() => {
                const isDark = document.documentElement.classList.contains('dark');
                document.documentElement.classList.toggle('dark', !isDark);
                localStorage.setItem('onlymass-theme', isDark ? 'light' : 'dark');
                setShowProfileMenu(false);
              }}
            />
            <MenuItem
              icon={Briefcase}
              label="Professional Mode"
              description="Creator insights and monetization"
              onClick={() => {
                setShowProfessionalMode(true);
                setShowProfileMenu(false);
              }}
            />

            {/* Separator + Log Out (canonical placement) */}
            <div className="border-t border-gray-100 dark:border-[#1e2d4a] my-1" />
            <button
              onClick={() => {
                setShowProfileMenu(false);
                if (onLogout) onLogout();
              }}
              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              data-testid="profile-dropdown-logout"
            >
              <span className="mt-0.5 flex-shrink-0 w-9 h-9 rounded-lg bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
                <LogOut className="h-4 w-4 text-red-600" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-red-600 dark:text-red-400">Log Out</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400">End your OnlyMass session</span>
              </span>
            </button>
          </div>
          </div>
        </>
      )}

      {/* QR Code Modal */}
      <QRCodeModal
        open={showQRCode}
        onClose={() => setShowQRCode(false)}
        user={user}
      />

      {/* Account Settings Modal */}
      <AccountSettingsModal
        open={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
        user={user}
        updateUser={updateUser}
      />

      {/* Privacy Settings Modal */}
      <PrivacySettingsModal
        open={showPrivacySettings}
        onClose={() => setShowPrivacySettings(false)}
        user={user}
        updateUser={updateUser}
      />

      {/* Creator Privacy Settings Modal */}
      <CreatorPrivacySettingsModal
        open={showCreatorPrivacySettings}
        onClose={() => setShowCreatorPrivacySettings(false)}
        userId={user?.id}
      />

      {/* Professional Mode Modal */}
      <ProfessionalModeModal
        open={showProfessionalMode}
        onClose={() => setShowProfessionalMode(false)}
        user={user}
        updateUser={updateUser}
      />
    </div>
  );
};

// Menu Item Component
const MenuItem = ({ icon: Icon, label, description, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center space-x-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#162038] transition-colors text-left min-w-0"
  >
    <Icon className="h-5 w-5 text-gray-600 dark:text-gray-400 shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{description}</p>
    </div>
  </button>
);

// Account Settings Modal Component — unified profile + account management
const AccountSettingsModal = ({ open, onClose, user, updateUser }) => {
  const { refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState({});

  // Profile data
  const [profileData, setProfileData] = useState({
    name: '', bio: '', profile_picture: '', cover_photo: '', featured_photos: [], featured_posters: {},
  });
  // Account data
  const [accountData, setAccountData] = useState({
    email: '', current_password: '', new_password: '', confirm_password: '',
    notifications_email: true, notifications_push: true,
    two_factor_enabled: false, language: 'en', timezone: 'UTC',
  });

  useEffect(() => {
    if (open && user) {
      setProfileData({
        name: user.name || '', bio: user.bio || '',
        profile_picture: user.profile_picture || '',
        cover_photo: user.cover_photo || '',
        featured_photos: user.featured_photos || [],
        featured_posters: user.featured_posters || {},
      });
      setAccountData({
        email: user.email || '', current_password: '', new_password: '', confirm_password: '',
        notifications_email: user.notifications_email ?? true,
        notifications_push: user.notifications_push ?? true,
        two_factor_enabled: user.two_factor_enabled ?? false,
        language: user.language || 'en', timezone: user.timezone || 'UTC',
      });
    }
  }, [open, user]);

  const handleUpload = async (file, category) => {
    setUploading(prev => ({ ...prev, [category]: true }));
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${API}/upload/media?category=${category}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${Cookies.get('session_token')}` }
      });
      return resp.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      return null;
    } finally {
      setUploading(prev => ({ ...prev, [category]: false }));
    }
  };

  const handleSave = async () => {
    if (accountData.new_password && accountData.new_password !== accountData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const token = Cookies.get('session_token');
      // Build payload — only send changed profile fields
      const payload = {};
      if (profileData.name !== (user?.name || '')) payload.name = profileData.name;
      if (profileData.bio !== (user?.bio || '')) payload.bio = profileData.bio;
      if (profileData.profile_picture !== (user?.profile_picture || '')) payload.profile_picture = profileData.profile_picture;
      if (profileData.cover_photo !== (user?.cover_photo || '')) payload.cover_photo = profileData.cover_photo;
      const oldPhotos = JSON.stringify(user?.featured_photos || []);
      const newPhotos = JSON.stringify(profileData.featured_photos || []);
      if (newPhotos !== oldPhotos) payload.featured_photos = profileData.featured_photos;
      const oldPosters = JSON.stringify(user?.featured_posters || {});
      const newPostersJson = JSON.stringify(profileData.featured_posters || {});
      if (newPostersJson !== oldPosters) payload.featured_posters = profileData.featured_posters;

      if (Object.keys(payload).length > 0) {
        await axios.put(`${BACKEND_URL}/api/users/profile`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Pull the canonical record so the sidebar reflects the saved profile
      if (refreshUser) await refreshUser();

      // Update local user state
      const merged = {
        ...user, ...profileData,
        email: accountData.email,
        notifications_email: accountData.notifications_email,
        notifications_push: accountData.notifications_push,
        two_factor_enabled: accountData.two_factor_enabled,
        language: accountData.language,
        timezone: accountData.timezone,
      };
      updateUser(merged);
      toast.success('Settings saved!');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'visuals', label: 'Photos', icon: Camera },
    { id: 'security', label: 'Security', icon: Lock },
    { id: 'preferences', label: 'Preferences', icon: Globe },
  ];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="account-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Settings className="h-5 w-5 text-blue-600" />
            <span>Account & Profile Settings</span>
          </DialogTitle>
          <DialogDescription>Manage your profile, security, and preferences</DialogDescription>
        </DialogHeader>

        <div className="flex max-h-[calc(90vh-10rem)] min-h-0">
          {/* Sidebar tabs */}
          <div className="w-40 sm:w-48 border-r border-gray-200 pr-3 space-y-1 shrink-0 overflow-y-auto">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm transition-colors ${
                    activeTab === tab.id ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`settings-tab-${tab.id}`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto pl-6 space-y-5">
            {activeTab === 'profile' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Display Name</label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                    placeholder="Your name"
                    data-testid="settings-display-name"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Bio</label>
                  <Textarea
                    value={profileData.bio}
                    onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                    placeholder="Tell the world about yourself..."
                    rows={3}
                    data-testid="settings-bio"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <Input
                    value={accountData.email}
                    disabled
                    className="bg-gray-50 text-gray-500"
                    data-testid="settings-email"
                  />
                  <p className="text-xs text-gray-400">Email cannot be changed here</p>
                </div>
              </div>
            )}

            {activeTab === 'visuals' && (
              <div className="space-y-5">
                {/* Profile Picture */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Profile Picture</label>
                  <div className="flex items-center gap-4">
                    <Avatar className="h-20 w-20">
                      <AvatarImage src={resolveMediaUrl(profileData.profile_picture)} />
                      <AvatarFallback className="bg-blue-100 text-blue-600 text-xl">{profileData.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <input type="file" accept="image/*" className="hidden" id="settings-pfp-upload"
                        onChange={async (e) => {
                          const url = await handleUpload(e.target.files[0], 'profile');
                          if (url) setProfileData(p => ({ ...p, profile_picture: url }));
                        }}
                      />
                      <label htmlFor="settings-pfp-upload">
                        <Button variant="outline" size="sm" asChild className="cursor-pointer">
                          <span>{uploading.profile ? 'Uploading...' : 'Change Photo'}</span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>
                {/* Cover Photo */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Cover Photo</label>
                  <div className="relative h-32 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    {profileData.cover_photo ? (
                      <img src={resolveMediaUrl(profileData.cover_photo)} alt="Cover" className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full text-gray-400 text-sm">No cover photo</div>
                    )}
                  </div>
                  <input type="file" accept="image/*" className="hidden" id="settings-cover-upload"
                    onChange={async (e) => {
                      const url = await handleUpload(e.target.files[0], 'cover');
                      if (url) setProfileData(p => ({ ...p, cover_photo: url }));
                    }}
                  />
                  <label htmlFor="settings-cover-upload">
                    <Button variant="outline" size="sm" asChild className="cursor-pointer">
                      <span>{uploading.cover ? 'Uploading...' : 'Change Cover'}</span>
                    </Button>
                  </label>
                </div>
                {/* Featured Photos & Videos */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Featured Photos & Videos</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(profileData.featured_photos || []).map((media, i) => {
                      const lower = (media || '').toLowerCase();
                      const isVid = lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.avi') || lower.includes('/video');
                      const posterUrl = isVid ? (profileData.featured_posters || {})[String(i)] : null;
                      return (
                        <div key={i} className="relative h-24 rounded-lg overflow-hidden border border-gray-200 group" data-testid={`settings-featured-slot-${i}`}>
                          {isVid ? (
                            <>
                              {posterUrl ? (
                                <img src={resolveMediaUrl(posterUrl)} alt={`Video ${i + 1}`} className="w-full h-full object-cover" />
                              ) : (
                                <video src={resolveMediaUrl(media)} className="w-full h-full object-cover" muted preload="metadata" />
                              )}
                              <div className="absolute bottom-1 left-1 bg-black/60 rounded px-1.5 py-0.5 pointer-events-none">
                                <span className="text-[10px] text-white font-medium">Video</span>
                              </div>
                            </>
                          ) : (
                            <img src={resolveMediaUrl(media)} alt={`Featured ${i + 1}`} className="w-full h-full object-cover" />
                          )}
                          <button
                            onClick={() => {
                              const photos = [...(profileData.featured_photos || [])];
                              photos.splice(i, 1);
                              const oldPosters = profileData.featured_posters || {};
                              const newPosters = {};
                              Object.keys(oldPosters).forEach(k => {
                                const ki = parseInt(k);
                                if (ki < i) newPosters[k] = oldPosters[k];
                                else if (ki > i) newPosters[String(ki - 1)] = oldPosters[k];
                              });
                              setProfileData(p => ({ ...p, featured_photos: photos, featured_posters: newPosters }));
                            }}
                            className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })}
                    {(profileData.featured_photos || []).length < 6 && (
                      <div>
                        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm" className="hidden" id="settings-featured-upload"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            const isVideo = file.type.startsWith('video/');
                            const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
                            if (file.size > maxSize) { toast.error(`Max ${isVideo ? '50' : '10'}MB`); return; }
                            const uploadCat = isVideo ? 'post' : 'featured';
                            const url = await handleUpload(file, uploadCat);
                            if (url) {
                              const newPosters = { ...(profileData.featured_posters || {}) };
                              const newIndex = (profileData.featured_photos || []).length;
                              if (isVideo) {
                                const frameBlob = await extractVideoFrame(file);
                                const pUrl = await uploadPosterBlob(frameBlob);
                                if (pUrl) newPosters[String(newIndex)] = pUrl;
                              }
                              setProfileData(p => ({
                                ...p,
                                featured_photos: [...(p.featured_photos || []), url],
                                featured_posters: newPosters,
                              }));
                            }
                            e.target.value = '';
                          }}
                        />
                        <label htmlFor="settings-featured-upload" className="flex flex-col items-center justify-center h-24 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-blue-400 transition-colors" data-testid="settings-featured-upload-btn">
                          <Plus className="h-5 w-5 text-gray-400" />
                          <span className="text-[10px] text-gray-400 mt-0.5">Photo / Video</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-5">
                <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                  <h4 className="font-semibold text-sm text-[#0f1d3d] mb-1">Change your password</h4>
                  <p className="text-xs text-slate-600 mb-3">
                    We'll send a 6-digit reset code to your account email so you can set a new password.
                  </p>
                  <Button
                    variant="outline"
                    className="bg-white text-[#0f1d3d] border-[#0f1d3d]/20 hover:bg-blue-100"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/auth/request-password-reset`, { email: user?.email });
                        toast.success('If your email is on file, a reset code has been sent.');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Could not request reset code');
                      }
                    }}
                    data-testid="settings-request-password-reset-btn"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Send password reset email
                  </Button>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <h4 className="font-semibold text-sm text-slate-700 mb-1">More security options</h4>
                  <p className="text-xs text-slate-500">
                    Two-factor authentication and per-device notification preferences are not yet
                    available. We'll let you know as soon as they're live.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-5">
                {/* Accent Theme — local only, this browser */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Accent Color</label>
                  <p className="text-xs text-gray-500">Personalize buttons, tabs, and highlights (this browser only)</p>
                  <div className="flex gap-3 pt-1" data-testid="accent-theme-selector">
                    {ACCENT_THEMES.map(t => {
                      const isActive = (localStorage.getItem('onlymass-accent') || 'blue') === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => { setAccentTheme(t.id); setAccountData(d => ({ ...d })); }}
                          className={`flex flex-col items-center gap-1.5 p-2.5 rounded-xl border-2 transition-all ${
                            isActive ? 'border-gray-900 shadow-sm scale-105' : 'border-transparent hover:border-gray-200'
                          }`}
                          data-testid={`accent-${t.id}`}
                        >
                          <div
                            className={`w-8 h-8 rounded-full shadow-sm ${isActive ? `ring-2 ${t.ring} ring-offset-2` : ''}`}
                            style={{ backgroundColor: t.color }}
                          />
                          <span className={`text-[11px] font-medium ${isActive ? 'text-gray-900' : 'text-gray-500'}`}>{t.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <hr className="border-gray-100" />

                <div className="rounded-xl border border-slate-200 p-4">
                  <h4 className="font-semibold text-sm text-slate-700 mb-1">Language & timezone</h4>
                  <p className="text-xs text-slate-500">
                    OnlyMass currently runs in English with timestamps shown in your device's local
                    timezone. Per-account language and timezone settings will arrive in a future
                    update.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="om-accent-gradient text-white"
            data-testid="settings-save-btn"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Privacy Settings Modal Component
// Non-creator privacy controls do not yet have a backend contract that
// validates the full set of fields the original modal exposed. To avoid
// silently lying to users about saved preferences we render an honest
// "coming soon" surface here. Creator-side privacy lives in
// CreatorPrivacySettingsModal which IS backend-wired.
const PrivacySettingsModal = ({ open, onClose, user }) => {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="privacy-settings-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>Privacy Settings</span>
          </DialogTitle>
          <DialogDescription>
            Control who can see your information and how others can interact with you
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
            <h4 className="font-semibold text-sm text-[#0f1d3d] mb-1">Detailed privacy controls are on the way</h4>
            <p className="text-xs text-slate-600">
              We're polishing per-section visibility, tagging, mentions, blocking,
              and data download. Until then, OnlyMass uses sensible safe defaults:
            </p>
            <ul className="text-xs text-slate-600 mt-3 space-y-1 list-disc list-inside">
              <li>Your profile is visible to everyone who has the link.</li>
              <li>Posts you publish are visible according to each post's audience.</li>
              <li>Only signed-in users can comment or message you.</li>
            </ul>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <h4 className="font-semibold text-sm text-slate-700 mb-1">Need help right now?</h4>
            <p className="text-xs text-slate-500">
              Email <a href="mailto:support@onlymass.net" className="text-blue-600 underline">support@onlymass.net</a> to request blocks,
              removals, or report abuse and we'll handle it manually while the
              self-service controls are being built.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={onClose} data-testid="privacy-settings-close-btn">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Professional Mode Modal Component
const ProfessionalModeModal = ({ open, onClose, user, updateUser }) => {
  const [professionalData, setProfessionalData] = useState({
    is_creator: user?.is_creator ?? false,
    creator_category: user?.creator_category || 'fitness',
    monetization_enabled: user?.monetization_enabled ?? false
  });
  const [loading, setLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  
  // Developer mode — explicit opt-in ONLY.
  // Show debug UI when NODE_ENV=development OR ?debug=true is set in the URL.
  // Admin role no longer auto-exposes raw API URLs / Stripe account IDs.
  const isDevMode = process.env.NODE_ENV === 'development' ||
                    window.location.search.includes('debug=true');

  const getAuthHeaders = () => {
    const token = Cookies.get('session_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Fetch connect status when modal opens
  useEffect(() => {
    if (open && professionalData.is_creator) {
      fetchConnectStatus();
    }
  }, [open, professionalData.is_creator]);

  const fetchConnectStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/connect/status`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setConnectStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch connect status:', error);
    }
  };

  const fetchDebugData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creator/metrics/debug`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDebugData(data);
      }
    } catch (error) {
      console.error('Failed to fetch debug data:', error);
    }
  };

  // Helper to extract error message from structured response
  const extractErrorMessage = (errorData) => {
    if (!errorData) return 'Unknown error';
    
    // Handle structured error response
    if (typeof errorData.detail === 'object') {
      const detail = errorData.detail;
      // Build comprehensive error message
      let msg = detail.user_message || detail.message || '';
      
      // Add Stripe error details if present
      if (detail.stripe_error) {
        const se = detail.stripe_error;
        if (se.message) msg += ` (${se.message})`;
        if (se.code) msg += ` [code: ${se.code}]`;
        if (se.request_id) msg += ` [request_id: ${se.request_id}]`;
      }
      
      // Add config issues if present
      if (detail.config_issues?.length > 0) {
        msg += '\n\nConfig issues:\n• ' + detail.config_issues.join('\n• ');
      }
      
      return msg || JSON.stringify(detail);
    }
    
    // Handle string error
    if (typeof errorData.detail === 'string') {
      return errorData.detail;
    }
    
    return JSON.stringify(errorData);
  };

  // Helper to safely parse JSON response, handling HTML/non-JSON errors
  const safeParseJson = async (response, endpoint) => {
    const contentType = response.headers.get('content-type') || '';
    const responseText = await response.text();
    
    console.log(`[API] ${endpoint} - Status: ${response.status}, Content-Type: ${contentType}`);
    
    // Check if response is JSON
    if (!contentType.includes('application/json')) {
      // Response is not JSON (likely HTML from SPA catch-all)
      const isHtml = responseText.trim().startsWith('<') || responseText.includes('<!DOCTYPE');
      const preview = responseText.substring(0, 200);
      
      console.error(`[API] ${endpoint} returned non-JSON response:`, preview);
      
      throw new Error(
        isHtml 
          ? `API returned HTML instead of JSON. This usually means the endpoint doesn't exist or routing is misconfigured.\n\nEndpoint: ${endpoint}\nStatus: ${response.status}\nPreview: ${preview.substring(0, 100)}...`
          : `API returned unexpected content type: ${contentType}\n\nEndpoint: ${endpoint}\nStatus: ${response.status}\nResponse: ${preview}`
      );
    }
    
    // Try to parse JSON
    try {
      return JSON.parse(responseText);
    } catch (parseError) {
      console.error(`[API] ${endpoint} JSON parse error:`, parseError);
      throw new Error(`Failed to parse JSON from ${endpoint}: ${responseText.substring(0, 100)}...`);
    }
  };

  // State for onboarding URL fallback
  const [onboardingUrl, setOnboardingUrl] = useState(null);
  const [stripeError, setStripeError] = useState(null);
  
  // State for Connect country selection
  // Common Stripe Connect supported countries (European focus)
  const STRIPE_COUNTRIES = [
    { code: 'BE', name: 'Belgium' },
    { code: 'GR', name: 'Greece' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'DE', name: 'Germany' },
    { code: 'FR', name: 'France' },
    { code: 'IT', name: 'Italy' },
    { code: 'ES', name: 'Spain' },
    { code: 'PT', name: 'Portugal' },
    { code: 'AT', name: 'Austria' },
    { code: 'IE', name: 'Ireland' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'AU', name: 'Australia' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SE', name: 'Sweden' },
    { code: 'DK', name: 'Denmark' },
    { code: 'NO', name: 'Norway' },
    { code: 'FI', name: 'Finland' },
    { code: 'PL', name: 'Poland' },
    { code: 'RO', name: 'Romania' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'HU', name: 'Hungary' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LV', name: 'Latvia' },
    { code: 'EE', name: 'Estonia' },
    { code: 'MT', name: 'Malta' },
    { code: 'LU', name: 'Luxembourg' },
  ].sort((a, b) => a.name.localeCompare(b.name));
  
  const [connectCountry, setConnectCountry] = useState('BE'); // Default to Belgium

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    setStripeError(null);
    setOnboardingUrl(null);
    
    console.log('[Connect] Starting Stripe Connect flow');
    console.log('[Connect] API_URL:', API_URL);
    console.log('[Connect] Selected country:', connectCountry);
    console.log('[Connect] Current status:', connectStatus);
    
    // First, verify API is reachable
    try {
      console.log('[Connect] Verifying API reachability...');
      const pingResponse = await fetch(`${API_URL}/api/connect/ping`);
      const pingData = await safeParseJson(pingResponse, '/api/connect/ping');
      console.log('[Connect] Ping response:', pingData);
      
      if (!pingData.ok) {
        throw new Error('API ping failed');
      }
    } catch (pingError) {
      console.error('[Connect] API ping failed:', pingError);
      setStripeError({
        step: 'ping',
        status: 'failed',
        message: `API not reachable: ${pingError.message}`,
        raw: { error: pingError.message }
      });
      toast.error(`API not reachable: ${pingError.message}`);
      setConnectLoading(false);
      return;
    }
    
    try {
      // Step 1: Create account if needed
      if (!connectStatus?.stripe_connect_account_id) {
        console.log('[Connect] Step 1: Creating Connect account');
        console.log('[Connect] Sending country:', connectCountry);
        
        const requestBody = { country: connectCountry, origin_url: window.location.origin };
        console.log('[Connect] Full request body:', JSON.stringify(requestBody));
        
        const createResponse = await fetch(`${API_URL}/api/connect/create-account`, {
          method: 'POST',
          headers: getAuthHeaders(),
        credentials: 'include',
          body: JSON.stringify(requestBody)
        });
        
        let createData;
        try {
          createData = await safeParseJson(createResponse, '/api/connect/create-account');
        } catch (parseError) {
          console.error('[Connect] Create account parse error:', parseError);
          setStripeError({
            step: 'create-account',
            status: createResponse.status,
            message: parseError.message,
            raw: { parseError: parseError.message }
          });
          toast.error(`Create account failed: ${parseError.message}`);
          setConnectLoading(false);
          return;
        }
        
        console.log('[Connect] Create account response:', createResponse.status, createData);
        console.log('[Connect] Account created in country:', createData.country, '(requested:', createData.country_requested, ')');
        
        if (!createResponse.ok) {
          const errorMsg = extractErrorMessage(createData);
          console.error('[Connect] Create account failed:', errorMsg);
          setStripeError({
            step: 'create-account',
            status: createResponse.status,
            message: errorMsg,
            raw: createData
          });
          toast.error(`Create account failed: ${errorMsg}`);
          setConnectLoading(false);
          return;
        }
        
        // Show success with country info
        toast.success(`Stripe account created for ${createData.country || connectCountry}`);
        
        // Refresh connect status after creating account
        await fetchConnectStatus();
      } else {
        console.log('[Connect] Step 1: Account already exists:', connectStatus.stripe_connect_account_id, 'in country:', connectStatus.country);
      }
      
      // Step 2: Get onboarding link
      console.log('[Connect] Step 2: Getting onboarding link');
      
      const linkResponse = await fetch(`${API_URL}/api/connect/onboarding-link`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      
      let linkData;
      try {
        linkData = await safeParseJson(linkResponse, '/api/connect/onboarding-link');
      } catch (parseError) {
        console.error('[Connect] Onboarding link parse error:', parseError);
        setStripeError({
          step: 'onboarding-link',
          status: linkResponse.status,
          message: parseError.message,
          raw: { parseError: parseError.message }
        });
        toast.error(`Onboarding link failed: ${parseError.message}`);
        setConnectLoading(false);
        return;
      }
      
      console.log('[Connect] Onboarding link response:', linkResponse.status, linkData);
      
      if (!linkResponse.ok) {
        const errorMsg = extractErrorMessage(linkData);
        console.error('[Connect] Onboarding link failed:', errorMsg);
        setStripeError({
          step: 'onboarding-link',
          status: linkResponse.status,
          message: errorMsg,
          raw: linkData
        });
        toast.error(`Onboarding link failed: ${errorMsg}`);
        setConnectLoading(false);
        return;
      }
      
      // Step 3: Check for URL and redirect
      if (!linkData.url) {
        const errorMsg = 'No onboarding URL returned from API';
        console.error('[Connect] ' + errorMsg);
        setStripeError({
          step: 'onboarding-link',
          status: linkResponse.status,
          message: errorMsg,
          raw: linkData
        });
        toast.error(errorMsg);
        setConnectLoading(false);
        return;
      }
      
      console.log('[Connect] Step 3: Got URL, attempting redirect');
      console.log('[Connect] URL:', linkData.url);
      setOnboardingUrl(linkData.url);
      
      // Attempt redirect
      try {
        window.location.href = linkData.url;
      } catch (redirectError) {
        console.error('[Connect] Redirect failed:', redirectError);
        toast.info('Redirect failed. Use the link below to complete onboarding.');
      }
      
    } catch (error) {
      console.error('[Connect] Unexpected error:', error);
      setStripeError({
        step: 'unknown',
        status: 'exception',
        message: error.message,
        raw: error
      });
      toast.error(`Failed to connect Stripe: ${error.message}`);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setConnectLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/connect/dashboard-link?origin_url=${encodeURIComponent(window.location.origin)}`,
        { headers: getAuthHeaders() }
      );
      
      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to open dashboard');
      }
    } catch (error) {
      toast.error('Failed to open Stripe dashboard');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    setConnectLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/refresh-status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        await fetchConnectStatus();
        toast.success('Status refreshed');
      }
    } catch (error) {
      toast.error('Failed to refresh status');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleResetConnect = async () => {
    // Confirm with user
    const confirmed = window.confirm(
      'This will disconnect your current Stripe account so you can connect a new one in a different country.\n\n' +
      'Your existing Stripe account will NOT be deleted, but you won\'t be able to receive payments until you complete onboarding with a new account.\n\n' +
      'Continue?'
    );
    
    if (!confirmed) return;
    
    setConnectLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/connect/reset`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchConnectStatus();
        toast.success(`Disconnected from ${data.previous_country || 'previous'} account. Select your new country and click "Connect Stripe".`);
      } else {
        toast.error(data.detail || 'Failed to reset Stripe connection');
      }
    } catch (error) {
      toast.error('Failed to reset Stripe connection');
    } finally {
      setConnectLoading(false);
    }
  };

  const handleToggleCreator = async () => {
    setLoading(true);
    try {
      // Call the dedicated activate-creator endpoint
      const response = await fetch(`${API_URL}/api/users/activate-creator`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        const updatedUser = { 
          ...user, 
          is_creator: true,
          monetization_enabled: true
        };
        updateUser(updatedUser);
        setProfessionalData({ ...professionalData, is_creator: true });
        
        toast.success(result.already_creator 
          ? 'You are already a creator!' 
          : 'Creator mode activated successfully!'
        );
        
        // Refresh creator state after activation
        setTimeout(() => {
          fetchConnectStatus();
        }, 500);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to activate creator mode');
      }
    } catch (error) {
      toast.error('Failed to activate creator mode');
    } finally {
      setLoading(false);
    }
  };

  // Get connect button state
  const getConnectButtonConfig = () => {
    if (!connectStatus?.stripe_connect_account_id) {
      return { label: 'Connect Stripe', action: handleConnectStripe, variant: 'default' };
    }
    if (!connectStatus?.charges_enabled) {
      return { label: 'Continue Onboarding', action: handleConnectStripe, variant: 'default' };
    }
    return { label: 'Open Stripe Dashboard', action: handleOpenDashboard, variant: 'outline' };
  };

  const connectButton = getConnectButtonConfig();

  // Get connect status display
  const getConnectStatusDisplay = () => {
    if (!connectStatus?.stripe_connect_account_id) {
      return { text: 'Not Connected', color: 'text-gray-500', bg: 'bg-gray-100' };
    }
    if (!connectStatus?.charges_enabled) {
      return { text: 'Onboarding Incomplete', color: 'text-yellow-700', bg: 'bg-yellow-100' };
    }
    return { text: 'Ready to Receive Payments', color: 'text-green-700', bg: 'bg-green-100' };
  };

  const statusDisplay = getConnectStatusDisplay();

  // Render "Become a Creator" screen for non-creators
  if (!professionalData.is_creator) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Crown className="h-6 w-6 text-yellow-500" />
              <span>Become a Creator</span>
            </DialogTitle>
            <DialogDescription>
              Start monetizing your fitness content and build your audience
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Hero Section */}
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
              <Sparkles className="h-12 w-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Turn Your Passion Into Income
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Join thousands of fitness creators earning from their expertise. Share workout plans, meal guides, and live coaching sessions.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Earn Revenue</h4>
                    <p className="text-sm text-gray-600">Sell subscriptions, workout plans, and receive tips from supporters</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Build Community</h4>
                    <p className="text-sm text-gray-600">Grow your subscriber base and engage with your audience</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Dumbbell className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Share Content</h4>
                    <p className="text-sm text-gray-600">Create workout plans, meal guides, and live stream sessions</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 border rounded-lg bg-white">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Track Performance</h4>
                    <p className="text-sm text-gray-600">Access analytics and insights to grow your business</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission Info */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Platform Commission</p>
                  <p className="text-lg font-bold">20%</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">You Keep</p>
                  <p className="text-lg font-bold text-green-600">80%</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Stripe payment processing fees apply separately. You can set up payouts after activation.
              </p>
            </div>

            {/* Activation Note */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">What happens when you activate:</p>
                  <ul className="mt-1 list-disc list-inside text-yellow-700">
                    <li>Your account becomes a creator account</li>
                    <li>You can create and sell content</li>
                    <li>Stripe setup is done separately (optional but required for payouts)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Maybe Later
            </Button>
            <Button 
              onClick={handleToggleCreator}
              disabled={loading}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
              data-testid="activate-creator-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Activating...
                </>
              ) : (
                <>
                  <Crown className="h-4 w-4 mr-2" />
                  Activate Creator Mode
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Main Professional Mode dashboard for creators
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Briefcase className="h-5 w-5 text-blue-600" />
            <span>Professional Mode</span>
          </DialogTitle>
          <DialogDescription>
            Manage your creator account, monetization, and business insights
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Creator Status Banner */}
          <div className="p-4 border border-green-200 rounded-lg bg-green-50">
            <div className="flex items-center space-x-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-900">Creator Account Active</h3>
                <p className="text-sm text-green-700">
                  You have full access to monetization tools and analytics
                </p>
              </div>
            </div>
          </div>

          {/* Payout Setup - Stripe Connect */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-blue-600" />
              <span>Payout Setup</span>
            </h3>
            
            <Card className="p-6" data-testid="connect-status-card">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-medium text-lg">Stripe Connect</h4>
                  <p className="text-sm text-gray-600">
                    Connect your Stripe account to receive payments from subscribers and tips
                  </p>
                </div>
                <Badge className={`${statusDisplay.bg} ${statusDisplay.color}`} data-testid="connect-status-badge">
                  {statusDisplay.text}
                </Badge>
              </div>
              
              {connectStatus?.charges_enabled && (
                <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-500">Commission Rate</p>
                    <p className="text-xl font-bold">{connectStatus.commission_rate}%</p>
                    <p className="text-xs text-green-600">You keep {100 - connectStatus.commission_rate}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payouts</p>
                    <p className="text-xl font-bold text-green-600">
                      {connectStatus.payouts_enabled ? 'Enabled' : 'Pending'}
                    </p>
                  </div>
                </div>
              )}

              {/* Commission Tier Transparency */}
              {connectStatus?.commission_rate === 15 && connectStatus?.early_adopter_until && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="commission-transition-info">
                  <div className="flex items-start gap-2">
                    <Crown className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-900">Early Adopter Rate: 15%</p>
                      <p className="text-xs text-amber-700 mt-0.5">
                        Your introductory 15% commission rate is active until{' '}
                        <strong>{new Date(connectStatus.early_adopter_until).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                        After that date, the standard 20% platform commission applies.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {connectStatus?.commission_rate === 20 && (
                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="commission-standard-info">
                  <p className="text-xs text-gray-600">
                    Standard platform commission: <strong>20%</strong>. You keep <strong>80%</strong> of all revenue.
                    Stripe processing fees apply separately.
                  </p>
                </div>
              )}
              
              {/* Country Selection - Only show if not yet connected */}
              {!connectStatus?.stripe_connect_account_id && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Your Country
                  </label>
                  <select
                    value={connectCountry}
                    onChange={(e) => setConnectCountry(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                    data-testid="connect-country-select"
                  >
                    {STRIPE_COUNTRIES.map(c => (
                      <option key={c.code} value={c.code}>
                        {c.name} ({c.code})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    This determines your payout currency and tax reporting country.
                  </p>
                </div>
              )}
              
              {/* Show current country if already connected - Clean UI */}
              {connectStatus?.stripe_connect_account_id && (
                <div className={`mb-4 p-3 rounded-lg border ${
                  connectStatus.country === 'US' 
                    ? 'bg-yellow-50 border-yellow-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${
                        connectStatus.charges_enabled ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></div>
                      <span className="text-sm font-medium text-gray-700">
                        {connectStatus.charges_enabled ? 'Connected' : 'Setup in progress'}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-bold ${
                        connectStatus.country === 'US' ? 'text-yellow-700' : 'text-green-700'
                      }`}>
                        {STRIPE_COUNTRIES.find(c => c.code === connectStatus.country)?.name || connectStatus.country || 'Unknown'}
                      </span>
                    </div>
                  </div>
                  {connectStatus.country === 'US' && (
                    <div className="mt-2 text-xs text-yellow-700">
                      Your account is set up for United States. Need a different country? Click "Change Country".
                    </div>
                  )}
                  {/* Technical account ID only in dev mode */}
                  {isDevMode && (
                    <div className="mt-2 text-xs text-gray-400 font-mono">
                      ID: {connectStatus.stripe_connect_account_id}
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-2">
                <Button 
                  onClick={connectButton.action}
                  disabled={connectLoading}
                  variant={connectButton.variant}
                  className="flex-1"
                  data-testid="connect-stripe-btn"
                >
                  {connectLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  {connectButton.label}
                </Button>
                {connectStatus?.stripe_connect_account_id && (
                  <>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={handleRefreshStatus}
                      disabled={connectLoading}
                      data-testid="refresh-connect-btn"
                    >
                      <RefreshCw className={`h-4 w-4 ${connectLoading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={handleResetConnect}
                      disabled={connectLoading}
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      data-testid="reset-connect-btn"
                    >
                      Change Country
                    </Button>
                  </>
                )}
              </div>

              {/* Stripe Error Display - User-friendly for production */}
              {stripeError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm" data-testid="stripe-error">
                  <div className="flex items-center gap-2 text-red-700 font-semibold mb-2">
                    <AlertCircle className="h-4 w-4" />
                    <span>Connection Error</span>
                  </div>
                  <p className="text-red-600">{stripeError.message}</p>
                  <p className="text-xs text-red-500 mt-2">
                    Please try again. If the problem persists, contact support.
                  </p>
                  {/* Technical details only in dev mode */}
                  {isDevMode && stripeError.raw?.detail?.stripe_error && (
                    <div className="mt-2 text-xs text-red-500 font-mono bg-red-100 p-2 rounded">
                      <div>Step: {stripeError.step}</div>
                      <div>Status: {stripeError.status}</div>
                      <div>Type: {stripeError.raw.detail.stripe_error.type}</div>
                      <div>Code: {stripeError.raw.detail.stripe_error.code}</div>
                      <div>Request ID: {stripeError.raw.detail.stripe_error.request_id}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Onboarding URL Fallback - Clean UI */}
              {onboardingUrl && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="onboarding-fallback">
                  <div className="flex items-center gap-2 text-blue-700 font-semibold mb-2">
                    <ExternalLink className="h-4 w-4" />
                    <span>Complete Your Setup</span>
                  </div>
                  <p className="text-sm text-blue-600 mb-3">
                    Click the button below to complete your Stripe account setup.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => window.open(onboardingUrl, '_blank')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open Setup Page
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(onboardingUrl);
                        toast.success('Link copied to clipboard');
                      }}
                    >
                      Copy Link
                    </Button>
                  </div>
                </div>
              )}

              {/* Developer Debug Info - Only visible in dev mode */}
              {isDevMode && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowDebug(!showDebug)}
                    className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1"
                  >
                    {showDebug ? '▼' : '▶'} Developer Debug
                  </button>
                  {showDebug && (
                    <div className="mt-2 p-2 bg-gray-100 border rounded text-xs font-mono">
                      <div className="text-gray-600 mb-1">Debug Info:</div>
                      <div className="text-gray-500 space-y-1">
                        <div>API: {API_URL}</div>
                        <div>Origin: {window.location.origin}</div>
                        <div>Account: {connectStatus?.stripe_connect_account_id || 'none'}</div>
                        <div>Country: {connectStatus?.country || 'none'}</div>
                        <div>Verified: {connectStatus?.stripe_verified_country || 'none'}</div>
                        {onboardingUrl && <div className="break-all">URL: {onboardingUrl}</div>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          </div>

              {/* Creator Analytics Dashboard — single source of truth */}
              <div className="space-y-4">
                <CreatorAnalytics />
              </div>
        </div>

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Share Profile Modal Component
const QRCodeModal = ({ open, onClose, user }) => {
  // Build the share URL from the current window origin so it always matches
  // the deployed domain (no hardcoded fallback that could leak the wrong domain).
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '';
  const profileUrl = `${origin}/profile/${user?.id || 'user'}`;
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      toast.success('Profile link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${user?.name || 'OnlyMass'} on OnlyMass`,
          text: 'Check out my OnlyMass profile',
          url: profileUrl,
        });
      } catch (e) {
        // user cancelled — no toast
      }
    } else {
      handleCopy();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="share-profile-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Share2 className="h-5 w-5 text-blue-600" />
            <span>Share Profile</span>
          </DialogTitle>
          <DialogDescription>
            Send your OnlyMass profile link to your audience
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-1">{user?.name || 'Your Profile'}</p>
            <p className="text-xs text-slate-600 break-all" data-testid="share-profile-url">{profileUrl}</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={handleCopy}
              className="w-full"
              data-testid="share-profile-copy-btn"
            >
              <Link className="h-4 w-4 mr-2" />
              {copied ? 'Copied' : 'Copy Link'}
            </Button>
            <Button
              onClick={handleShare}
              className="w-full bg-[#0f1d3d] hover:bg-[#1a2d55] text-white"
              data-testid="share-profile-share-btn"
            >
              <Share2 className="h-4 w-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Challenge Card (sidebar compact view)
const ChallengeCard = ({ challenge, currentUserId }) => {
  const [joinStatus, setJoinStatus] = useState(null);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/challenges/${challenge.id}/status`);
        setJoinStatus(data.joined ? 'joined' : 'available');
        setCurrentStreak(Number(data.current_streak || 0));
      } catch { setJoinStatus('available'); }
    };
    check();
  }, [challenge.id]);
  const handleJoin = async (e) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/challenges/${challenge.id}/join`);
      setJoinStatus('joined');
      toast.success('Joined challenge!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };
  const audienceLabel = { everyone: 'Open to all', followers: 'Followers only', subscribers: 'Subscribers only' };
  const audienceColor = { everyone: 'bg-emerald-50 text-emerald-700 border-emerald-200', followers: 'bg-blue-50 text-blue-700 border-blue-200', subscribers: 'bg-purple-50 text-purple-700 border-purple-200' };
  return (
    <>
      <Card className="border-amber-100 hover:border-amber-200 transition-all cursor-pointer" onClick={() => setShowDetail(true)} data-testid={`challenge-${challenge.id}`}>
        <CardContent className="p-3">
          {challenge.cover_image && (
            <img src={resolveMediaUrl(challenge.cover_image)} alt="" className="w-full h-20 object-cover rounded-md mb-2" />
          )}
          <p className="text-xs font-semibold text-gray-900 line-clamp-1">{challenge.title}</p>
          <div className="flex items-center justify-between mt-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-gray-400">{audienceLabel[challenge.audience] || 'Public'}</span>
              <span className="text-[10px] text-gray-300">·</span>
              <span className="text-[10px] text-gray-400">{challenge.participants_count} joined</span>
            </div>
            {joinStatus === 'joined' ? (
              <span className="inline-flex items-center gap-1">
                <span className="inline-flex items-center text-[10px] font-medium text-emerald-600"><CheckCircle className="h-3 w-3 mr-0.5" /> Joined</span>
                {currentStreak > 0 && (
                  <span
                    className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-orange-600"
                    data-testid={`challenge-streak-sidebar-${challenge.id}`}
                  >
                    <span aria-hidden>🔥</span>{currentStreak}
                  </span>
                )}
              </span>
            ) : (
              <button onClick={handleJoin} className="text-[10px] font-medium text-amber-600 hover:text-amber-700">Join</button>
            )}
          </div>
        </CardContent>
      </Card>
      <ChallengeDetailModal challenge={challenge} open={showDetail} onClose={() => setShowDetail(false)} joined={joinStatus === 'joined'} onJoined={() => setJoinStatus('joined')} currentUserId={currentUserId} />
    </>
  );
};

// Challenge Detail Modal with Leaderboard + Proof Submission
const ChallengeDetailModal = ({ challenge, open, onClose, joined: joinedProp, onJoin, onJoined, currentUserId }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [proofUrl, setProofUrl] = useState('');
  const [proofNotes, setProofNotes] = useState('');
  const [proofFileKind, setProofFileKind] = useState('photo');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeView, setActiveView] = useState('overview');
  // Join state is fetched locally from the backend every time the modal opens so it stays in sync
  // with any other surface (feed card, companion post, manager) that may have joined already.
  const [joinedLocal, setJoinedLocal] = useState(false);
  const [joining, setJoining] = useState(false);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const joined = joinedLocal || !!joinedProp;

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/challenges/${challenge.id}/leaderboard`).then(r => setLeaderboard(r.data)).catch(() => {});
    axios.get(`${API}/challenges/${challenge.id}/submissions`).then(r => setSubmissions(r.data)).catch(() => {});
    // Always re-check the authoritative join status every time the modal opens
    axios.get(`${API}/challenges/${challenge.id}/status`).then(r => {
      setJoinedLocal(!!r.data?.joined);
      setCurrentStreak(Number(r.data?.current_streak || 0));
      setLongestStreak(Number(r.data?.longest_streak || 0));
    }).catch(() => {});
  }, [open, challenge.id]);

  const handleJoinInternal = async () => {
    if (joining) return;
    setJoining(true);
    try {
      if (onJoin) {
        await onJoin();
      } else {
        await axios.post(`${API}/challenges/${challenge.id}/join`);
        toast.success('Joined challenge!');
      }
      setJoinedLocal(true);
      if (onJoined) onJoined();
      // Refresh leaderboard immediately so the user sees themselves listed
      try {
        const r = await axios.get(`${API}/challenges/${challenge.id}/leaderboard`);
        setLeaderboard(r.data);
      } catch {}
    } catch (err) {
      // If the user is already joined server-side, treat the 400 as a sync fix
      if (err?.response?.status === 400) setJoinedLocal(true);
      else toast.error(err?.response?.data?.detail || 'Failed to join');
    } finally {
      setJoining(false);
    }
  };

  const handleProofUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Size check — server caps at 50MB for videos
    const maxBytes = 50 * 1024 * 1024;
    if (file.size > maxBytes) {
      toast.error(`File too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Max 50MB.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      // category MUST be passed as a query param — backend reads it as a non-Form() param
      const { data } = await axios.post(`${API}/upload/media?category=post`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProofUrl(data.url);
      setProofFileKind(file.type.startsWith('video/') ? 'video' : 'photo');
      toast.success('Proof ready to submit');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Upload failed — try a smaller file or different format';
      toast.error(msg);
    }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleSubmitProof = async () => {
    if (!proofUrl && !proofNotes.trim()) { toast.error('Add proof (photo/video or notes)'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/challenges/${challenge.id}/submit`, {
        proof_url: proofUrl,
        proof_type: proofUrl ? (proofFileKind === 'video' ? 'video' : 'photo') : 'text_log',
        notes: proofNotes,
      });
      const newCurrent = Number(res?.data?.current_streak || 0);
      const newLongest = Number(res?.data?.longest_streak || 0);
      if (newCurrent > currentStreak && newCurrent >= 2) {
        toast.success(`Proof submitted — you're on a ${newCurrent}-day streak!`);
      } else {
        toast.success('Proof submitted for review!');
      }
      setCurrentStreak(newCurrent);
      setLongestStreak(newLongest);
      setProofUrl(''); setProofNotes(''); setProofFileKind('photo');
      const r = await axios.get(`${API}/challenges/${challenge.id}/submissions`);
      setSubmissions(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const handleReview = async (subId, action) => {
    try {
      await axios.post(`${API}/challenges/${challenge.id}/review/${subId}`, { action });
      toast.success(action === 'approve' ? 'Approved!' : 'Rejected');
      const [s, l, st] = await Promise.all([
        axios.get(`${API}/challenges/${challenge.id}/submissions`),
        axios.get(`${API}/challenges/${challenge.id}/leaderboard`),
        axios.get(`${API}/challenges/${challenge.id}/status`).catch(() => ({ data: {} })),
      ]);
      setSubmissions(s.data); setLeaderboard(l.data);
      if (st?.data) {
        setCurrentStreak(Number(st.data.current_streak || 0));
        setLongestStreak(Number(st.data.longest_streak || 0));
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Review failed'); }
  };

  const audienceLabel = { everyone: 'Open to all', followers: 'Followers only', subscribers: 'Subscribers only' };
  const audienceColor = { everyone: 'bg-emerald-50 text-emerald-700 border-emerald-200', followers: 'bg-blue-50 text-blue-700 border-blue-200', subscribers: 'bg-purple-50 text-purple-700 border-purple-200' };
  const statusColors = { pending: 'text-amber-600 bg-amber-50', approved: 'text-emerald-600 bg-emerald-50', rejected: 'text-red-600 bg-red-50' };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto p-0" data-testid="challenge-detail-modal">
        <DialogHeader className="sr-only"><DialogTitle>{challenge.title}</DialogTitle></DialogHeader>
        {/* Header */}
        {challenge.cover_image && (
          <img src={resolveMediaUrl(challenge.cover_image)} alt="" className="w-full h-32 object-cover" />
        )}
        <div className="p-5 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{challenge.challenge_type}</Badge>
              <Badge className={`text-[10px] border ${audienceColor[challenge.audience] || 'bg-gray-50 text-gray-700 border-gray-200'}`} data-testid="challenge-detail-audience">{audienceLabel[challenge.audience] || 'Open to all'}</Badge>
              <Badge variant="outline" className="text-[10px]">{challenge.participants_count} joined</Badge>
            </div>
            <h2 className="text-lg font-bold text-gray-900">{challenge.title}</h2>
            <p className="text-xs text-gray-500 mt-0.5">by {challenge.creator_name}</p>
            {/* Fitness niches as tags if present */}
            {Array.isArray(challenge.fitness_niches) && challenge.fitness_niches.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2" data-testid="challenge-detail-niches">
                {challenge.fitness_niches.map(nid => <NicheBadge key={nid} nicheId={nid} size="xs" />)}
              </div>
            )}
          </div>
          {challenge.description && <p className="text-sm text-gray-600">{challenge.description}</p>}
          {(challenge.start_date || challenge.end_date) && (
            <div className="flex gap-4 text-xs text-gray-500">
              {challenge.start_date && <span>Start: {challenge.start_date}</span>}
              {challenge.end_date && <span>End: {challenge.end_date}</span>}
            </div>
          )}
          {challenge.goal && <div className="p-3 bg-amber-50 rounded-lg border border-amber-100"><p className="text-xs font-medium text-amber-800">Goal: {challenge.goal}</p></div>}
          {challenge.rules && <div className="p-3 bg-gray-50 rounded-lg"><p className="text-xs text-gray-600">{challenge.rules}</p></div>}

          {joined && (currentStreak > 0 || longestStreak > 0) && (
            <div
              className="flex items-center justify-between gap-3 p-3 rounded-xl bg-gradient-to-r from-orange-50 via-amber-50 to-rose-50 border border-orange-100"
              data-testid="challenge-streak-panel"
            >
              <div className="flex items-center gap-3">
                <div className="text-3xl leading-none" aria-hidden>🔥</div>
                <div>
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-orange-700/80">Current streak</p>
                  <p className="text-lg font-bold text-orange-700 leading-tight" data-testid="challenge-current-streak">
                    {currentStreak === 0 ? 'No active streak' : `${currentStreak}-day streak`}
                  </p>
                  {currentStreak === 0 && longestStreak > 0 && (
                    <p className="text-[10px] text-orange-500/80 mt-0.5">Submit proof today to start a new streak.</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-gray-500">Best</p>
                <p className="text-sm font-bold text-gray-800" data-testid="challenge-longest-streak">{longestStreak} days</p>
              </div>
            </div>
          )}

          {/* Tab nav */}
          <div className="flex gap-1 border-b border-gray-100 pb-1">
            {['overview', 'leaderboard', 'submissions'].map(v => (
              <button key={v} onClick={() => setActiveView(v)} className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${activeView === v ? 'text-amber-700 bg-amber-50 border-b-2 border-amber-500' : 'text-gray-500 hover:text-gray-700'}`}>
                {v.charAt(0).toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>

          {/* Leaderboard */}
          {activeView === 'leaderboard' && (
            <div className="space-y-2" data-testid="challenge-leaderboard">
              {leaderboard.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No participants yet</p>
              ) : leaderboard.map((p, i) => (
                <div key={p.user_id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                  <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
                    {i + 1}
                  </span>
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={resolveMediaUrl(p.profile_picture)} />
                    <AvatarFallback className="text-xs bg-gray-100">{p.user_name?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{p.user_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-bold text-amber-600">{p.completions} done</p>
                    <p className="text-[10px] text-gray-400">{p.score} pts</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Submit Proof */}
          {activeView === 'overview' && joined && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-testid="proof-submission">
              <h4 className="text-xs font-semibold text-gray-700">Submit Completion Proof</h4>
              <p className="text-[10px] text-gray-400">Upload a photo/video or describe what you completed. The creator will review your submission.</p>
              <div className="flex gap-2">
                <input type="file" accept="image/*,video/*" className="hidden" id="proof-upload" onChange={handleProofUpload} />
                <label htmlFor="proof-upload" className="flex items-center gap-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 cursor-pointer hover:border-amber-300">
                  {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
                  {proofUrl ? 'Change file' : 'Upload proof'}
                </label>
                {proofUrl && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> File ready</span>}
              </div>
              <Input placeholder="Notes about your completion..." value={proofNotes} onChange={e => setProofNotes(e.target.value)} className="text-xs h-8" />
              <Button size="sm" onClick={handleSubmitProof} disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-8" data-testid="submit-proof-btn">
                {submitting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null} Submit Proof
              </Button>
            </div>
          )}

          {/* Submissions list */}
          {activeView === 'submissions' && (
            <div className="space-y-2" data-testid="challenge-submissions">
              {submissions.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">No submissions yet</p>
              ) : submissions.map(s => (
                <div key={s.id} className="p-3 border border-gray-100 rounded-lg space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-900">{s.user_name}</span>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusColors[s.status] || 'text-gray-500 bg-gray-50'}`}>{s.status}</span>
                  </div>
                  {s.proof_url && (s.proof_type === 'video' ? (
                    <video src={resolveMediaUrl(s.proof_url)} controls className="w-full h-24 object-cover rounded-md bg-black" />
                  ) : (
                    <img src={resolveMediaUrl(s.proof_url)} alt="Proof" className="w-full h-24 object-cover rounded-md" />
                  ))}
                  {s.notes && <p className="text-xs text-gray-600">{s.notes}</p>}
                  <p className="text-[10px] text-gray-400">{new Date(s.submitted_at).toLocaleString()}</p>
                  {s.status === 'pending' && challenge.creator_id === currentUserId && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleReview(s.id, 'approve')} className="bg-emerald-500 text-white text-xs h-7 px-3">Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => handleReview(s.id, 'reject')} className="text-xs h-7 px-3">Reject</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Winners display */}
          {challenge.winners && challenge.winners.length > 0 && (
            <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-100" data-testid="challenge-winners">
              <h4 className="text-xs font-semibold text-amber-800 flex items-center gap-1"><Trophy className="h-3.5 w-3.5" /> Winners</h4>
              {challenge.winners.map((w, i) => {
                const medal = ['text-amber-500', 'text-gray-400', 'text-orange-400'][w.rank - 1] || 'text-gray-400';
                return (
                  <div key={w.user_id} className="flex items-start gap-2 p-2 bg-white rounded-lg">
                    <Trophy className={`h-4 w-4 mt-0.5 ${medal}`} />
                    <Avatar className="h-6 w-6"><AvatarImage src={resolveMediaUrl(w.profile_picture)} /><AvatarFallback className="text-[10px] bg-gray-100">{w.user_name?.charAt(0)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-gray-900 truncate">{w.user_name}</span>
                        <span className="text-[10px] text-gray-500 shrink-0">{w.completions} done · {w.score} pts</span>
                      </div>
                      {w.reward_label && (
                        <p className="text-[10px] text-amber-700 mt-0.5">Reward: {w.reward_label}</p>
                      )}
                      {w.reward_status && (
                        <p className="text-[10px] text-emerald-600 mt-0.5">{
                          { granted: 'Entitlement granted', extended: 'Subscription extended', already_owned: 'Already owned — recorded', plan_not_found: 'Plan not found', missing_product: 'No plan selected', fulfillment_failed: 'Grant failed — retry', no_reward_configured: '' }[w.reward_status] || ''
                        }</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Select Winners (creator only, active challenges with participants) */}
          {challenge.creator_id === currentUserId && challenge.status === 'active' && leaderboard.length > 0 && !(challenge.winners && challenge.winners.length > 0) && (
            <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <h4 className="text-xs font-semibold text-blue-800">Award Winners</h4>
              <p className="text-[10px] text-blue-600">Confirm the top {Math.min(3, leaderboard.length)} {leaderboard.length === 1 ? 'participant' : 'participants'} — rewards will be granted automatically.</p>
              {/* Show the rewards that will be given */}
              {Array.isArray(challenge.rewards) && challenge.rewards.length > 0 && (
                <div className="space-y-0.5 pt-1">
                  {leaderboard.slice(0, 3).map((p, i) => {
                    const rank = i + 1;
                    const reward = (challenge.rewards || []).find(r => r.rank === rank);
                    const label = reward?.value || reward?.type?.replace(/_/g, ' ') || '—';
                    return (
                      <p key={rank} className="text-[10px] text-blue-700">
                        <span className="font-semibold">#{rank}</span> {p.user_name} → {label}
                      </p>
                    );
                  })}
                </div>
              )}
              <Button size="sm" onClick={async () => {
                const topN = leaderboard.slice(0, 3).map((p, i) => ({ user_id: p.user_id, rank: i + 1 }));
                try {
                  const resp = await axios.post(`${API}/challenges/${challenge.id}/select-winners`, { winners: topN });
                  const count = resp.data?.count || topN.length;
                  toast.success(`${count} winner${count > 1 ? 's' : ''} awarded — rewards granted`);
                  onClose();
                } catch (err) { toast.error(err.response?.data?.detail || 'Failed to award winners'); }
              }} className="bg-blue-600 text-white text-xs h-8" data-testid="select-winners-btn">
                <Trophy className="h-3 w-3 mr-1" /> Award Top {Math.min(3, leaderboard.length)}
              </Button>
            </div>
          )}

          {!joined && challenge.status === 'active' && (
            <Button onClick={handleJoinInternal} disabled={joining} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="challenge-detail-join-btn">
              {joining ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Joining…</> : 'Join Challenge'}
            </Button>
          )}
          {joined && challenge.status === 'active' && (
            <div className="flex items-center justify-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-lg py-2 border border-emerald-200" data-testid="challenge-detail-joined-indicator">
              <CheckCircle className="h-3.5 w-3.5" /> You've joined this challenge — submit proof above
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Create Challenge Dialog
const CreateChallengeDialog = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const REWARD_TYPES = [
    { id: 'free_subscription', label: 'Free 1-Month Subscription' },
    { id: 'free_workout_plan', label: 'Free Workout Plan' },
    { id: 'free_meal_plan', label: 'Free Meal Plan' },
    { id: 'shoutout', label: 'Shoutout / Featured' },
    { id: 'badge', label: 'Achievement Badge' },
  ];
  const defaultRewards = () => ([
    { rank: 1, type: 'shoutout', value: '', product_id: '' },
    { rank: 2, type: 'shoutout', value: '', product_id: '' },
    { rank: 3, type: 'shoutout', value: '', product_id: '' },
  ]);
  const [form, setForm] = useState({
    title: '', description: '', challenge_type: 'general',
    start_date: '', end_date: '', cover_image: '',
    audience: 'followers', goal: '', rules: '',
    rewards: defaultRewards(),
    fitness_niches: [],
  });
  const [uploading, setUploading] = useState(false);
  const [creatorWorkoutPlans, setCreatorWorkoutPlans] = useState([]);
  const [creatorMealPlans, setCreatorMealPlans] = useState([]);
  useEffect(() => {
    if (!open || !user?.id) return;
    (async () => {
      try {
        const [wp, mp] = await Promise.all([
          axios.get(`${API}/workout-plans?creator_id=${user.id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/meal-plans?creator_id=${user.id}`).catch(() => ({ data: [] })),
        ]);
        setCreatorWorkoutPlans(wp.data || []);
        setCreatorMealPlans(mp.data || []);
      } catch {}
    })();
  }, [open, user?.id]);
  const validateRewards = () => {
    for (const r of form.rewards) {
      if (r.type === 'free_workout_plan' && !r.product_id) {
        toast.error(`Select a workout plan for rank ${r.rank}`);
        return false;
      }
      if (r.type === 'free_meal_plan' && !r.product_id) {
        toast.error(`Select a meal plan for rank ${r.rank}`);
        return false;
      }
    }
    return true;
  };
  const handleSubmit = async () => {
    if (!form.title.trim()) { toast.error('Challenge title is required'); return; }
    if (!validateRewards()) return;
    try {
      const creatorTz = (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; } })();
      await axios.post(`${API}/challenges`, { ...form, timezone: creatorTz });
      toast.success('Challenge created!');
      setForm({ title: '', description: '', challenge_type: 'general', start_date: '', end_date: '', cover_image: '', audience: 'followers', goal: '', rules: '', rewards: defaultRewards(), fitness_niches: [] });
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create challenge'); }
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('category', 'featured');
      const { data } = await axios.post(`${API}/upload/media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, cover_image: data.url }));
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };
  const CHALLENGE_TYPES = ['general', 'workout', 'nutrition', 'mindset', 'consistency'];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-challenge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-amber-500" /> Create Challenge
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Title *</label>
            <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="30-Day Push-Up Challenge" data-testid="challenge-title" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Describe the challenge..." className="w-full h-20 px-3 py-2 border rounded-lg text-sm resize-none" data-testid="challenge-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select value={form.challenge_type} onValueChange={v => setForm(f => ({...f, challenge_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CHALLENGE_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Audience</label>
              <Select value={form.audience} onValueChange={v => setForm(f => ({...f, audience: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="everyone">Everyone</SelectItem>
                  <SelectItem value="followers">Followers Only</SelectItem>
                  <SelectItem value="subscribers">Subscribers Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Start Date</label>
              <Input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} data-testid="challenge-start-date" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">End Date</label>
              <Input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} data-testid="challenge-end-date" />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Goal</label>
            <Input value={form.goal} onChange={e => setForm(f => ({...f, goal: e.target.value}))} placeholder="e.g., Complete 100 push-ups daily" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Rules / Instructions</label>
            <textarea value={form.rules} onChange={e => setForm(f => ({...f, rules: e.target.value}))} placeholder="Challenge rules..." className="w-full h-16 px-3 py-2 border rounded-lg text-sm resize-none" />
          </div>
          {/* Fitness Niches */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Fitness niches <span className="text-xs text-gray-400 font-normal">(helps discovery)</span></label>
            <NicheSelector selected={form.fitness_niches} onChange={(niches) => setForm(f => ({...f, fitness_niches: niches}))} max={3} />
          </div>

          {/* Rewards */}
          <div className="space-y-3">
            <label className="text-sm font-medium">Rewards (Top 3)</label>
            <p className="text-[11px] text-gray-500 -mt-1">Choose one reward type per rank. Plan rewards require you to pick the specific plan you're giving away.</p>
            {form.rewards.map((r, i) => {
              const rankLabel = ['1st Place', '2nd Place', '3rd Place'][i];
              const medal = ['text-amber-500', 'text-gray-400', 'text-orange-400'][i];
              const plans = r.type === 'free_workout_plan' ? creatorWorkoutPlans : (r.type === 'free_meal_plan' ? creatorMealPlans : null);
              const needsPlanSelect = r.type === 'free_workout_plan' || r.type === 'free_meal_plan';
              const placeholderByType = {
                free_subscription: 'Auto: 1-month free subscription',
                discount: 'e.g., 20% off — code: SUMMER20',
                shoutout: 'e.g., Pinned on profile for 1 week',
                badge: 'e.g., 30-Day Pushup Champion',
              };
              return (
                <div key={i} className="p-2.5 bg-gray-50 rounded-lg border border-gray-100 space-y-2" data-testid={`reward-rank-${i + 1}`}>
                  <div className="flex items-center gap-2">
                    <Trophy className={`h-4 w-4 ${medal} shrink-0`} />
                    <span className="text-xs font-medium text-gray-700 w-16 shrink-0">{rankLabel}</span>
                    <Select value={r.type} onValueChange={v => { const rw = [...form.rewards]; rw[i] = {...rw[i], type: v, product_id: ''}; setForm(f => ({...f, rewards: rw})); }}>
                      <SelectTrigger className="h-8 text-xs flex-1" data-testid={`reward-type-${i + 1}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{REWARD_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {needsPlanSelect ? (
                    <Select value={r.product_id || ''} onValueChange={v => { const rw = [...form.rewards]; const chosen = (plans || []).find(p => p.id === v); rw[i] = {...rw[i], product_id: v, value: chosen?.title || ''}; setForm(f => ({...f, rewards: rw})); }}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`reward-plan-${i + 1}`}>
                        <SelectValue placeholder={r.type === 'free_workout_plan' ? 'Select a workout plan…' : 'Select a meal plan…'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(plans || []).length === 0 ? (
                          <div className="px-2 py-1.5 text-xs text-gray-400">No plans yet — create one first</div>
                        ) : (plans || []).map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : r.type === 'free_subscription' ? (
                    <Input value="1-month free subscription (auto)" disabled className="text-xs h-8 bg-white" />
                  ) : (
                    <Input value={r.value} onChange={e => { const rw = [...form.rewards]; rw[i] = {...rw[i], value: e.target.value}; setForm(f => ({...f, rewards: rw})); }} placeholder={placeholderByType[r.type] || 'Details…'} className="text-xs h-8" data-testid={`reward-value-${i + 1}`} />
                  )}
                </div>
              );
            })}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Cover Image</label>
            {form.cover_image ? (
              <div className="relative h-32 rounded-lg overflow-hidden border">
                <img src={resolveMediaUrl(form.cover_image)} alt="Cover" className="w-full h-full object-cover" />
                <button onClick={() => setForm(f => ({...f, cover_image: ''}))} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1"><X className="h-3 w-3" /></button>
              </div>
            ) : (
              <div>
                <input type="file" accept="image/*" className="hidden" id="challenge-cover-upload" onChange={handleImageUpload} />
                <label htmlFor="challenge-cover-upload" className="flex items-center justify-center h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-amber-400 transition-colors">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" /> : <Image className="h-5 w-5 text-gray-400" />}
                </label>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="create-challenge-submit">
              Create Challenge
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// My Rewards Panel — user-centric view of rewards the current user has won (challenge_rewards_granted)
const MyRewardsPanel = ({ rewards, loading, onOpenChallenge, onNavigateAway }) => {
  const navigate = useNavigate();
  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (!rewards || rewards.length === 0) {
    return (
      <div className="text-center py-10" data-testid="my-rewards-empty">
        <Trophy className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">No rewards yet — win a challenge to unlock rewards.</p>
      </div>
    );
  }
  const TYPE_META = {
    free_subscription: { icon: Crown, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Free subscription' },
    free_workout_plan: { icon: Dumbbell, color: 'text-blue-600 bg-blue-50 border-blue-200', label: 'Free workout plan' },
    free_meal_plan: { icon: Apple, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Free meal plan' },
    discount: { icon: Gift, color: 'text-rose-600 bg-rose-50 border-rose-200', label: 'Discount' },
    shoutout: { icon: Flame, color: 'text-orange-600 bg-orange-50 border-orange-200', label: 'Shoutout' },
    badge: { icon: Trophy, color: 'text-amber-700 bg-amber-50 border-amber-200', label: 'Badge' },
  };
  // Badge rarity mirrors the backend catalog tiering by rank
  const BADGE_RANK_META = {
    1: { label: 'Gold Medalist', color: 'text-amber-700 bg-amber-50 border-amber-300', rarityLabel: 'Epic' },
    2: { label: 'Silver Medalist', color: 'text-blue-700 bg-blue-50 border-blue-300', rarityLabel: 'Rare' },
    3: { label: 'Bronze Medalist', color: 'text-orange-700 bg-orange-50 border-orange-300', rarityLabel: 'Uncommon' },
  };
  const STATUS_COPY = {
    granted: { text: 'Unlocked', cls: 'text-emerald-700 bg-emerald-50' },
    extended: { text: 'Subscription extended', cls: 'text-emerald-700 bg-emerald-50' },
    already_owned: { text: 'Already owned', cls: 'text-gray-600 bg-gray-100' },
    missing_product: { text: 'Plan not specified', cls: 'text-amber-700 bg-amber-50' },
    plan_not_found: { text: 'Plan missing', cls: 'text-red-700 bg-red-50' },
    fulfillment_failed: { text: 'Grant failed', cls: 'text-red-700 bg-red-50' },
    no_reward_configured: { text: 'No reward attached', cls: 'text-gray-600 bg-gray-100' },
    unknown_type: { text: 'Unknown reward', cls: 'text-gray-600 bg-gray-100' },
  };
  return (
    <div className="space-y-3" data-testid="my-rewards-panel">
      {rewards.map(r => {
        let meta = TYPE_META[r.reward_type] || { icon: Trophy, color: 'text-gray-600 bg-gray-100 border-gray-200', label: r.reward_type || 'Reward' };
        // For badge rewards, override meta with rank-specific gold/silver/bronze styling so My Rewards matches catalog rendering
        if (r.reward_type === 'badge') {
          const rankMeta = BADGE_RANK_META[r.rank] || BADGE_RANK_META[3];
          meta = { icon: Trophy, color: rankMeta.color, label: rankMeta.label, rarityLabel: rankMeta.rarityLabel };
        }
        const Icon = meta.icon;
        const status = STATUS_COPY[r.status] || { text: r.status || 'Recorded', cls: 'text-gray-600 bg-gray-100' };
        const rankLabel = { 1: '1st', 2: '2nd', 3: '3rd' }[r.rank] || `#${r.rank}`;
        const expiresText = r.expires_at ? new Date(r.expires_at).toLocaleDateString() : null;
        const canLink = (r.reward_type === 'free_workout_plan' || r.reward_type === 'free_meal_plan') && r.product_id;
        return (
          <Card key={r.id} className={`border ${meta.color.split(' ').filter(c => c.startsWith('border-')).join(' ')}`} data-testid={`my-reward-${r.id}`}>
            <CardContent className="p-3">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg shrink-0 ${meta.color.split(' ').filter(c => !c.startsWith('border-')).join(' ')}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-gray-900">{meta.label}</span>
                    <Badge variant="outline" className="text-[10px]">{rankLabel} place</Badge>
                    {meta.rarityLabel && (
                      <Badge className="text-[10px] border-0 bg-purple-50 text-purple-700" data-testid={`my-reward-rarity-${r.id}`}>{meta.rarityLabel}</Badge>
                    )}
                    <Badge className={`text-[10px] border-0 ${status.cls}`}>{status.text}</Badge>
                  </div>
                  <p className="text-xs text-gray-700 mt-1 line-clamp-2">{r.label || r.reward_value || '—'}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-500 flex-wrap">
                    {r.challenge_title && (
                      <button onClick={() => onOpenChallenge && onOpenChallenge(r.challenge_id)} className="hover:text-amber-600 underline-offset-2 hover:underline" data-testid={`my-reward-open-challenge-${r.id}`}>
                        from "{r.challenge_title}"
                      </button>
                    )}
                    {expiresText && <span>Expires {expiresText}</span>}
                    {r.granted_at && <span>Won {new Date(r.granted_at).toLocaleDateString()}</span>}
                  </div>
                  {r.reward_type === 'discount' && r.reward_value && (
                    <div className="mt-2 inline-flex items-center gap-2 px-2 py-1 bg-white border border-rose-200 rounded-md">
                      <span className="text-[10px] font-mono text-rose-700">{r.reward_value}</span>
                    </div>
                  )}
                  {canLink && (
                    <Button size="sm" variant="outline" className="mt-2 text-[11px] h-7" onClick={() => {
                      if (onNavigateAway) onNavigateAway();
                      if (r.reward_type === 'free_workout_plan') {
                        navigate('/dashboard?tab=library');
                        toast.success('Find it under "Library → Workouts"');
                      } else {
                        navigate('/dashboard?tab=library');
                        toast.success('Find it under "Library → Meals"');
                      }
                    }} data-testid={`my-reward-open-plan-${r.id}`}>
                      {r.reward_type === 'free_workout_plan' ? 'Open in Library → Workouts' : 'Open in Library → Meals'}
                    </Button>
                  )}
                  {r.reward_type === 'free_subscription' && (r.status === 'granted' || r.status === 'extended') && (
                    <p className="text-[10px] text-emerald-700 mt-1.5">Active — view under My Subscriptions</p>
                  )}
                  {r.reward_type === 'badge' && r.status === 'granted' && (
                    <p className="text-[10px] text-violet-700 mt-1.5">Added to your profile badges</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

// Challenges Manager Modal — unified dashboard panel for managing and browsing challenges
const ChallengesManagerModal = ({ open, onClose, challenges, isCreator, onCreateClick, onRefresh, currentUserId, initialChallengeId, onInitialConsumed }) => {
  const [tab, setTab] = useState('active');
  const [openDetailId, setOpenDetailId] = useState(null);
  const [myRewards, setMyRewards] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const consumedRef = React.useRef(false);
  useEffect(() => {
    // Consume initialChallengeId only once per open cycle to avoid reopen loops
    if (open && initialChallengeId && !consumedRef.current) {
      consumedRef.current = true;
      setOpenDetailId(initialChallengeId);
      const ch = (challenges || []).find(c => c.id === initialChallengeId);
      if (ch) {
        if (ch.status === 'completed') setTab(ch.winners?.length ? 'winners' : 'completed');
        else setTab('active');
      }
      // Tell parent to clear the initialChallengeId so a later reopen of the manager
      // doesn't re-pop the detail modal
      if (onInitialConsumed) onInitialConsumed();
    } else if (!open) {
      setOpenDetailId(null);
      consumedRef.current = false;
    }
  }, [open, initialChallengeId]);

  // Fetch my rewards when the tab is opened
  useEffect(() => {
    if (!open || tab !== 'my-rewards') return;
    let aborted = false;
    (async () => {
      setRewardsLoading(true);
      try {
        const { data } = await axios.get(`${API}/challenges/rewards/my`);
        if (!aborted) setMyRewards(Array.isArray(data) ? data : []);
      } catch {
        if (!aborted) setMyRewards([]);
      } finally {
        if (!aborted) setRewardsLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [open, tab]);

  const active = (challenges || []).filter(c => c.status === 'active');
  const completed = (challenges || []).filter(c => c.status === 'completed');
  const winners = completed.filter(c => Array.isArray(c.winners) && c.winners.length > 0);

  const openDetailChallenge = openDetailId ? (challenges || []).find(c => c.id === openDetailId) : null;

  const renderList = (list, emptyText) => (
    list.length === 0 ? (
      <div className="text-center py-10">
        <Flame className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">{emptyText}</p>
      </div>
    ) : (
      <div className="grid gap-3 sm:grid-cols-2">
        {list.map(ch => (
          <button key={ch.id} onClick={() => setOpenDetailId(ch.id)} className="text-left" data-testid={`manager-challenge-${ch.id}`}>
            <Card className="hover:border-amber-300 transition-colors overflow-hidden">
              {ch.cover_image && <img src={resolveMediaUrl(ch.cover_image)} alt="" className="w-full h-24 object-cover" />}
              <CardContent className="p-3 space-y-1.5">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">{ch.challenge_type}</Badge>
                  <Badge variant="outline" className="text-[10px]">{ch.participants_count} joined</Badge>
                  {ch.status === 'completed' && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">Completed</Badge>}
                </div>
                <p className="text-sm font-semibold text-gray-900 line-clamp-1">{ch.title}</p>
                {ch.description && <p className="text-xs text-gray-500 line-clamp-2">{ch.description}</p>}
                {ch.status === 'completed' && Array.isArray(ch.winners) && ch.winners.length > 0 && (
                  <div className="pt-1 flex items-center gap-1 text-[10px] text-amber-700">
                    <Trophy className="h-3 w-3" />
                    <span>{ch.winners.length} winner{ch.winners.length > 1 ? 's' : ''} awarded</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </button>
        ))}
      </div>
    )
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="challenges-manager-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-amber-500" /> Challenges
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 gap-2 flex-wrap">
              <div className="flex gap-1 flex-wrap">
                {[
                  { id: 'active', label: 'Active', count: active.length },
                  { id: 'completed', label: 'Completed', count: completed.length },
                  { id: 'winners', label: 'Winners', count: winners.length },
                  { id: 'my-rewards', label: 'My Rewards', count: myRewards.length, hideCount: tab !== 'my-rewards' && myRewards.length === 0 },
                ].map(t => (
                  <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${tab === t.id ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'text-gray-500 hover:text-gray-700'}`} data-testid={`manager-tab-${t.id}`}>
                    {t.label}{t.hideCount ? '' : <span className="text-[10px] opacity-60"> ({t.count})</span>}
                  </button>
                ))}
              </div>
              {isCreator && (
                <Button size="sm" onClick={onCreateClick} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs h-8" data-testid="manager-create-challenge-btn">
                  <Flame className="h-3 w-3 mr-1" /> New Challenge
                </Button>
              )}
            </div>
            {tab === 'active' && renderList(active, isCreator ? 'No active challenges yet — create one!' : 'No active challenges right now.')}
            {tab === 'completed' && renderList(completed, 'No completed challenges yet.')}
            {tab === 'winners' && renderList(winners, 'No winners announced yet.')}
            {tab === 'my-rewards' && (
              <MyRewardsPanel rewards={myRewards} loading={rewardsLoading} onOpenChallenge={(chId) => { setOpenDetailId(chId); }} onNavigateAway={onClose} />
            )}
          </div>
        </DialogContent>
      </Dialog>
      {openDetailChallenge && (
        <ChallengeDetailModal
          challenge={openDetailChallenge}
          open={!!openDetailChallenge}
          onClose={() => { setOpenDetailId(null); if (onRefresh) onRefresh(); }}
          onJoined={() => { if (onRefresh) onRefresh(); }}
          currentUserId={currentUserId}
        />
      )}
    </>
  );
};

// Profile Challenges Tab — accepts `challenges` prop (single source of truth with the tab header count)
const ProfileChallengesTab = ({ challenges, currentUserId }) => {
  if (!challenges) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>;
  if (challenges.length === 0) return (
    <div className="text-center py-12">
      <Flame className="h-16 w-16 text-gray-300 mx-auto mb-4" />
      <p className="text-gray-600">No challenges yet</p>
    </div>
  );
  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="profile-challenges-tab">
      {challenges.map(ch => (
        <ChallengeFeedCard key={ch.id} challenge={ch} currentUserId={currentUserId} />
      ))}
    </div>
  );
};

// Challenge Feed Card — shown in the main feed
const ChallengeFeedCard = ({ challenge, currentUserId }) => {
  const navigate = useNavigate();
  const [joinStatus, setJoinStatus] = useState(null);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await axios.get(`${API}/challenges/${challenge.id}/status`);
        setJoinStatus(data.joined ? 'joined' : 'available');
        setCurrentStreak(Number(data.current_streak || 0));
        setLongestStreak(Number(data.longest_streak || 0));
      } catch { setJoinStatus('available'); }
    };
    check();
  }, [challenge.id]);
  const handleJoin = async (e) => {
    e?.stopPropagation();
    try { await axios.post(`${API}/challenges/${challenge.id}/join`); setJoinStatus('joined'); toast.success('Joined challenge!'); } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };
  const audienceLabel = { everyone: 'Public', followers: 'Followers only', subscribers: 'Subscribers only' };
  return (
    <>
      <Card className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => setShowDetail(true)} data-testid={`challenge-feed-${challenge.id}`}>
        {challenge.cover_image && (
          <img src={resolveMediaUrl(challenge.cover_image)} alt="" className="w-full h-40 object-cover" />
        )}
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-amber-50 rounded-lg"><Flame className="h-4 w-4 text-amber-500" /></div>
                <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Challenge</Badge>
                <Badge variant="outline" className="text-[10px]">{challenge.challenge_type}</Badge>
              </div>
              <h3 className="text-base font-bold text-gray-900 line-clamp-1">{challenge.title}</h3>
              <p className="text-xs text-gray-500 mt-0.5 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); navigate(`/profile/${challenge.creator_id}`); }}>
                by {challenge.creator_name}
              </p>
              {challenge.description && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{challenge.description}</p>}
              <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                {challenge.start_date && <span>Starts {challenge.start_date}</span>}
                {challenge.end_date && <span>Ends {challenge.end_date}</span>}
                <span>{challenge.participants_count} joined</span>
                <span>{audienceLabel[challenge.audience]}</span>
              </div>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-2">
              {joinStatus === 'joined' ? (
                <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" /> Joined</Badge>
              ) : (
                <Button size="sm" onClick={handleJoin} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs">
                  Join
                </Button>
              )}
              {joinStatus === 'joined' && currentStreak > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 border border-orange-200 text-[11px] font-semibold text-orange-700"
                  title={longestStreak > currentStreak ? `Best: ${longestStreak}-day streak` : 'Current streak'}
                  data-testid={`challenge-streak-feed-${challenge.id}`}
                >
                  <span aria-hidden>🔥</span>
                  <span>{currentStreak}-day streak</span>
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <ChallengeDetailModal challenge={challenge} open={showDetail} onClose={() => setShowDetail(false)} joined={joinStatus === 'joined'} onJoined={() => setJoinStatus('joined')} currentUserId={currentUserId} />
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Library Tab — groups everything the user has collected on OnlyMass:
//   • My Workouts          (workout plans they own / were granted)
//   • My Meals             (meal plans they own / were granted)
//   • Saved Posts          (bookmarks where product_type === 'post')
//   • Saved Challenges     (bookmarks where product_type === 'challenge')
// All four use the same Save/Unsave endpoints (/api/bookmarks/...).
// ─────────────────────────────────────────────────────────────────────────
const LibraryTab = ({ myWorkoutPlans = [], myMealPlans = [], currentUserId, onOpenPost }) => {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState('workouts');
  const [savedPosts, setSavedPosts] = useState([]);
  const [savedChallenges, setSavedChallenges] = useState([]);
  const [loadingSavedPosts, setLoadingSavedPosts] = useState(false);
  const [loadingSavedChallenges, setLoadingSavedChallenges] = useState(false);

  const loadSavedPosts = useCallback(async () => {
    setLoadingSavedPosts(true);
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { params: { product_type: 'post' } });
      const items = await Promise.all(
        (data || []).map(async (b) => {
          try {
            const r = await axios.get(`${API}/posts/${b.product_id}`);
            return r.data;
          } catch (e) {
            return null;
          }
        })
      );
      // Saved Posts must NEVER contain challenge companion posts (those belong to
      // Saved Challenges). Filter defensively so legacy bookmarks saved before the
      // classification fix don't leak into this tab.
      setSavedPosts(items.filter(p => p && !p.challenge_id));
    } catch (e) {
      setSavedPosts([]);
    } finally {
      setLoadingSavedPosts(false);
    }
  }, []);

  const loadSavedChallenges = useCallback(async () => {
    setLoadingSavedChallenges(true);
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { params: { product_type: 'challenge' } });
      const items = await Promise.all(
        (data || []).map(async (b) => {
          try {
            const r = await axios.get(`${API}/challenges/${b.product_id}`);
            return r.data;
          } catch (e) {
            return null;
          }
        })
      );
      setSavedChallenges(items.filter(Boolean));
    } catch (e) {
      setSavedChallenges([]);
    } finally {
      setLoadingSavedChallenges(false);
    }
  }, []);

  useEffect(() => {
    if (subTab === 'saved-posts') loadSavedPosts();
    if (subTab === 'saved-challenges') loadSavedChallenges();
  }, [subTab, loadSavedPosts, loadSavedChallenges]);

  return (
    <div className="space-y-5" data-testid="library-tab">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-sm">
          <BookMarked className="h-5 w-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Library</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Everything you own, won, and saved</p>
        </div>
      </div>

      <Tabs value={subTab} onValueChange={setSubTab} className="space-y-4">
        <TabsList className="flex w-full bg-gray-50 dark:bg-[#111a2e] border border-gray-100 dark:border-[#1e2d4a] overflow-x-auto scrollbar-none">
          <TabsTrigger value="workouts" className="flex-1 min-w-0" data-testid="library-sub-workouts">
            <Trophy className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline ml-1.5">Workouts</span>
          </TabsTrigger>
          <TabsTrigger value="meals" className="flex-1 min-w-0" data-testid="library-sub-meals">
            <CheckCircle className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline ml-1.5">Meals</span>
          </TabsTrigger>
          <TabsTrigger value="saved-posts" className="flex-1 min-w-0" data-testid="library-sub-saved-posts">
            <Bookmark className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline ml-1.5">Saved Posts</span>
          </TabsTrigger>
          <TabsTrigger value="saved-challenges" className="flex-1 min-w-0" data-testid="library-sub-saved-challenges">
            <Flame className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline ml-1.5">Saved Challenges</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="workouts" className="space-y-4">
          <MyWorkoutPlansTab myWorkoutPlans={myWorkoutPlans} />
        </TabsContent>

        <TabsContent value="meals" className="space-y-4">
          <MyMealPlansTab myMealPlans={myMealPlans} />
        </TabsContent>

        <TabsContent value="saved-posts" className="space-y-4">
          {loadingSavedPosts ? (
            <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-blue-500" /></div>
          ) : savedPosts.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-gray-200 dark:border-[#1e2d4a] rounded-2xl bg-white dark:bg-[#0e1729]" data-testid="saved-posts-empty">
              <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-[#111a2e] flex items-center justify-center mx-auto mb-3">
                <Bookmark className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">No saved posts yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                Tap the bookmark on any feed post to save it here for later.
              </p>
            </div>
          ) : (
            savedPosts.map(post => (
              <PostCard
                key={post.id}
                post={post}
                currentUserId={currentUserId}
                onDelete={(postId) => setSavedPosts(prev => prev.filter(p => p.id !== postId))}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="saved-challenges" className="space-y-4">
          {loadingSavedChallenges ? (
            <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-blue-500" /></div>
          ) : savedChallenges.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-gray-200 dark:border-[#1e2d4a] rounded-2xl bg-white dark:bg-[#0e1729]" data-testid="saved-challenges-empty">
              <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-[#111a2e] flex items-center justify-center mx-auto mb-3">
                <Flame className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-100">No saved challenges yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                Bookmark a challenge to keep it handy in your Library.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {savedChallenges.map(ch => (
                <ChallengeCard key={ch.id} challenge={ch} currentUserId={currentUserId} />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};


// Dashboard Component
const Dashboard = () => {
  const { user, setUser, logout } = useAuth();
  const { logActivity } = useActivityLogger();
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showGamificationDashboard, setShowGamificationDashboard] = useState(false);
  const [showWorkoutProgressDashboard, setShowWorkoutProgressDashboard] = useState(false);
  const [showWorkoutCalendar, setShowWorkoutCalendar] = useState(false);
  const [showCompleteWorkoutModal, setShowCompleteWorkoutModal] = useState(false);
  const [showReferralPanel, setShowReferralPanel] = useState(false);
  const [showUserSubscriptions, setShowUserSubscriptions] = useState(false);
  const [showCreatorSubscribers, setShowCreatorSubscribers] = useState(false);
  const [showCreateChallenge, setShowCreateChallenge] = useState(false);
  const [showChallengesManager, setShowChallengesManager] = useState(false);
  const [openChallengeId, setOpenChallengeId] = useState(null);
  const [openPostId, setOpenPostId] = useState(null);
  const [openPostData, setOpenPostData] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    content_type: 'photo',
    post_type: 'general',
    media_url: '',
    media_type: '',
    poster_url: null,
    fitness_niches: [],
    is_premium: false,
    access_type: 'public',
    tip_enabled: true,
    min_tip_amount: 5
  });
  const [postMediaUploading, setPostMediaUploading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostData, setEditPostData] = useState({});
  const [showCreateWorkoutPlan, setShowCreateWorkoutPlan] = useState(false);
  const [showCreateLiveStream, setShowCreateLiveStream] = useState(false);
  const [showCreateMealPlan, setShowCreateMealPlan] = useState(false);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [myWorkoutPlans, setMyWorkoutPlans] = useState([]);
  const [myMealPlans, setMyMealPlans] = useState([]);
  const [activeTab, setActiveTab] = useState('feed');

  useEffect(() => {
    fetchPosts();
    fetchWorkoutPlans();
    fetchLiveStreams();
    fetchMealPlans();
    fetchMyWorkoutPlans();
    fetchMyMealPlans();
  }, []);

  // Handle deep-link params (e.g., ?openChallenge=<id>) for notifications
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const chId = params.get('openChallenge');
    const postId = params.get('post');
    const tabParam = params.get('tab');
    let consumed = false;
    if (chId) {
      setOpenChallengeId(chId);
      setShowChallengesManager(true);
      consumed = true;
    }
    if (postId) {
      setOpenPostId(postId);
      (async () => {
        try {
          const { data } = await axios.get(`${API}/posts/${postId}`);
          setOpenPostData(data);
        } catch (err) {
          const local = posts.find(p => p.id === postId);
          if (local) setOpenPostData(local);
          else toast.error('Post not found or no longer available');
        }
      })();
      consumed = true;
    }
    if (tabParam) {
      // Backward-compat: old "my-workouts" / "my-meals" URLs land on Library
      const tabMap = { 'my-workouts': 'library', 'my-meals': 'library' };
      setActiveTab(tabMap[tabParam] || tabParam);
      consumed = true;
    }
    if (consumed) {
      const newParams = new URLSearchParams(location.search);
      newParams.delete('openChallenge');
      newParams.delete('post');
      newParams.delete('tab');
      const newSearch = newParams.toString();
      navigate(`/dashboard${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    }
  }, [location.search]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkoutPlans = async () => {
    try {
      const response = await axios.get(`${API}/workout-plans`);
      setWorkoutPlans(response.data);
    } catch (error) {
      console.error('Error fetching workout plans:', error);
    }
  };

  const fetchLiveStreams = async () => {
    try {
      const response = await axios.get(`${API}/streams`);
      setLiveStreams(response.data);
    } catch (error) {
      console.error('Error fetching live streams:', error);
    }
  };

  const fetchMyWorkoutPlans = async () => {
    try {
      const response = await axios.get(`${API}/my-workout-plans`);
      setMyWorkoutPlans(response.data);
    } catch (error) {
      console.error('Error fetching my workout plans:', error);
    }
  };

  const fetchMealPlans = async () => {
    try {
      const response = await axios.get(`${API}/meal-plans`);
      setMealPlans(response.data);
    } catch (error) {
      console.error('Error fetching meal plans:', error);
    }
  };

  const fetchMyMealPlans = async () => {
    try {
      const response = await axios.get(`${API}/my-meal-plans`);
      setMyMealPlans(response.data);
    } catch (error) {
      console.error('Error fetching my meal plans:', error);
    }
  };

  const fetchChallenges = async () => {
    try {
      const { data } = await axios.get(`${API}/challenges`);
      setChallenges(data || []);
    } catch {}
  };

  React.useEffect(() => { fetchChallenges(); }, []);

  const createPost = async () => {
    try {
      await axios.post(`${API}/posts`, newPost);
      
      setNewPost({
        title: '',
        content: '',
        content_type: 'photo',
        post_type: 'general',
        media_url: '',
        media_type: '',
        poster_url: null,
        fitness_niches: [],
        is_premium: false,
        access_type: 'public',
        tip_enabled: true,
        min_tip_amount: 5
      });
      setShowCreatePost(false);
      fetchPosts();
      
      // Log activity for gamification
      await logActivity('post_created', `Created post: ${newPost.title}`);
      
      toast.success('Post created successfully!');
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    }
  };

  const handlePostMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Client-side file size validation
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '50' : '10'}MB.`);
      return;
    }

    setPostMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${API}/upload/media?category=post`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      let posterUrl = null;
      if (isVideo) {
        const frameBlob = await extractVideoFrame(file);
        posterUrl = await uploadPosterBlob(frameBlob);
      }

      setNewPost(prev => ({
        ...prev,
        media_url: resp.data.url,
        media_type: isVideo ? 'video' : 'image',
        content_type: isVideo ? 'video' : 'photo',
        poster_url: posterUrl || prev.poster_url || null
      }));
      toast.success(`${isVideo ? 'Video' : 'Photo'} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setPostMediaUploading(false);
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditPostData({
      title: post.title || '',
      content: post.content || '',
      post_type: post.post_type || 'general',
      fitness_niches: post.fitness_niches || [],
      media_url: post.media_url || '',
      media_type: post.media_type || '',
      poster_url: post.poster_url || null,
      content_type: post.content_type || 'text',
      access_type: post.access_type || 'public',
    });
  };

  const handleSaveEditPost = async () => {
    if (!editingPost) return;
    try {
      const token = Cookies.get('session_token');
      await axios.put(`${API}/posts/${editingPost.id}`, editPostData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post updated');
      setEditingPost(null);
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeletePost = (postId) => {
    setPosts(prev => prev.filter(p => p.id !== postId));
  };

  const handleEditPostMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '50' : '10'}MB.`);
      return;
    }
    setPostMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${API}/upload/media?category=post`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      let posterUrl = null;
      if (isVideo) {
        const frameBlob = await extractVideoFrame(file);
        posterUrl = await uploadPosterBlob(frameBlob);
      }
      setEditPostData(prev => ({
        ...prev,
        media_url: resp.data.url,
        media_type: isVideo ? 'video' : 'image',
        content_type: isVideo ? 'video' : 'photo',
        poster_url: posterUrl || prev.poster_url || null
      }));
      toast.success(`${isVideo ? 'Video' : 'Photo'} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setPostMediaUploading(false);
    }
  };

  const likePost = async (postId) => {
    try {
      await axios.post(`${API}/posts/${postId}/like`);
      fetchPosts();
      
      // Log activity for gamification
      await logActivity('like_given', `Liked a post`);
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0b1120] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
            <Dumbbell className="h-6 w-6 text-white" />
          </div>
          <p className="text-gray-600 dark:text-gray-400">Loading your fitness feed...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1120]">
      {/* Header */}
      <header className="bg-white dark:bg-[#111a2e] border-b border-gray-200 dark:border-[#1e2d4a] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Left side - Logo and Display Name */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                <img src="/brand/logo-icon.png" alt="OnlyMass" className="w-8 h-8 rounded-lg" />
                <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                  OnlyMass
                </span>
              </div>
              
              <div className="h-6 w-px bg-gray-300 dark:bg-[#253552]"></div>
              
              <div className="flex items-center space-x-2">
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {user?.display_name || user?.name || 'User'}
                </span>
                {user?.is_creator && (
                  <Badge className="bg-blue-100 text-blue-700 px-2 py-1 text-xs">
                    Creator
                  </Badge>
                )}
              </div>
            </div>

            {/* Center - Search Bar */}
            <div className="flex-1 max-w-md mx-8">
              <UserSearch />
            </div>
              
            {/* Right side - Notifications + Profile (Logout lives inside the avatar dropdown) */}
            <div className="flex items-center space-x-2">
              <NotificationsPanel />
              <ProfileDropdown user={user} updateUser={setUser} onLogout={logout} />
            </div>
          </div>
        </div>
      </header>

      {/* Creation Buttons Bar */}
      <div className="bg-gray-50 border-b border-gray-200 dark:border-[#1e2d4a]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center space-x-4">
            <Button onClick={() => setShowCreatePost(true)} className="om-accent-gradient text-white">
              <Plus className="h-4 w-4 mr-2" />
              Create Post
            </Button>
            <Button onClick={() => setShowCreateWorkoutPlan(true)} variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50">
              <Dumbbell className="h-4 w-4 mr-2" />
              Workout Plan
            </Button>
            <Button onClick={() => setShowCreateMealPlan(true)} variant="outline" className="border-green-200 text-green-600 hover:bg-green-50">
              <Apple className="h-4 w-4 mr-2" />
              Meal Plan
            </Button>
            <Button onClick={() => setActiveTab('live-streams')} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 relative">
              <PlayCircle className="h-4 w-4 mr-2" />
              Live Stream
              <span className="absolute -top-1.5 -right-1.5 px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full leading-none">SOON</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <Card 
              className="border-blue-100 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200"
              onClick={() => navigate(`/profile/${user?.id}`)}
            >
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={resolveMediaUrl(user?.profile_picture)} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 text-lg">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg hover:text-blue-600 transition-colors">{user?.name}</CardTitle>
                    {user?.is_creator && (
                      <Badge className="bg-blue-100 text-blue-700 text-xs mt-1">Creator</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Following</span>
                    <span className="font-semibold text-blue-600">{user?.following_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Followers</span>
                    <span className="font-semibold text-blue-600 hover:text-blue-700 transition-colors">{user?.followers_count || 0}</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 text-center flex items-center justify-center">
                    <Eye className="h-3 w-3 mr-1" />
                    Click to view your profile
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Workout Progress Widget - New simplified system */}
            <WorkoutProgressWidget 
              onCompleteWorkout={() => setShowCompleteWorkoutModal(true)}
            />
            
            {/* View Progress Dashboard Button */}
            <Button 
              onClick={() => setShowWorkoutProgressDashboard(true)}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
              data-testid="view-full-progress-btn"
            >
              <Dumbbell className="h-4 w-4 mr-2" />
              View Full Progress
            </Button>

            {/* Workout Calendar Button */}
            <Button 
              onClick={() => setShowWorkoutCalendar(true)}
              className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
              data-testid="open-workout-calendar-btn"
            >
              <CalendarDays className="h-4 w-4 mr-2" />
              Workout Calendar
            </Button>

            {/* Challenges Button */}
            <Button
              onClick={() => setShowChallengesManager(true)}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              data-testid="open-challenges-btn"
            >
              <Flame className="h-4 w-4 mr-2" />
              Challenges
            </Button>

            {/* Achievements Dashboard Button */}
            <Button 
              onClick={() => setShowGamificationDashboard(true)}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              data-testid="view-achievements-btn"
            >
              <Trophy className="h-4 w-4 mr-2" />
              View Achievements & Badges
            </Button>

            {/* Purchase History Button — navigates to the /purchase-history route
                which uses the modern /api/user/purchase-history endpoint with
                normalized fields + PageErrorBoundary. The legacy in-Dashboard modal
                (PaymentComponents.PurchaseHistory) referenced wrong field names
                (item_type/purchase_date/amount_paid) and white-screened the whole
                Dashboard — replaced by a single source of truth here. */}
            <Button 
              onClick={() => navigate('/purchase-history')}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white"
              data-testid="dashboard-purchase-history-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Purchase History
            </Button>

            {/* Phase R1: Invite Friends — opens the ReferralPanel modal showing
                the user's invite link, referral status, and invitee progress. */}
            <Button
              onClick={() => setShowReferralPanel(true)}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
              data-testid="dashboard-invite-friends-btn"
            >
              <Gift className="h-4 w-4 mr-2" />
              Invite & Earn
            </Button>

            {/* Subscription Management Buttons */}
            <Button 
              onClick={() => setShowUserSubscriptions(true)}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
            >
              <Users className="h-4 w-4 mr-2" />
              My Subscriptions
            </Button>

            {/* Creator-only buttons */}
            <div className="space-y-2">
              <Button 
                onClick={() => setShowCreatorSubscribers(true)}
                className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white"
              >
                <Users className="h-4 w-4 mr-2" />
                My Subscribers
              </Button>
            </div>
          </div>

          {/* Main Content with Tabs */}
          <div className="lg:col-span-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="flex w-full om-accent-bg-light dark:bg-[#111a2e] border om-accent-border dark:border-[#1e2d4a] overflow-x-auto scrollbar-none">
                <TabsTrigger value="feed" className="flex-1 min-w-0 om-tab-accent dark:text-gray-400">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline ml-1">Feed</span>
                </TabsTrigger>
                <TabsTrigger value="discover" className="flex-1 min-w-0 om-tab-accent dark:text-gray-400" data-testid="discover-tab-trigger">
                  <Search className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline ml-1">Discover</span>
                </TabsTrigger>
                <TabsTrigger value="live-streams" className="flex-1 min-w-0 om-tab-accent dark:text-gray-400">
                  <PlayCircle className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline ml-1">Live</span>
                </TabsTrigger>
                <TabsTrigger value="library" className="flex-1 min-w-0 om-tab-accent dark:text-gray-400" data-testid="library-tab-trigger">
                  <BookMarked className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline ml-1 truncate">Library</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="feed" className="space-y-0">
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                  {/* Main feed column */}
                  <div className="xl:col-span-2 space-y-6">
                    {posts.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                          <Dumbbell className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">No posts yet</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-4">Be the first to share your fitness journey!</p>
                        <Button onClick={() => setShowCreatePost(true)} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white">
                          <Plus className="h-4 w-4 mr-2" />
                          Create Your First Post
                        </Button>
                      </div>
                    ) : (
                      <>
                        {posts.map((post) => (
                          <PostCard key={post.id} post={post} onLike={likePost} currentUserId={user?.id} onDelete={handleDeletePost} onEdit={handleEditPost} />
                        ))}
                      </>
                    )}
                  </div>

                  {/* Right sidebar — Suggested Profiles + AI Fitness Coach */}
                  <div className="xl:col-span-1 space-y-6">
                    <SuggestedProfiles currentUserId={user?.id} onNavigateToProfile={(userId) => navigate(`/profile/${userId}`)} />
                    <AIRecommendations />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="discover" className="space-y-6">
                <DiscoverTab />
              </TabsContent>

              <TabsContent value="workout-plans" className="space-y-6">
                <WorkoutPlansTab workoutPlans={workoutPlans} />
              </TabsContent>

              <TabsContent value="meal-plans" className="space-y-6">
                <MealPlansTab mealPlans={mealPlans} />
              </TabsContent>

              <TabsContent value="live-streams" className="space-y-6">
                {/* Coming Soon teaser — live streaming is not yet launched */}
                <div className="relative overflow-hidden rounded-2xl border border-gray-100">
                  <div className="absolute inset-0 bg-gradient-to-br from-red-950 via-orange-950 to-amber-950 opacity-95" />
                  <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
                  <div className="relative z-10 flex flex-col items-center justify-center text-center py-20 px-8">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center mb-6 shadow-lg shadow-red-500/20 animate-pulse">
                      <PlayCircle className="h-10 w-10 text-white" />
                    </div>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/10 mb-5">
                      <span className="w-2 h-2 bg-red-400 rounded-full animate-pulse" />
                      <span className="text-xs font-semibold text-red-200 tracking-wider uppercase">Coming Soon</span>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="live-coming-soon-title">
                      Live Streaming
                    </h2>
                    <p className="text-lg text-orange-100/80 max-w-lg mb-3 leading-relaxed">
                      Train live with your favorite creators. Real-time workouts, exclusive live sessions, and interactive fitness experiences are on the way.
                    </p>
                    <p className="text-sm text-orange-200/50 max-w-md">
                      We're building something powerful for the next phase of OnlyMass. Stay tuned.
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="library" className="space-y-6">
                <LibraryTab
                  myWorkoutPlans={myWorkoutPlans}
                  myMealPlans={myMealPlans}
                  currentUserId={user?.id}
                  onOpenPost={(postId) => navigate(`/dashboard?tab=feed&post=${postId}`)}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Create Post Dialog */}
      <Dialog open={showCreatePost} onOpenChange={setShowCreatePost}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Post</DialogTitle>
            <DialogDescription>Share your fitness journey with the community</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <Input
              placeholder="Post title"
              value={newPost.title}
              onChange={(e) => setNewPost({...newPost, title: e.target.value})}
              data-testid="create-post-title"
            />
            
            <Textarea
              placeholder="Share your fitness tip, workout, or progress..."
              value={newPost.content}
              onChange={(e) => setNewPost({...newPost, content: e.target.value})}
              rows={3}
              data-testid="create-post-content"
            />
            
            {/* Post Type Selector */}
            <PostTypeSelector 
              value={newPost.post_type}
              onChange={(value) => setNewPost({...newPost, post_type: value})}
            />
            
            {/* Fitness Niche Selector */}
            <NicheSelector
              selected={newPost.fitness_niches}
              onChange={(niches) => setNewPost({...newPost, fitness_niches: niches})}
              max={3}
            />

            {/* Media Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Photo or Video</label>
              {newPost.media_url ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200">
                  {newPost.media_type === 'video' ? (
                    <video src={resolveMediaUrl(newPost.media_url)} poster={resolveMediaUrl(newPost.poster_url)} controls preload="metadata" className="w-full max-h-40 bg-black rounded" data-testid="post-video-preview" />
                  ) : (
                    <img src={resolveMediaUrl(newPost.media_url)} alt="Preview" className="w-full max-h-40 object-cover" />
                  )}
                  <button
                    onClick={() => setNewPost({...newPost, media_url: '', media_type: '', content_type: 'text', poster_url: null})}
                    className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    onChange={handlePostMediaUpload}
                    className="hidden"
                    id="post-media-upload"
                    data-testid="post-media-upload-input"
                  />
                  <label
                    htmlFor="post-media-upload"
                    className={`flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors text-sm text-gray-600 ${postMediaUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {postMediaUploading ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4" /> Upload photo or video</>
                    )}
                  </label>
                </div>
              )}
            </div>
            
            {/* Tip Settings (creators only) */}
            {user?.is_creator && (
              <TipSettingsForm
                tipEnabled={newPost.tip_enabled}
                setTipEnabled={(v) => setNewPost({...newPost, tip_enabled: v})}
                minTipAmount={newPost.min_tip_amount}
                setMinTipAmount={(v) => setNewPost({...newPost, min_tip_amount: v})}
              />
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Access Type</label>
              <Select value={newPost.access_type} onValueChange={(value) => setNewPost({...newPost, access_type: value, is_premium: value === 'subscription'})}>
                <SelectTrigger data-testid="post-access-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">Public — Visible to everyone</SelectItem>
                  <SelectItem value="subscription">Subscribers Only — Requires subscription</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                {newPost.access_type === 'public' && 'Anyone can see this post.'}
                {newPost.access_type === 'subscription' && 'Only your subscribers can see the full content.'}
              </p>
            </div>
            
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreatePost(false)}>
                Cancel
              </Button>
              <Button onClick={createPost} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white" data-testid="create-post-submit-btn">
                Post
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <EditPostDialog
        post={editingPost}
        editData={editPostData}
        setEditData={setEditPostData}
        onSave={handleSaveEditPost}
        onClose={() => setEditingPost(null)}
        onMediaUpload={handleEditPostMediaUpload}
        mediaUploading={postMediaUploading}
      />

      {/* Create Workout Plan Dialog */}
      <CreateWorkoutPlanDialog
        open={showCreateWorkoutPlan}
        onClose={() => setShowCreateWorkoutPlan(false)}
        onSuccess={() => {
          fetchWorkoutPlans();
          setActiveTab('workout-plans');
        }}
      />

      {/* Create Meal Plan Dialog */}
      <CreateMealPlanDialog
        open={showCreateMealPlan}
        onClose={() => setShowCreateMealPlan(false)}
        onSuccess={() => {
          fetchMealPlans();
          setActiveTab('meal-plans');
        }}
      />

      {/* Create Live Stream Dialog */}
      <CreateLiveStreamDialog
        open={showCreateLiveStream}
        onClose={() => setShowCreateLiveStream(false)}
        onSuccess={() => {
          fetchLiveStreams();
          setActiveTab('live-streams');
        }}
      />

      {/* Gamification Dashboard */}
      <GamificationDashboard
        open={showGamificationDashboard}
        onClose={() => setShowGamificationDashboard(false)}
      />

      {/* Workout Calendar */}
      <WorkoutCalendar
        open={showWorkoutCalendar}
        onClose={() => setShowWorkoutCalendar(false)}
      />

      {/* Workout Progress Dashboard */}
      <WorkoutProgressDashboard
        open={showWorkoutProgressDashboard}
        onClose={() => setShowWorkoutProgressDashboard(false)}
      />

      {/* Complete Workout Modal */}
      <CompleteWorkoutModal
        open={showCompleteWorkoutModal}
        onClose={() => setShowCompleteWorkoutModal(false)}
        onComplete={() => {
          // Refresh progress after completing workout
          setShowCompleteWorkoutModal(false);
        }}
      />

      {/* Purchase History — replaced with route navigation (see button above).
          Route /purchase-history is the canonical source of truth and is wrapped
          in PageErrorBoundary, so a render error cannot blank-screen the Dashboard. */}

      {/* Phase R1: Referral / Invite & Earn panel */}
      <ReferralPanelModal
        open={showReferralPanel}
        onClose={() => setShowReferralPanel(false)}
      />

      {/* Subscription Modals */}
      <UserSubscriptions
        open={showUserSubscriptions}
        onClose={() => setShowUserSubscriptions(false)}
      />

      <CreatorSubscribers
        open={showCreatorSubscribers}
        onClose={() => setShowCreatorSubscribers(false)}
      />

      <CreateChallengeDialog
        open={showCreateChallenge}
        onClose={() => setShowCreateChallenge(false)}
        onSuccess={fetchChallenges}
      />

      <ChallengesManagerModal
        open={showChallengesManager}
        onClose={() => { setShowChallengesManager(false); setOpenChallengeId(null); }}
        challenges={challenges}
        isCreator={!!user?.is_creator}
        onCreateClick={() => setShowCreateChallenge(true)}
        onRefresh={fetchChallenges}
        currentUserId={user?.id}
        initialChallengeId={openChallengeId}
        onInitialConsumed={() => setOpenChallengeId(null)}
      />

      {/* Deep-link post modal — shows a single post opened from a notification or share link */}
      <Dialog open={!!openPostId} onOpenChange={(v) => { if (!v) { setOpenPostId(null); setOpenPostData(null); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="deep-link-post-modal">
          <DialogHeader className="sr-only"><DialogTitle>Post</DialogTitle></DialogHeader>
          <div className="p-4">
            {openPostData ? (
              <PostCard post={openPostData} onLike={likePost} currentUserId={user?.id} onDelete={(id) => { if (id === openPostData.id) { setOpenPostId(null); setOpenPostData(null); } }} onEdit={handleEditPost} />
            ) : (
              <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Suggested Profiles Sidebar Component
const SuggestedProfiles = ({ currentUserId, onNavigateToProfile }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const sessionToken = Cookies.get('session_token');
        const headers = sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {};
        const resp = await axios.get(`${BACKEND_URL}/api/recommendations/profiles?limit=8`, { headers });
        setProfiles(resp.data);
      } catch (err) {
        console.error('Failed to load suggestions:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchSuggestions();
  }, [currentUserId]);

  if (loading) {
    return (
      <div className="sticky top-6" data-testid="suggested-profiles-loading">
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="h-5 w-36 bg-gray-200 rounded animate-pulse mb-4" />
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3 py-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3.5 w-24 bg-gray-200 rounded animate-pulse" />
                <div className="h-2.5 w-16 bg-gray-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (profiles.length === 0) return null;

  return (
    <div className="sticky top-6" data-testid="suggested-profiles-panel">
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-testid="suggested-profiles-title">
            Suggested for you
          </h3>
        </div>

        {/* Profile list */}
        <div className="divide-y divide-gray-50">
          {profiles.map((profile) => (
            <div
              key={profile.id}
              className="px-5 py-3 hover:bg-gray-50/80 transition-colors cursor-pointer group"
              data-testid={`suggested-profile-${profile.id}`}
              onClick={() => onNavigateToProfile(profile.id)}
            >
              <div className="flex items-center gap-3">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {profile.profile_picture ? (
                    <img
                      src={profile.profile_picture}
                      alt={profile.name}
                      className="w-10 h-10 rounded-full object-cover ring-2 ring-gray-100 group-hover:ring-blue-200 transition-all"
                      onError={handleImgError}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center ring-2 ring-gray-100 group-hover:ring-blue-200 transition-all">
                      <span className="text-white font-semibold text-sm">
                        {(profile.name || '?')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                  {profile.is_creator && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center ring-2 ring-white" title="Creator">
                      <Star className="h-2.5 w-2.5 text-white fill-white" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {profile.name}
                    </span>
                    {profile.payment_ready && (
                      <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                  {profile.bio ? (
                    <p className="text-xs text-gray-500 truncate mt-0.5">{profile.bio}</p>
                  ) : (
                    <div className="flex items-center gap-2 mt-0.5">
                      {profile.post_count > 0 && (
                        <span className="text-xs text-gray-400">{profile.post_count} post{profile.post_count !== 1 ? 's' : ''}</span>
                      )}
                      {profile.plan_count > 0 && (
                        <span className="text-xs text-gray-400">{profile.plan_count} plan{profile.plan_count !== 1 ? 's' : ''}</span>
                      )}
                      {profile.post_count === 0 && profile.plan_count === 0 && (
                        <span className="text-xs text-gray-400">{profile.is_creator ? 'Creator' : 'Member'}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* CTA */}
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-shrink-0 h-7 px-3 text-xs font-medium border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-all"
                  onClick={(e) => { e.stopPropagation(); onNavigateToProfile(profile.id); }}
                  data-testid={`view-profile-${profile.id}`}
                >
                  View
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50/50 border-t border-gray-100">
          <p className="text-[11px] text-gray-400 text-center">
            Based on activity and content
          </p>
        </div>
      </div>
    </div>
  );
};


// Post Card Component
// ── Save Post Button ────────────────────────────────────────────────────
// Bookmarks a post (or any item by id) using the existing bookmarks API.
// Shows the current state and toggles on click. Uses optimistic update.
const SavePostButton = ({ postId, productType = 'post' }) => {
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let aborted = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookmarks/status/${productType}/${postId}`);
        if (!aborted) setSaved(!!data?.saved);
      } catch (e) {
        // Anonymous users -> saved stays false. Don't toast on read errors.
      }
    })();
    return () => { aborted = true; };
  }, [postId, productType]);

  const handleToggle = async (e) => {
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    const optimistic = !saved;
    setSaved(optimistic);
    try {
      const endpoint = optimistic ? 'save' : 'remove';
      await axios.post(`${API}/bookmarks/${endpoint}`, { product_id: postId, product_type: productType });
      toast.success(optimistic ? 'Saved to Library' : 'Removed from Library');
    } catch (err) {
      setSaved(!optimistic);
      toast.error(err.response?.data?.detail || (optimistic ? 'Could not save' : 'Could not remove'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={handleToggle}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
        saved
          ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
          : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50'
      }`}
      data-testid={`post-save-btn-${postId}`}
      aria-label={saved ? 'Remove from Library' : 'Save to Library'}
    >
      <Bookmark className={`h-4 w-4 ${saved ? 'fill-amber-500' : ''}`} />
      <span>{saved ? 'Saved' : 'Save'}</span>
    </button>
  );
};


const PostCard = ({ post, onLike, currentUserId, onDelete, onEdit, onPostUpdate }) => {
  const navigate = useNavigate();
  const [mediaExpanded, setMediaExpanded] = useState(false);
  const [liked, setLiked] = useState(post.is_liked || false);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [commentsCount, setCommentsCount] = useState(post.comments_count || 0);
  const [loadingComments, setLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [showOwnerMenu, setShowOwnerMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  // Challenge linkage — if this post is a companion post for a challenge, load the challenge for the in-card CTA + detail modal
  const [linkedChallenge, setLinkedChallenge] = useState(null);
  const [showChallengeDetail, setShowChallengeDetail] = useState(false);
  const [challengeJoined, setChallengeJoined] = useState(false);
  const [challengeCurrentStreak, setChallengeCurrentStreak] = useState(0);
  useEffect(() => {
    if (!post.challenge_id) return;
    let aborted = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/challenges?creator_id=${post.creator_id}`);
        const ch = (data || []).find(c => c.id === post.challenge_id);
        if (!aborted && ch) setLinkedChallenge(ch);
        if (!aborted) {
          try {
            const s = await axios.get(`${API}/challenges/${post.challenge_id}/status`);
            setChallengeJoined(!!s.data?.joined);
            setChallengeCurrentStreak(Number(s.data?.current_streak || 0));
          } catch {}
        }
      } catch {}
    })();
    return () => { aborted = true; };
  }, [post.challenge_id, post.creator_id]);
  const handleJoinChallenge = async (e) => {
    e?.stopPropagation();
    if (!post.challenge_id) return;
    try {
      await axios.post(`${API}/challenges/${post.challenge_id}/join`);
      setChallengeJoined(true);
      toast.success('Joined challenge!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to join'); }
  };

  const isOwner = currentUserId && currentUserId === post.creator_id;
  const isAdmin = useIsAdmin();
  const canModerate = Boolean(isOwner || isAdmin);

  const handleProfileClick = () => {
    if (post.creator_id) navigate(`/profile/${post.creator_id}`);
  };

  const inferredMediaType = (() => {
    if (post.media_type) return post.media_type;
    if (post.content_type === 'video') return 'video';
    if (post.media_url) {
      const url = post.media_url.toLowerCase();
      if (url.match(/\.(mp4|mov|webm|avi)($|\?)/)) return 'video';
    }
    return 'image';
  })();

  const niches = post.fitness_niches || [];

  // Like handler with optimistic update
  const handleLike = async () => {
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikesCount(prev => wasLiked ? prev - 1 : prev + 1);
    try {
      const token = Cookies.get('session_token');
      if (!token) { toast.error('Please log in to like posts'); setLiked(wasLiked); setLikesCount(prev => wasLiked ? prev + 1 : prev - 1); return; }
      await axios.post(`${API}/posts/${post.id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      setLiked(wasLiked);
      setLikesCount(prev => wasLiked ? prev + 1 : prev - 1);
    }
  };

  // Comment handlers
  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const resp = await axios.get(`${API}/posts/${post.id}/comments`);
      setComments(resp.data);
    } catch { /* silent */ } finally { setLoadingComments(false); }
  };

  const handleToggleComments = () => {
    if (!showComments) fetchComments();
    setShowComments(!showComments);
  };

  const handlePostComment = async () => {
    if (!commentText.trim()) return;
    const token = Cookies.get('session_token');
    if (!token) { toast.error('Please log in to comment'); return; }
    setPostingComment(true);
    try {
      await axios.post(`${API}/posts/${post.id}/comments`, { content: commentText.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      setCommentText('');
      setCommentsCount(prev => prev + 1);
      fetchComments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to post comment'); }
    finally { setPostingComment(false); }
  };

  const handlePostReply = async (parentId) => {
    if (!replyText.trim()) return;
    const token = Cookies.get('session_token');
    if (!token) { toast.error('Please log in to reply'); return; }
    setPostingComment(true);
    try {
      await axios.post(`${API}/posts/${post.id}/comments`, { content: replyText.trim(), parent_comment_id: parentId }, { headers: { Authorization: `Bearer ${token}` } });
      setReplyText('');
      setReplyingTo(null);
      setCommentsCount(prev => prev + 1);
      fetchComments();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to post reply'); }
    finally { setPostingComment(false); }
  };

  // Share handler
  const handleShare = async () => {
    // Challenge posts share the specific post deep-link; regular posts share the creator profile
    const shareUrl = post.challenge_id
      ? `${window.location.origin}/dashboard?post=${post.id}`
      : `${window.location.origin}/profile/${post.creator_id}?post=${post.id}`;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, text: post.content?.substring(0, 100), url: shareUrl }); } catch { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied to clipboard!'); } catch { toast.error('Failed to copy link'); }
    }
  };

  // Owner / admin delete — the post is only removed from the UI once the
  // backend confirms the deletion, so a failure can never "hide then resurrect".
  const handleDelete = async () => {
    try {
      const token = Cookies.get('session_token');
      const { data } = await axios.delete(`${API}/posts/${post.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!data?.post_id && !data?.message) throw new Error('Unexpected delete response');
      toast.success(isOwner ? 'Post deleted' : 'Post deleted (admin)');
      if (onDelete) onDelete(post.id);
    } catch (err) {
      const detail = err.response?.data?.detail;
      console.error('[PostCard] delete failed', post.id, err.response?.status, detail || err.message);
      toast.error(detail || 'Failed to delete post. The post was not removed.');
    } finally { setShowDeleteConfirm(false); }
  };

  return (
    <Card className="border-gray-200/80 hover:shadow-lg transition-shadow" data-testid={`post-card-${post.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center space-x-3 min-w-0">
            <Avatar className="h-10 w-10 cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all shrink-0" onClick={handleProfileClick}>
              <AvatarImage src={resolveMediaUrl(post.creator_profile_picture)} />
              <AvatarFallback className="bg-blue-100 text-blue-600">{post.creator_name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="flex items-center space-x-2">
                <p className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600 transition-colors truncate" onClick={handleProfileClick}>
                  {post.creator_name}
                </p>
                {post.creator_id && (
                  <SubscriptionButton creatorId={post.creator_id} creatorName={post.creator_name} className="text-xs px-2 py-1 shrink-0" variant="outline" />
                )}
              </div>
              <p className="text-xs text-gray-500">{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 shrink-0">
            {post.post_type && post.post_type !== 'general' && <PostTypeBadge postType={post.post_type} size="sm" />}
            {post.is_premium && (
              <Badge data-testid={`post-access-badge-${post.id}`} className="bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700 border-amber-200 text-xs">
                <Lock className="h-3 w-3 mr-0.5" />Subs
              </Badge>
            )}
            {/* Owner menu — admins/founders can moderate any post */}
            {canModerate && (
              <div className="relative">
                <button
                  onClick={() => setShowOwnerMenu(!showOwnerMenu)}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                  data-testid={`post-owner-menu-${post.id}`}
                >
                  <MoreVertical className="h-4 w-4 text-gray-400" />
                </button>
                {showOwnerMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowOwnerMenu(false)} />
                    <div className="absolute right-0 top-8 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-36 max-h-[calc(100vh-6rem)] overflow-y-auto">
                      {isOwner && (
                        <button
                          onClick={() => { setShowOwnerMenu(false); if (onEdit) onEdit(post); }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                          data-testid={`post-edit-btn-${post.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" /> Edit Post
                        </button>
                      )}
                      <button
                        onClick={() => { setShowOwnerMenu(false); setShowDeleteConfirm(true); }}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                        data-testid={`post-delete-btn-${post.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> {isOwner ? 'Delete Post' : 'Delete (admin)'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="pt-0">
        <div className="space-y-3">
          {/* Title + Content */}
          <div>
            <h3 className="font-semibold text-base text-gray-900 mb-1">{post.title}</h3>
            {post.is_locked ? (
              <div>
                <p className="text-sm text-gray-500 leading-relaxed">{post.content_preview || post.content}</p>
                <div className="flex items-center gap-2 mt-2 p-2.5 rounded-lg bg-gray-50 border border-gray-200">
                  <Lock className="h-4 w-4 text-gray-400 shrink-0" />
                  <span className="text-xs text-gray-500">{post.lock_message || 'Subscribe to see full content'}</span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-700 leading-relaxed">{post.content}</p>
            )}
          </div>

          {/* Challenge CTA — shown when this post is a companion post for a challenge */}
          {post.challenge_id && linkedChallenge && (
            <div className="p-3 rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50" data-testid={`post-challenge-cta-${post.id}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Flame className="h-4 w-4 text-amber-600 shrink-0" />
                <span className="text-xs font-semibold text-amber-800">
                  {linkedChallenge.status === 'completed' ? 'Challenge Completed' : 'Active Challenge'}
                </span>
                <Badge variant="outline" className="text-[10px]">{linkedChallenge.participants_count || 0} joined</Badge>
                {/* Audience/eligibility requirement */}
                <Badge
                  className={`text-[10px] border ${{ everyone: 'bg-emerald-50 text-emerald-700 border-emerald-200', followers: 'bg-blue-50 text-blue-700 border-blue-200', subscribers: 'bg-purple-50 text-purple-700 border-purple-200' }[linkedChallenge.audience] || 'bg-gray-50 text-gray-700 border-gray-200'}`}
                  data-testid={`post-challenge-audience-${post.id}`}
                >
                  {{ everyone: 'Open to all', followers: 'Followers only', subscribers: 'Subscribers only' }[linkedChallenge.audience] || 'Open to all'}
                </Badge>
                {linkedChallenge.end_date && (
                  <span className="text-[10px] text-gray-500 ml-auto">Ends {linkedChallenge.end_date}</span>
                )}
              </div>
              {linkedChallenge.status === 'completed' && Array.isArray(linkedChallenge.winners) && linkedChallenge.winners.length > 0 && (
                <div className="mb-2 p-2 bg-white rounded-md border border-amber-100" data-testid={`challenge-completed-winners-${post.id}`}>
                  <p className="text-[10px] font-semibold text-amber-800 mb-1 flex items-center gap-1"><Trophy className="h-3 w-3" /> Winners</p>
                  <div className="space-y-0.5">
                    {linkedChallenge.winners.slice(0, 3).map(w => (
                      <p key={w.user_id} className="text-[11px] text-gray-700">
                        <span className="font-medium">#{w.rank} {w.user_name}</span>
                        {w.reward_label ? <span className="text-gray-500"> — {w.reward_label}</span> : null}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs h-8 bg-white" onClick={() => setShowChallengeDetail(true)} data-testid={`post-open-challenge-${post.id}`}>
                  <Trophy className="h-3 w-3 mr-1" />{linkedChallenge.status === 'completed' ? 'View results' : 'View leaderboard'}
                </Button>
                {linkedChallenge.status === 'active' && !challengeJoined && (
                  <Button size="sm" onClick={handleJoinChallenge} className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs h-8" data-testid={`post-join-challenge-${post.id}`}>
                    Join Challenge
                  </Button>
                )}
                {linkedChallenge.status === 'active' && challengeJoined && (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 h-8 px-2.5 flex items-center"><CheckCircle className="h-3 w-3 mr-1" />Joined</Badge>
                )}
                {linkedChallenge.status === 'active' && challengeJoined && challengeCurrentStreak > 0 && (
                  <Badge
                    className="bg-orange-50 text-orange-700 border-orange-200 h-8 px-2.5 flex items-center"
                    data-testid={`post-challenge-streak-${post.id}`}
                  >
                    <span className="mr-1" aria-hidden>🔥</span>{challengeCurrentStreak}-day streak
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Niche Tags */}
          {niches.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid={`post-niches-${post.id}`}>
              {niches.map(nid => <NicheBadge key={nid} nicheId={nid} size="xs" />)}
            </div>
          )}
          
          {/* Media */}
          {!post.is_locked && post.media_url && (
            <div className="rounded-lg overflow-hidden border border-gray-100">
              {inferredMediaType === 'video' ? (
                <video src={resolveMediaUrl(post.media_url)} poster={resolveMediaUrl(post.poster_url)} controls preload="metadata" className="w-full max-h-80 bg-black" data-testid={`post-video-${post.id}`} />
              ) : (
                <img src={resolveMediaUrl(post.media_url)} alt={post.title} className="w-full max-h-80 object-cover cursor-pointer hover:opacity-95 transition-opacity" onClick={() => setMediaExpanded(true)} data-testid={`post-image-${post.id}`} />
              )}
            </div>
          )}
          {post.is_locked && post.has_media && (
            <div className="rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center h-40 border border-gray-200">
              <div className="text-center text-gray-400"><Lock className="h-6 w-6 mx-auto mb-1" /><span className="text-xs">Media locked</span></div>
            </div>
          )}
          
          {/* Action Bar */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-3">
              {/* Like */}
              <button
                onClick={handleLike}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 ${
                  liked
                    ? 'text-red-500 bg-red-50 hover:bg-red-100'
                    : 'text-gray-500 hover:text-red-500 hover:bg-red-50'
                }`}
                data-testid={`post-like-btn-${post.id}`}
              >
                <Heart className={`h-4 w-4 transition-all duration-200 ${liked ? 'fill-red-500 text-red-500 scale-110' : ''}`} />
                <span>{likesCount}</span>
              </button>
              
              {/* Comment */}
              <button
                onClick={handleToggleComments}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                  showComments ? 'text-blue-500 bg-blue-50' : 'text-gray-500 hover:text-blue-500 hover:bg-blue-50'
                }`}
                data-testid={`post-comment-btn-${post.id}`}
              >
                <MessageCircle className={`h-4 w-4 ${showComments ? 'fill-blue-100' : ''}`} />
                <span>{commentsCount}</span>
              </button>
              
              {/* Share */}
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:text-green-500 hover:bg-green-50 transition-all"
                data-testid={`post-share-btn-${post.id}`}
              >
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </button>

              {/* Save / Bookmark — if this is a challenge companion post, classify the
                  save as product_type='challenge' against the real challenge id so the
                  item lands in Library → Saved Challenges (not Saved Posts). */}
              {post.challenge_id ? (
                <SavePostButton postId={post.challenge_id} productType="challenge" />
              ) : (
                <SavePostButton postId={post.id} />
              )}

              {/* Tip */}
              <TipButton post={post} creatorName={post.creator_name} variant="icon-only" />
            </div>
            <PostTipStats post={post} showDetails={true} />
          </div>

          {/* Comments Section */}
          {showComments && (
            <div className="pt-2 space-y-3" data-testid={`post-comments-section-${post.id}`}>
              {/* Top-level comment input */}
              <div className="flex items-center gap-2">
                <Input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Write a comment..."
                  className="flex-1 text-sm h-9"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handlePostComment()}
                  data-testid={`post-comment-input-${post.id}`}
                />
                <Button
                  size="sm"
                  onClick={handlePostComment}
                  disabled={postingComment || !commentText.trim()}
                  className="h-9 px-3 om-accent-bg text-white"
                  data-testid={`post-comment-send-${post.id}`}
                >
                  {postingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
              
              {/* Threaded comment list */}
              {loadingComments ? (
                <div className="text-center py-3"><Loader2 className="h-4 w-4 animate-spin mx-auto text-gray-400" /></div>
              ) : comments.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-2">No comments yet. Be the first!</p>
              ) : (
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {comments.map(c => (
                    <div key={c.id} data-testid={`comment-${c.id}`}>
                      {/* Parent comment */}
                      <div className="flex items-start gap-2">
                        <Avatar className="h-7 w-7 shrink-0 cursor-pointer" onClick={() => navigate(`/profile/${c.user_id}`)} data-testid={`comment-avatar-${c.id}`}>
                          <AvatarImage src={resolveMediaUrl(c.user_profile_picture)} />
                          <AvatarFallback className="bg-gray-100 text-gray-600 text-xs">{c.user_name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="bg-gray-50 rounded-lg px-3 py-2">
                            <p className="text-xs font-medium text-gray-900 cursor-pointer hover:underline" onClick={() => navigate(`/profile/${c.user_id}`)} data-testid={`comment-name-${c.id}`}>{c.user_name}</p>
                            <p className="text-xs text-gray-700 mt-0.5">{c.content}</p>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 ml-1">
                            <span className="text-[10px] text-gray-400">{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <button
                              onClick={() => { setReplyingTo(replyingTo === c.id ? null : c.id); setReplyText(''); }}
                              className="text-[11px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                              data-testid={`reply-btn-${c.id}`}
                            >
                              Reply
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Replies */}
                      {c.replies && c.replies.length > 0 && (
                        <div className="ml-9 mt-1.5 space-y-1.5 border-l-2 border-gray-100 pl-3">
                          {c.replies.map(r => (
                            <div key={r.id} className="flex items-start gap-2" data-testid={`reply-${r.id}`}>
                              <Avatar className="h-6 w-6 shrink-0 cursor-pointer" onClick={() => navigate(`/profile/${r.user_id}`)} data-testid={`reply-avatar-${r.id}`}>
                                <AvatarImage src={resolveMediaUrl(r.user_profile_picture)} />
                                <AvatarFallback className="bg-gray-100 text-gray-500 text-[10px]">{r.user_name?.charAt(0)}</AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="bg-gray-50 rounded-lg px-2.5 py-1.5">
                                  <p className="text-[11px] font-medium text-gray-900 cursor-pointer hover:underline" onClick={() => navigate(`/profile/${r.user_id}`)} data-testid={`reply-name-${r.id}`}>{r.user_name}</p>
                                  <p className="text-[11px] text-gray-700 mt-0.5">{r.content}</p>
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 ml-1">
                                  <span className="text-[10px] text-gray-400">{new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                  <button
                                    onClick={() => { setReplyingTo(replyingTo === c.id ? null : c.id); setReplyText(''); }}
                                    className="text-[10px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
                                    data-testid={`reply-to-reply-btn-${r.id}`}
                                  >
                                    Reply
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Inline reply input */}
                      {replyingTo === c.id && (
                        <div className="ml-9 mt-2 flex items-center gap-2" data-testid={`reply-input-${c.id}`}>
                          <Input
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder={`Reply to ${c.user_name}...`}
                            className="flex-1 text-xs h-8"
                            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handlePostReply(c.id)}
                            autoFocus
                          />
                          <Button
                            size="sm"
                            onClick={() => handlePostReply(c.id)}
                            disabled={postingComment || !replyText.trim()}
                            className="h-8 px-2.5 om-accent-bg text-white text-xs"
                          >
                            <Send className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { setReplyingTo(null); setReplyText(''); }}
                            className="h-8 px-2 text-xs text-gray-400"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Photo Lightbox */}
        {mediaExpanded && post.media_url && inferredMediaType === 'image' && (
          <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4" onClick={() => setMediaExpanded(false)} data-testid={`post-lightbox-${post.id}`}>
            <button onClick={() => setMediaExpanded(false)} className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 rounded-full p-2">
              <X className="h-6 w-6" />
            </button>
            <img src={resolveMediaUrl(post.media_url)} alt={post.title} className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this post?</AlertDialogTitle>
              <AlertDialogDescription>
                <strong>{post.title}</strong> will be removed from the feed, profiles and search. This cannot be undone from the app.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid={`post-confirm-delete-${post.id}`}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Linked Challenge Detail Modal — opens from the challenge CTA inside the companion post */}
        {post.challenge_id && linkedChallenge && (
          <ChallengeDetailModal
            challenge={linkedChallenge}
            open={showChallengeDetail}
            onClose={() => setShowChallengeDetail(false)}
            joined={challengeJoined}
            onJoined={() => setChallengeJoined(true)}
            currentUserId={currentUserId}
          />
        )}
      </CardContent>
    </Card>
  );
};


// Edit Post Dialog Component
const EditPostDialog = ({ post, editData, setEditData, onSave, onClose, onMediaUpload, mediaUploading }) => {
  if (!post) return null;
  return (
    <Dialog open={!!post} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Post</DialogTitle>
          <DialogDescription>Update your post details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <Input value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })} placeholder="Post title" data-testid="edit-post-title" />
          <Textarea value={editData.content} onChange={(e) => setEditData({ ...editData, content: e.target.value })} placeholder="Post content..." rows={3} data-testid="edit-post-content" />
          <PostTypeSelector value={editData.post_type} onChange={(value) => setEditData({ ...editData, post_type: value })} />
          <NicheSelector selected={editData.fitness_niches || []} onChange={(niches) => setEditData({ ...editData, fitness_niches: niches })} max={3} />
          
          {/* Media */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Photo or Video</label>
            {editData.media_url ? (
              <div className="relative rounded-lg overflow-hidden border border-gray-200">
                {editData.media_type === 'video' ? (
                  <video src={resolveMediaUrl(editData.media_url)} poster={resolveMediaUrl(editData.poster_url)} controls preload="metadata" className="w-full max-h-40 bg-black rounded" />
                ) : (
                  <img src={resolveMediaUrl(editData.media_url)} alt="Preview" className="w-full max-h-40 object-cover" />
                )}
                <button onClick={() => setEditData({ ...editData, media_url: '', media_type: '', content_type: 'text' })} className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input type="file" accept="image/*,video/*" onChange={onMediaUpload} className="hidden" id="edit-post-media-upload" data-testid="edit-post-media-upload-input" />
                <label htmlFor="edit-post-media-upload" className={`flex items-center justify-center gap-2 w-full py-3 px-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors text-sm text-gray-600 ${mediaUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {mediaUploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> Upload photo or video</>}
                </label>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Access Type</label>
            <Select value={editData.access_type} onValueChange={(value) => setEditData({ ...editData, access_type: value })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public</SelectItem>
                <SelectItem value="subscription">Subscribers Only</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={onSave} className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white" data-testid="save-edit-post-btn">Save Changes</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Media Lightbox Component
const MediaLightbox = ({ images, initialIndex = 0, open, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoomed, setZoomed] = useState(false);
  const [animatingIn, setAnimatingIn] = useState(false);

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
      setZoomed(false);
      requestAnimationFrame(() => setAnimatingIn(true));
    } else {
      setAnimatingIn(false);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') { setAnimatingIn(false); setTimeout(onClose, 200); }
      if (e.key === 'ArrowRight' && images.length > 1) { setZoomed(false); setCurrentIndex(i => (i + 1) % images.length); }
      if (e.key === 'ArrowLeft' && images.length > 1) { setZoomed(false); setCurrentIndex(i => (i - 1 + images.length) % images.length); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, images.length, onClose]);

  if (!open || !images.length) return null;

  const src = images[currentIndex];
  const fullSrc = resolveMediaUrl(src);

  const handleClose = () => { setAnimatingIn(false); setTimeout(onClose, 200); };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center bg-black/95 transition-opacity duration-200 ${animatingIn ? 'opacity-100' : 'opacity-0'}`}
      onClick={handleClose}
      data-testid="media-lightbox"
    >
      {/* Top bar: counter + close */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-5 py-4 z-20" onClick={(e) => e.stopPropagation()}>
        {images.length > 1 ? (
          <span className="text-white/80 text-sm font-medium tracking-wide" data-testid="lightbox-counter">
            {currentIndex + 1} / {images.length}
          </span>
        ) : <span />}
        <button
          onClick={handleClose}
          className="text-white/70 hover:text-white transition-colors rounded-full hover:bg-white/10 p-2"
          data-testid="lightbox-close"
        >
          <X className="h-7 w-7" />
        </button>
      </div>

      {/* Navigation arrows */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setZoomed(false); setCurrentIndex(i => (i - 1 + images.length) % images.length); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white bg-black/30 hover:bg-black/60 rounded-full p-2.5 z-10 transition-all"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setZoomed(false); setCurrentIndex(i => (i + 1) % images.length); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white bg-black/30 hover:bg-black/60 rounded-full p-2.5 z-10 transition-all"
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-8 w-8" />
          </button>
        </>
      )}

      {/* Main image */}
      <div
        className={`flex items-center justify-center transition-transform duration-300 ease-out ${zoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`}
        onClick={(e) => { e.stopPropagation(); setZoomed(z => !z); }}
        style={{ maxWidth: '92vw', maxHeight: '88vh' }}
      >
        <img
          src={fullSrc}
          alt={`Photo ${currentIndex + 1}`}
          className={`transition-transform duration-300 ease-out rounded select-none ${zoomed ? 'scale-150' : 'scale-100'}`}
          style={{
            maxWidth: zoomed ? 'none' : '92vw',
            maxHeight: zoomed ? 'none' : '85vh',
            objectFit: 'contain',
            imageRendering: 'auto',
          }}
          draggable={false}
          data-testid="lightbox-image"
        />
      </div>

      {/* Dot indicators */}
      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex space-x-2.5 bg-black/40 rounded-full px-4 py-2" onClick={(e) => e.stopPropagation()}>
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => { setZoomed(false); setCurrentIndex(i); }}
              className={`rounded-full transition-all duration-200 ${i === currentIndex ? 'w-3 h-3 bg-white' : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};


// User Profile Page Component
const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, setUser, refreshUser: refreshCurrentUser } = useAuth();
  
  const [profileUser, setProfileUser] = useState(null);
  const [posts, setPosts] = useState([]);
  const [workoutPlans, setWorkoutPlans] = useState([]);
  const [mealPlans, setMealPlans] = useState([]);
  const [profileChallenges, setProfileChallenges] = useState([]);
  const [liveStreams, setLiveStreams] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('posts');
  const location = useLocation();
  // Honor ?tab=<name> deep-link when navigating from analytics cards
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && ['posts', 'photos', 'workouts', 'meals', 'challenges', 'live'].includes(t)) {
      setActiveTab(t);
    }
  }, [location.search]);
  const [postTypeFilter, setPostTypeFilter] = useState(null);
  const [followingCount, setFollowingCount] = useState(0);
  const [showSubscriptionEditor, setShowSubscriptionEditor] = useState(false);
  const [viewWorkoutPlan, setViewWorkoutPlan] = useState(null);
  const [viewMealPlan, setViewMealPlan] = useState(null);
  const [subscriptionSettings, setSubscriptionSettings] = useState({
    subscription_price: '',
    subscription_tier_name: '',
    subscription_benefits: ['']
  });
  const [showBioEditor, setShowBioEditor] = useState(false);
  const [bioText, setBioText] = useState('');
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  const [editingLinks, setEditingLinks] = useState(false);
  const [linksDraft, setLinksDraft] = useState({ website: '', instagram: '', youtube: '', tiktok: '' });
  const [editingMerch, setEditingMerch] = useState(false);
  const [merchDraft, setMerchDraft] = useState([{ title: '', url: '' }]);
  const [deletingPlan, setDeletingPlan] = useState(null); // {type: 'workout'|'meal', id, title}
  const [editingWorkoutPlan, setEditingWorkoutPlan] = useState(null);
  const [editingMealPlan, setEditingMealPlan] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostData, setEditPostData] = useState({});
  const [postMediaUploading, setPostMediaUploading] = useState(false);

  const isOwnProfile = currentUser && currentUser.id === userId;

  // Profile-scope like handler
  const likePost = async (postId) => {
    try {
      const token = Cookies.get('session_token');
      if (!token) return;
      await axios.post(`${BACKEND_URL}/api/posts/${postId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch { /* handled in PostCard optimistic update */ }
  };

  // Profile-scope edit handler
  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditPostData({
      title: post.title || '',
      content: post.content || '',
      post_type: post.post_type || 'general',
      fitness_niches: post.fitness_niches || [],
      media_url: post.media_url || '',
      media_type: post.media_type || '',
      poster_url: post.poster_url || null,
      content_type: post.content_type || 'text',
      access_type: post.access_type || 'public',
    });
  };

  const handleSaveEditPost = async () => {
    if (!editingPost) return;
    try {
      const token = Cookies.get('session_token');
      await axios.put(`${BACKEND_URL}/api/posts/${editingPost.id}`, editPostData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post updated');
      setEditingPost(null);
      // Refetch posts
      const postsResp = await axios.get(`${BACKEND_URL}/api/posts`, { params: { creator_id: userId } });
      setPosts(postsResp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleEditPostMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isVideo = file.type.startsWith('video/');
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File too large. Max ${isVideo ? '50' : '10'}MB.`);
      return;
    }
    setPostMediaUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const resp = await axios.post(`${BACKEND_URL}/api/upload/media?category=post`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      let posterUrl = null;
      if (isVideo) {
        const frameBlob = await extractVideoFrame(file);
        posterUrl = await uploadPosterBlob(frameBlob);
      }
      setEditPostData(prev => ({
        ...prev,
        media_url: resp.data.url,
        media_type: isVideo ? 'video' : 'image',
        content_type: isVideo ? 'video' : 'photo',
        poster_url: posterUrl || prev.poster_url || null
      }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setPostMediaUploading(false);
    }
  };

  const openLightbox = (images, index = 0) => setLightbox({ open: true, images, index });
  const closeLightbox = () => setLightbox({ open: false, images: [], index: 0 });
  
  // Filter posts by type
  const filteredPosts = postTypeFilter 
    ? posts.filter(p => p.post_type === postTypeFilter)
    : posts;

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile
      const userResponse = await axios.get(`${BACKEND_URL}/api/users/${userId}`);
      setProfileUser(userResponse.data);
      
      // Initialize subscription settings if it's the current user's profile
      if (currentUser && currentUser.id === userId) {
        setSubscriptionSettings({
          subscription_price:
            userResponse.data.subscription_price === null || userResponse.data.subscription_price === undefined
              ? ''
              : String(userResponse.data.subscription_price),
          subscription_tier_name: userResponse.data.subscription_tier_name || '',
          subscription_benefits: userResponse.data.subscription_benefits?.length > 0 ? 
            userResponse.data.subscription_benefits : ['']
        });
        setBioText(userResponse.data.bio || '');
      }
      
      // Fetch user's posts
      const postsResponse = await axios.get(`${BACKEND_URL}/api/posts?creator_id=${userId}`);
      setPosts(postsResponse.data);
      
      // Fetch user's workout plans
      const workoutResponse = await axios.get(`${BACKEND_URL}/api/workout-plans?creator_id=${userId}`);
      setWorkoutPlans(workoutResponse.data);
      
      // Fetch user's meal plans
      const mealResponse = await axios.get(`${BACKEND_URL}/api/meal-plans?creator_id=${userId}`);
      setMealPlans(mealResponse.data);
      
      // Fetch user's live streams
      const streamsResponse = await axios.get(`${BACKEND_URL}/api/streams?creator_id=${userId}`);
      setLiveStreams(streamsResponse.data);

      // Fetch user's challenges — SINGLE source of truth for both the tab label count and the tab list render
      try {
        const challengesResponse = await axios.get(`${BACKEND_URL}/api/challenges?creator_id=${userId}`);
        setProfileChallenges(challengesResponse.data || []);
      } catch (e) {
        setProfileChallenges([]);
      }
      
      // Check subscription status if logged in
      if (currentUser) {
        try {
          const subResponse = await axios.get(`${BACKEND_URL}/api/user/is-subscribed/${userId}`);
          setIsSubscribed(subResponse.data.is_subscribed);
          
          // Check following status
          const followResponse = await axios.get(`${BACKEND_URL}/api/users/${userId}/following-status`);
          setIsFollowing(followResponse.data.following);
        } catch (error) {
          console.log('Subscription/Follow check failed:', error);
        }
      }
      
    } catch (error) {
      console.error('Failed to fetch profile data:', error);
      toast.error('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchProfileData();
    }
  }, [userId, currentUser]);

  const handleFollowToggle = async () => {
    if (!currentUser) {
      toast.error('Please login to follow users');
      return;
    }
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/${userId}/follow`);
      
      if (response.data.following !== undefined) {
        setIsFollowing(response.data.following);
        if (response.data.following) {
          setProfileUser(prev => ({...prev, followers_count: prev.followers_count + 1}));
          toast.success(`Now following ${profileUser?.name}`);
        } else {
          setProfileUser(prev => ({...prev, followers_count: prev.followers_count - 1}));
          toast.success(`Unfollowed ${profileUser?.name}`);
        }
      }
    } catch (error) {
      console.error('Follow toggle failed:', error);
      toast.error('Failed to update follow status');
    }
  };

  const handleDeletePlan = async () => {
    if (!deletingPlan) return;
    try {
      const token = Cookies.get('session_token');
      const endpoint = deletingPlan.type === 'workout' ? 'workout-plans' : 'meal-plans';
      await axios.delete(`${BACKEND_URL}/api/${endpoint}/${deletingPlan.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (deletingPlan.type === 'workout') {
        setWorkoutPlans(prev => prev.filter(p => p.id !== deletingPlan.id));
      } else {
        setMealPlans(prev => prev.filter(p => p.id !== deletingPlan.id));
      }
      toast.success(`${deletingPlan.title} deleted`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeletingPlan(null);
    }
  };

  const checkContentAccess = (item) => {
    // Free content or user is subscribed
    if (!item.price || item.price === 0 || isSubscribed) {
      return true;
    }
    return false;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">User not found</p>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0b1120]">
      {/* Header */}
      <div className="bg-white dark:bg-[#111a2e] border-b border-gray-200 dark:border-[#1e2d4a] sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2"
            >
              <ChevronRight className="h-4 w-4 rotate-180" />
              <span>Back to Dashboard</span>
            </Button>
            <div className="flex items-center space-x-2">
              <Dumbbell className="h-6 w-6 text-blue-600" />
              <span className="font-bold text-xl bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                OnlyMass
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cover Photo + Profile Section */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Cover Photo */}
        {profileUser.cover_photo && (
          <div 
            className="rounded-t-xl overflow-hidden h-56 md:h-72 lg:h-80 mb-0 cursor-pointer group relative" 
            data-testid="profile-cover-photo"
            onClick={() => openLightbox([profileUser.cover_photo])}
          >
            <img 
              src={resolveMediaUrl(profileUser.cover_photo)}
              alt="Cover" 
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              style={{ imageRendering: 'auto' }}
              onError={(e) => handleImgError(e, true)}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
              <div className="bg-black/50 rounded-full p-3">
                <Eye className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        )}

        <div className={`bg-white ${profileUser.cover_photo ? 'rounded-b-xl' : 'rounded-xl'} shadow-sm border border-gray-200 p-8 mb-6`}>
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            
            {/* Profile Picture */}
            <div className={`relative group ${profileUser.profile_picture ? 'cursor-pointer' : ''}`} onClick={() => profileUser.profile_picture && openLightbox([profileUser.profile_picture])}>
              <Avatar 
                className={`h-32 w-32 lg:h-36 lg:w-36 ${profileUser.cover_photo ? '-mt-20 border-4 border-white shadow-lg' : ''}`} 
                data-testid="profile-avatar"
              >
                <AvatarImage 
                  src={resolveMediaUrl(profileUser.profile_picture)}
                  style={{ imageRendering: 'auto', objectFit: 'cover' }}
                />
                <AvatarFallback className="bg-blue-100 text-blue-600 text-4xl">
                  {profileUser.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              {profileUser.profile_picture && (
                <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <Eye className="h-5 w-5 text-white drop-shadow" />
                </div>
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{profileUser.name}</h1>
                  {profileUser.is_creator && (
                    <Badge className="bg-gradient-to-r from-blue-500 to-purple-600 text-white mb-2">
                      <Star className="h-3 w-3 mr-1" />
                      Creator
                    </Badge>
                  )}
                </div>
                
                {/* Action Buttons */}
                {currentUser && currentUser.id !== userId && (
                  <div className="flex items-center space-x-3">
                    <Button onClick={handleFollowToggle} variant="outline">
                      <UserPlus className="h-4 w-4 mr-2" />
                      {isFollowing ? 'Unfollow' : 'Follow'}
                    </Button>
                    {profileUser.is_creator && (
                      <SubscriptionButton 
                        creatorId={userId}
                        creatorName={profileUser.name}
                        className="px-6 py-2"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex flex-wrap justify-center md:justify-start gap-6 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{posts.length}</p>
                  <p className="text-sm text-gray-600">Posts</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{profileUser.followers_count}</p>
                  <p className="text-sm text-gray-600">Followers</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{profileUser.following_count}</p>
                  <p className="text-sm text-gray-600">Following</p>
                </div>
                {profileUser.is_creator && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-900">{workoutPlans.length + mealPlans.length + liveStreams.length}</p>
                    <p className="text-sm text-gray-600">Content</p>
                  </div>
                )}
              </div>

              {/* Bio */}
              <div className="max-w-2xl">
                {currentUser && currentUser.id === userId ? (
                  /* Editable Bio for Own Profile */
                  <div className="relative group">
                    {profileUser.bio ? (
                      <div 
                        className="text-gray-700 leading-relaxed cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors border border-transparent hover:border-gray-200"
                        onClick={() => setShowBioEditor(true)}
                      >
                        <p>{profileUser.bio}</p>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity text-xs text-gray-500 mt-2 flex items-center">
                          <Edit className="h-3 w-3 mr-1" />
                          Click to edit bio
                        </div>
                      </div>
                    ) : (
                      <div 
                        className="text-gray-500 italic cursor-pointer hover:bg-gray-50 p-3 rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors"
                        onClick={() => setShowBioEditor(true)}
                      >
                        <p>Add a bio to tell people about yourself...</p>
                        <div className="text-xs text-gray-400 mt-2 flex items-center">
                          <Plus className="h-3 w-3 mr-1" />
                          Click to add bio
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Read-only Bio for Others */
                  profileUser.bio && (
                    <p className="text-gray-700 leading-relaxed">{profileUser.bio}</p>
                  )
                )}
              </div>

              {/* Subscription Price */}
              {profileUser.is_creator &&
                profileUser.subscription_price !== null &&
                profileUser.subscription_price !== undefined && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg inline-block" data-testid="profile-subscription-price">
                  <p className="text-sm text-gray-600">{profileUser.subscription_tier_name || 'Monthly Subscription'}</p>
                  {Number(profileUser.subscription_price) === 0 ? (
                    <p className="text-2xl font-bold text-green-600">Free</p>
                  ) : (
                    <p className="text-2xl font-bold text-blue-600">${profileUser.subscription_price}/month</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Subscription Management Section - Only for Own Profile */}
        {currentUser && currentUser.id === userId && profileUser.is_creator && (
          <div className="max-w-6xl mx-auto px-4 mb-6">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl shadow-sm border border-purple-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Settings className="h-6 w-6 mr-2 text-purple-600" />
                  Manage Your Subscription
                </h3>
                <Button 
                  onClick={() => setShowSubscriptionEditor(true)}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Settings
                </Button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Current Settings */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-green-600" />
                    Current Pricing
                  </h4>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{profileUser.subscription_tier_name || 'Standard Tier'}</p>
                        <p className="text-sm text-gray-600">Monthly subscription</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">${profileUser.subscription_price || 0}</p>
                        <p className="text-xs text-gray-500">per month</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Subscriber Benefits Preview */}
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Star className="h-5 w-5 mr-2 text-yellow-600" />
                    Subscriber Benefits
                  </h4>
                  <div className="bg-white rounded-lg p-4 border border-gray-200 max-h-32 overflow-y-auto">
                    {profileUser.subscription_benefits && profileUser.subscription_benefits.length > 0 ? (
                      <ul className="space-y-1">
                        {profileUser.subscription_benefits.slice(0, 3).map((benefit, index) => (
                          <li key={index} className="flex items-center text-sm">
                            <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                        {profileUser.subscription_benefits.length > 3 && (
                          <li className="text-sm text-gray-500 italic">
                            +{profileUser.subscription_benefits.length - 3} more benefits...
                          </li>
                        )}
                      </ul>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No benefits configured yet</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="mt-4 p-4 bg-white/50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Estimated Monthly Revenue</span>
                  <span className="font-semibold text-purple-600">
                    ${((profileUser.subscription_price || 0) * Math.max(1, Math.floor(profileUser.followers_count * 0.05))).toFixed(0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Benefits Display - For Other Users Viewing Profile */}
        {profileUser.is_creator && profileUser.subscription_benefits && profileUser.subscription_benefits.length > 0 && currentUser && currentUser.id !== userId && (
          <div className="max-w-6xl mx-auto px-4 mb-6">
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl shadow-sm border border-yellow-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Star className="h-5 w-5 mr-2 text-yellow-600" />
                  {profileUser.subscription_tier_name || 'Subscription'} Benefits
                </h3>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-600">${profileUser.subscription_price}</p>
                  <p className="text-sm text-gray-600">per month</p>
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                {profileUser.subscription_benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-yellow-200">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 font-medium">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 pt-4 border-t border-yellow-200">
                <SubscriptionButton 
                  creatorId={userId}
                  creatorName={profileUser.name}
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-3"
                />
              </div>
            </div>
          </div>
        )}

        {/* Personal Links & Merchandise Section */}
        {profileUser.is_creator && (
          <div className="max-w-6xl mx-auto px-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="grid md:grid-cols-2 gap-6">
                
                {/* Connect With Me */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Link className="h-5 w-5 mr-2 text-blue-600" />
                      Connect With Me
                    </h3>
                    {isOwnProfile && !editingLinks && (
                      <Button variant="ghost" size="sm" onClick={() => { setEditingLinks(true); setLinksDraft({ website: profileUser.website || '', instagram: profileUser.instagram || '', youtube: profileUser.youtube || '', tiktok: profileUser.tiktok || '' }); }} data-testid="edit-links-btn">
                        <Edit className="h-4 w-4 mr-1" />Edit
                      </Button>
                    )}
                  </div>

                  {editingLinks ? (
                    <div className="space-y-3">
                      <div className="space-y-1"><label className="text-xs font-medium text-gray-500">Website URL</label><Input placeholder="https://mysite.com" value={linksDraft.website} onChange={(e) => setLinksDraft({...linksDraft, website: e.target.value})} data-testid="edit-website-input" /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-gray-500">Instagram handle</label><Input placeholder="myhandle" value={linksDraft.instagram} onChange={(e) => setLinksDraft({...linksDraft, instagram: e.target.value})} data-testid="edit-instagram-input" /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-gray-500">YouTube handle</label><Input placeholder="mychannel" value={linksDraft.youtube} onChange={(e) => setLinksDraft({...linksDraft, youtube: e.target.value})} data-testid="edit-youtube-input" /></div>
                      <div className="space-y-1"><label className="text-xs font-medium text-gray-500">TikTok handle</label><Input placeholder="myhandle" value={linksDraft.tiktok} onChange={(e) => setLinksDraft({...linksDraft, tiktok: e.target.value})} data-testid="edit-tiktok-input" /></div>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={async () => { try { const token = Cookies.get('session_token'); await axios.put(`${API}/users/profile`, linksDraft, { headers: { Authorization: `Bearer ${token}` } }); setProfileUser(prev => ({...prev, ...linksDraft})); setEditingLinks(false); toast.success('Links updated'); } catch { toast.error('Failed to save'); } }} data-testid="save-links-btn">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingLinks(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {profileUser.website && (
                        <a href={profileUser.website.startsWith('http') ? profileUser.website : `https://${profileUser.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group" data-testid="link-website">
                          <Globe className="h-5 w-5 text-gray-600 group-hover:text-blue-600" /><div><p className="font-medium text-gray-900">Website</p><p className="text-sm text-blue-600 truncate">{profileUser.website}</p></div>
                        </a>
                      )}
                      {profileUser.instagram && (
                        <a href={`https://instagram.com/${profileUser.instagram}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-colors group" data-testid="link-instagram">
                          <Camera className="h-5 w-5 text-gray-600 group-hover:text-pink-600" /><div><p className="font-medium text-gray-900">Instagram</p><p className="text-sm text-pink-600">@{profileUser.instagram}</p></div>
                        </a>
                      )}
                      {profileUser.youtube && (
                        <a href={`https://youtube.com/@${profileUser.youtube}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors group" data-testid="link-youtube">
                          <PlayCircle className="h-5 w-5 text-gray-600 group-hover:text-red-600" /><div><p className="font-medium text-gray-900">YouTube</p><p className="text-sm text-red-600">@{profileUser.youtube}</p></div>
                        </a>
                      )}
                      {profileUser.tiktok && (
                        <a href={`https://tiktok.com/@${profileUser.tiktok}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors group" data-testid="link-tiktok">
                          <Video className="h-5 w-5 text-gray-600 group-hover:text-purple-600" /><div><p className="font-medium text-gray-900">TikTok</p><p className="text-sm text-purple-600">@{profileUser.tiktok}</p></div>
                        </a>
                      )}
                      {!profileUser.website && !profileUser.instagram && !profileUser.youtube && !profileUser.tiktok && (
                        isOwnProfile
                          ? <p className="text-sm text-gray-400 italic py-4 text-center">Click Edit to add your social links</p>
                          : <p className="text-sm text-gray-400 italic py-4 text-center">No links shared yet</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Merchandise Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <ShoppingCart className="h-5 w-5 mr-2 text-green-600" />
                      Official Merchandise
                    </h3>
                    {isOwnProfile && !editingMerch && (
                      <Button variant="ghost" size="sm" onClick={() => { setEditingMerch(true); setMerchDraft([...(profileUser.merch_items?.length ? profileUser.merch_items : [{ title: '', url: '' }])]); }} data-testid="edit-merch-btn">
                        <Edit className="h-4 w-4 mr-1" />Edit
                      </Button>
                    )}
                  </div>

                  {editingMerch ? (
                    <div className="space-y-3">
                      {merchDraft.map((item, i) => (
                        <div key={i} className="flex gap-2 items-start">
                          <div className="flex-1 space-y-1">
                            <Input placeholder="Product name" value={item.title} onChange={(e) => { const d = [...merchDraft]; d[i] = {...d[i], title: e.target.value}; setMerchDraft(d); }} data-testid={`merch-title-${i}`} />
                            <Input placeholder="https://store.com/product" value={item.url} onChange={(e) => { const d = [...merchDraft]; d[i] = {...d[i], url: e.target.value}; setMerchDraft(d); }} data-testid={`merch-url-${i}`} />
                          </div>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 mt-1" onClick={() => setMerchDraft(merchDraft.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => setMerchDraft([...merchDraft, { title: '', url: '' }])} data-testid="add-merch-btn"><Plus className="h-4 w-4 mr-1" />Add Item</Button>
                      <div className="flex gap-2 pt-2">
                        <Button size="sm" onClick={async () => { try { const token = Cookies.get('session_token'); const cleaned = merchDraft.filter(m => m.title.trim() && m.url.trim()); await axios.put(`${API}/users/profile`, { merch_items: cleaned }, { headers: { Authorization: `Bearer ${token}` } }); setProfileUser(prev => ({...prev, merch_items: cleaned})); setEditingMerch(false); toast.success('Merchandise updated'); } catch { toast.error('Failed to save'); } }} data-testid="save-merch-btn">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingMerch(false)}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(profileUser.merch_items || []).filter(m => m.title && m.url).map((item, i) => (
                        <a key={i} href={item.url.startsWith('http') ? item.url : `https://${item.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors group" data-testid={`merch-link-${i}`}>
                          <ShoppingCart className="h-5 w-5 text-gray-600 group-hover:text-green-600" />
                          <div><p className="font-medium text-gray-900 group-hover:text-green-700">{item.title}</p><p className="text-sm text-green-600 truncate">{item.url}</p></div>
                          <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-green-600 ml-auto shrink-0" />
                        </a>
                      ))}
                      {(!(profileUser.merch_items || []).some(m => m.title && m.url)) && (
                        isOwnProfile
                          ? <p className="text-sm text-gray-400 italic py-4 text-center">Click Edit to add merchandise links</p>
                          : <p className="text-sm text-gray-400 italic py-4 text-center">No merchandise available yet</p>
                      )}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        )}

        {/* Badges Section */}
        <ProfileBadgeSection userId={userId} />

        {/* Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-5 lg:w-auto">
            <TabsTrigger value="posts" className="flex items-center space-x-2">
              <BookOpen className="h-4 w-4" />
              <span>Posts ({posts.length})</span>
            </TabsTrigger>
            <TabsTrigger value="photos" className="flex items-center space-x-2">
              <Image className="h-4 w-4" />
              <span>Photos{profileUser.featured_photos?.length > 0 ? ` (${profileUser.featured_photos.length})` : ''}</span>
            </TabsTrigger>
            <TabsTrigger value="workouts" className="flex items-center space-x-2">
              <Dumbbell className="h-4 w-4" />
              <span>Workouts ({workoutPlans.length})</span>
            </TabsTrigger>
            <TabsTrigger value="meals" className="flex items-center space-x-2">
              <Apple className="h-4 w-4" />
              <span>Meals ({mealPlans.length})</span>
            </TabsTrigger>
            <TabsTrigger value="challenges" className="flex items-center space-x-2">
              <Flame className="h-4 w-4" />
              <span>Challenges ({profileChallenges.length})</span>
            </TabsTrigger>
            <TabsTrigger value="streams" className="flex items-center space-x-2">
              <PlayCircle className="h-4 w-4" />
              <span>Live ({liveStreams.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Posts Tab */}
          <TabsContent value="posts" className="space-y-6 mt-6">
            {/* Post Type Filters */}
            {posts.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <PostTypeFilters 
                  activeFilter={postTypeFilter} 
                  onFilterChange={setPostTypeFilter}
                />
              </div>
            )}
            
            {filteredPosts.length > 0 ? (
              <div className="grid gap-6">
                {filteredPosts.map((post) => (
                  <PostCard 
                    key={post.id} 
                    post={post} 
                    onLike={likePost}
                    currentUserId={currentUser?.id}
                    onDelete={(postId) => setPosts(prev => prev.filter(p => p.id !== postId))}
                    onEdit={handleEditPost}
                  />
                ))}
              </div>
            ) : posts.length > 0 ? (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No {postTypeFilter ? POST_TYPES.find(t => t.value === postTypeFilter)?.label?.toLowerCase() : ''} posts yet</p>
                <Button 
                  variant="outline" 
                  onClick={() => setPostTypeFilter(null)}
                  className="mt-4"
                >
                  Show all posts
                </Button>
              </div>
            ) : (
              <div className="text-center py-12">
                <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No posts yet</p>
              </div>
            )}
          </TabsContent>

          {/* Photos & Videos Tab */}
          <TabsContent value="photos" className="space-y-6 mt-6" data-testid="photos-tab-content">
            {/* Owner: Add Media Button */}
            {isOwnProfile && (
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Featured Media</h3>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm';
                    input.onchange = async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const isVideo = file.type.startsWith('video/');
                      const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
                      if (file.size > maxSize) { toast.error(`Max ${isVideo ? '50' : '10'}MB`); return; }
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const uploadCat = isVideo ? 'post' : 'featured';
                        const resp = await axios.post(`${API}/upload/media?category=${uploadCat}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                        const url = resp.data.url;
                        const current = profileUser.featured_photos || [];
                        const posters = { ...(profileUser.featured_posters || {}) };
                        if (isVideo) {
                          const frameBlob = await extractVideoFrame(file);
                          const posterUrl = await uploadPosterBlob(frameBlob);
                          if (posterUrl) posters[String(current.length)] = posterUrl;
                        }
                        const updatedPhotos = [...current, url];
                        await axios.put(`${API}/users/profile`, { featured_photos: updatedPhotos, featured_posters: posters });
                        setProfileUser(prev => ({ ...prev, featured_photos: updatedPhotos, featured_posters: posters }));
                        toast.success(`${isVideo ? 'Video' : 'Photo'} added`);
                      } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
                    };
                    input.click();
                  }}
                  data-testid="profile-add-featured-media"
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Photo / Video
                </Button>
              </div>
            )}

            {profileUser.featured_photos?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {profileUser.featured_photos.map((media, index) => {
                  const lower = (media || '').toLowerCase();
                  const isVid = lower.endsWith('.mp4') || lower.endsWith('.mov') || lower.endsWith('.webm') || lower.endsWith('.avi') || lower.includes('/video');
                  const posterUrl = isVid ? (profileUser.featured_posters || {})[String(index)] : null;
                  return (
                    <div 
                      key={index} 
                      className="aspect-square rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-200 cursor-pointer group relative"
                      onClick={() => !isVid && openLightbox(profileUser.featured_photos.filter(p => {
                        const l = (p || '').toLowerCase();
                        return !(l.endsWith('.mp4') || l.endsWith('.mov') || l.endsWith('.webm') || l.endsWith('.avi') || l.includes('/video'));
                      }), profileUser.featured_photos.filter(p => {
                        const l = (p || '').toLowerCase();
                        return !(l.endsWith('.mp4') || l.endsWith('.mov') || l.endsWith('.webm') || l.endsWith('.avi') || l.includes('/video'));
                      }).indexOf(media))}
                    >
                      {isVid ? (
                        <video 
                          src={resolveMediaUrl(media)}
                          poster={resolveMediaUrl(posterUrl)}
                          controls
                          preload="metadata"
                          className="w-full h-full object-cover"
                          data-testid={`featured-video-${index}`}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <img 
                          src={resolveMediaUrl(media)}
                          alt={`Featured ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          style={{ imageRendering: 'auto' }}
                          data-testid={`featured-photo-${index}`}
                          onError={(e) => handleImgError(e)}
                        />
                      )}
                      {isVid && (
                        <div className="absolute top-2 left-2 bg-black/60 rounded px-2 py-0.5 pointer-events-none">
                          <span className="text-xs text-white font-medium">Video</span>
                        </div>
                      )}
                      {/* Owner remove button */}
                      {isOwnProfile && (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            const photos = [...(profileUser.featured_photos || [])];
                            photos.splice(index, 1);
                            const oldPosters = profileUser.featured_posters || {};
                            const newPosters = {};
                            Object.keys(oldPosters).forEach(k => {
                              const ki = parseInt(k);
                              if (ki < index) newPosters[k] = oldPosters[k];
                              else if (ki > index) newPosters[String(ki - 1)] = oldPosters[k];
                            });
                            try {
                              await axios.put(`${API}/users/profile`, { featured_photos: photos, featured_posters: newPosters });
                              setProfileUser(prev => ({ ...prev, featured_photos: photos, featured_posters: newPosters }));
                              toast.success('Removed');
                            } catch { toast.error('Failed to remove'); }
                          }}
                          className="absolute top-2 right-2 bg-black/60 hover:bg-red-600 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          data-testid={`remove-featured-${index}`}
                        >
                          <X className="h-3 w-3 text-white" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">No featured media yet</p>
                {isOwnProfile && (
                  <p className="text-sm text-gray-500 mt-2">Use the button above to add photos or videos</p>
                )}
              </div>
            )}
          </TabsContent>

          {/* Workout Plans Tab */}
          <TabsContent value="workouts" className="space-y-6 mt-6">
            {workoutPlans.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {workoutPlans.map((plan) => {
                  const hasAccess = checkContentAccess(plan);
                  return (
                  <Card key={plan.id} className={`relative border-blue-100 hover:shadow-lg transition-all duration-200 ${hasAccess ? 'cursor-pointer' : ''}`}
                    onClick={() => hasAccess && setViewWorkoutPlan(plan)}
                    data-testid={`profile-workout-card-${plan.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{plan.title}</CardTitle>
                          <CardDescription className="mt-2">
                            {hasAccess ? plan.description : 
                              `${plan.description?.substring(0, 100)}... (Subscribe to view full content)`
                            }
                          </CardDescription>
                        </div>
                        {plan.price > 0 && (
                          <Badge className="bg-green-100 text-green-700 shrink-0 ml-2">
                            ${plan.price}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <span>Difficulty: {plan.difficulty_level}</span>
                        <span>Duration: {plan.duration_weeks} weeks</span>
                      </div>
                      
                      {hasAccess ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span><strong>Exercises:</strong> {plan.exercises?.length ?? 0}</span>
                            {(plan.exercises || []).some(e => e.demo_video_url) && (
                              <span className="text-blue-500 flex items-center gap-1"><Video className="h-3 w-3" /> Videos</span>
                            )}
                          </div>
                          <div className="text-xs text-center text-blue-500 font-medium pt-1">
                            Click to view full plan &rarr;
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Lock className="h-4 w-4 mr-2" />
                            Subscribe to access full workout plan
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <PaymentButton 
                              itemType="workout_plan"
                              itemId={plan.id}
                              price={plan.price}
                              title={plan.title}
                            />
                          </div>
                        </div>
                      )}
                      {isOwnProfile && (
                        <div className="absolute bottom-3 right-3 flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingWorkoutPlan(plan); }}
                            className="p-1.5 rounded-full text-gray-400 hover:text-blue-500 hover:bg-blue-50 transition-colors"
                            data-testid={`edit-workout-${plan.id}`}
                            title="Edit workout plan"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeletingPlan({ type: 'workout', id: plan.id, title: plan.title }); }}
                            className="p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            data-testid={`delete-workout-${plan.id}`}
                            title="Delete workout plan"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Dumbbell className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No workout plans yet</p>
              </div>
            )}
            {/* Workout Plan Detail View */}
            <WorkoutPlanDetailView plan={viewWorkoutPlan} open={!!viewWorkoutPlan} onClose={() => setViewWorkoutPlan(null)} />
          </TabsContent>

          {/* Meal Plans Tab */}
          <TabsContent value="meals" className="space-y-6 mt-6">
            {mealPlans.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {mealPlans.map((plan) => {
                  const hasAccess = checkContentAccess(plan);
                  return (
                  <Card key={plan.id} className={`relative border-green-100 hover:shadow-lg transition-all duration-200 ${hasAccess ? 'cursor-pointer' : ''}`}
                    onClick={() => hasAccess && setViewMealPlan(plan)}
                    data-testid={`profile-meal-card-${plan.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{plan.title}</CardTitle>
                          <CardDescription className="mt-2">
                            {hasAccess ? plan.description : 
                              `${plan.description?.substring(0, 100)}... (Subscribe to view full content)`
                            }
                          </CardDescription>
                        </div>
                        {plan.price > 0 && (
                          <Badge className="bg-green-100 text-green-700 shrink-0 ml-2">
                            ${plan.price}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <span>Duration: {plan.duration_days} days</span>
                        {plan.daily_calories && <span>{plan.daily_calories} cal/day</span>}
                      </div>
                      
                      {hasAccess ? (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-sm">
                            <span><strong>Meals:</strong> {plan.meals?.length ?? 0}</span>
                            {(plan.meals || []).some(m => m.photo_url) && (
                              <span className="text-green-500 flex items-center gap-1"><Camera className="h-3 w-3" /> Photos</span>
                            )}
                          </div>
                          <div className="text-xs text-center text-green-500 font-medium pt-1">
                            Click to view full plan &rarr;
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Lock className="h-4 w-4 mr-2" />
                            Subscribe to access full meal plan
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <PaymentButton 
                              itemType="meal_plan"
                              itemId={plan.id}
                              price={plan.price}
                              title={plan.title}
                            />
                          </div>
                        </div>
                      )}
                      {isOwnProfile && (
                        <div className="absolute bottom-3 right-3 flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditingMealPlan(plan); }}
                            className="p-1.5 rounded-full text-gray-400 hover:text-green-500 hover:bg-green-50 transition-colors"
                            data-testid={`edit-meal-${plan.id}`}
                            title="Edit meal plan"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeletingPlan({ type: 'meal', id: plan.id, title: plan.title }); }}
                            className="p-1.5 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            data-testid={`delete-meal-${plan.id}`}
                            title="Delete meal plan"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Apple className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No meal plans yet</p>
              </div>
            )}
            {/* Meal Plan Detail View */}
            <MealPlanDetailView plan={viewMealPlan} open={!!viewMealPlan} onClose={() => setViewMealPlan(null)} />
          </TabsContent>

          {/* Live Streams Tab */}
          <TabsContent value="streams" className="space-y-6 mt-6">
            {liveStreams.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-6">
                {liveStreams.map((stream) => (
                  <Card key={stream.id} className="border-blue-100 hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{stream.title}</CardTitle>
                          <CardDescription className="mt-2">
                            {checkContentAccess(stream) ? stream.description : 
                              `${stream.description?.substring(0, 100)}... (Subscribe to view full content)`
                            }
                          </CardDescription>
                        </div>
                        {stream.price > 0 && (
                          <Badge className="bg-green-100 text-green-700">
                            ${stream.price}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(stream.scheduled_time).toLocaleDateString()}
                        </span>
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {new Date(stream.scheduled_time).toLocaleTimeString()}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                        <span>Duration: {stream.duration_minutes} minutes</span>
                        <Badge className={`${
                          new Date(stream.scheduled_time) > new Date() 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {new Date(stream.scheduled_time) > new Date() ? 'Upcoming' : 'Past'}
                        </Badge>
                      </div>
                      
                      {checkContentAccess(stream) ? (
                        <div className="space-y-2">
                          <Button className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600">
                            <PlayCircle className="h-4 w-4 mr-2" />
                            {new Date(stream.scheduled_time) > new Date() ? 'Join Stream' : 'Watch Replay'}
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-500">
                            <Lock className="h-4 w-4 mr-2" />
                            Subscribe to access live streams
                          </div>
                          <PaymentButton 
                            itemType="stream"
                            itemId={stream.id}
                            price={stream.price}
                            title={stream.title}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <PlayCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No live streams yet</p>
              </div>
            )}
          </TabsContent>

          {/* Challenges Tab */}
          <TabsContent value="challenges" className="space-y-6 mt-6">
            <ProfileChallengesTab challenges={profileChallenges} currentUserId={currentUser?.id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Plan Confirmation Dialog */}
      <AlertDialog open={!!deletingPlan} onOpenChange={(open) => !open && setDeletingPlan(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this product?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deletingPlan?.title}</strong> will be permanently removed. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePlan}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Post Dialog (Profile) */}
      <EditPostDialog
        post={editingPost}
        editData={editPostData}
        setEditData={setEditPostData}
        onSave={handleSaveEditPost}
        onClose={() => setEditingPost(null)}
        onMediaUpload={handleEditPostMediaUpload}
        mediaUploading={postMediaUploading}
      />

      {/* Edit Workout Plan Dialog (Profile) */}
      <CreateWorkoutPlanDialog
        open={!!editingWorkoutPlan}
        onClose={() => setEditingWorkoutPlan(null)}
        editPlan={editingWorkoutPlan}
        onSuccess={async () => {
          try {
            const resp = await axios.get(`${BACKEND_URL}/api/workout-plans?creator_id=${userId}`);
            setWorkoutPlans(resp.data);
          } catch (e) { console.error(e); }
        }}
      />

      {/* Edit Meal Plan Dialog (Profile) */}
      <CreateMealPlanDialog
        open={!!editingMealPlan}
        onClose={() => setEditingMealPlan(null)}
        editPlan={editingMealPlan}
        onSuccess={async () => {
          try {
            const resp = await axios.get(`${BACKEND_URL}/api/meal-plans?creator_id=${userId}`);
            setMealPlans(resp.data);
          } catch (e) { console.error(e); }
        }}
      />

      {/* Bio Editor Modal */}
      <Dialog open={showBioEditor} onOpenChange={setShowBioEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Edit className="h-5 w-5 mr-2 text-blue-600" />
              Edit Your Bio
            </DialogTitle>
            <DialogDescription>
              Tell people about yourself, your fitness journey, and what they can expect from your content
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Bio</label>
              <textarea
                className="w-full min-h-32 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Hi! I'm passionate about fitness and helping others reach their goals. I specialize in..."
                value={bioText}
                onChange={(e) => setBioText(e.target.value)}
                maxLength={500}
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-gray-500">
                  Help people understand what you're about and what content you create
                </p>
                <p className="text-xs text-gray-400">
                  {bioText.length}/500 characters
                </p>
              </div>
            </div>

            {/* Bio Tips */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">💡 Great Bio Tips:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-700">
                <div>• Share your fitness background</div>
                <div>• Mention your specialties</div>
                <div>• Include your achievements</div>
                <div>• Add personality & emojis</div>
                <div>• Describe your training style</div>
                <div>• Share what motivates you</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  setBioText(profileUser.bio || '');
                  setShowBioEditor(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    await axios.put(`${BACKEND_URL}/api/users/profile`, {
                      bio: bioText.trim()
                    });
                    
                    // Refresh profile data (local state)
                    await fetchProfileData();
                    // Also update AuthContext so modal stays in sync
                    if (currentUser && profileUser && currentUser.id === userId) {
                      setUser(prev => ({ ...prev, bio: bioText.trim() }));
                      if (refreshCurrentUser) await refreshCurrentUser();
                    }
                    setShowBioEditor(false);
                    toast.success('Bio updated successfully!');
                  } catch (error) {
                    console.error('Failed to update bio:', error);
                    toast.error('Failed to update bio');
                  }
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              >
                Save Bio
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Subscription Settings Editor Modal */}
      <Dialog open={showSubscriptionEditor} onOpenChange={setShowSubscriptionEditor}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Settings className="h-5 w-5 mr-2 text-purple-600" />
              Subscription Settings
            </DialogTitle>
            <DialogDescription>
              Customize your subscription offering and benefits for your subscribers
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Pricing & Tier */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Tier Name</label>
                <Input
                  placeholder="e.g., Premium Coaching, Elite Training"
                  value={subscriptionSettings.subscription_tier_name}
                  onChange={(e) => setSubscriptionSettings(prev => ({
                    ...prev,
                    subscription_tier_name: e.target.value
                  }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Monthly Price ($)</label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="19.99"
                  value={subscriptionSettings.subscription_price}
                  onChange={(e) => setSubscriptionSettings(prev => ({
                    ...prev,
                    subscription_price: e.target.value
                  }))}
                  data-testid="subscription-price-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter 0 to make your subscription free.
                </p>
              </div>
            </div>

            {/* Benefits */}
            <div>
              <label className="text-sm font-medium mb-2 block">Subscription Benefits</label>
              <p className="text-sm text-gray-600 mb-3">List what subscribers get when they subscribe to you</p>
              
              {subscriptionSettings.subscription_benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <Input
                    placeholder="e.g., Free 1-on-1 consultation call monthly"
                    value={benefit}
                    onChange={(e) => {
                      const newBenefits = [...subscriptionSettings.subscription_benefits];
                      newBenefits[index] = e.target.value;
                      setSubscriptionSettings(prev => ({
                        ...prev,
                        subscription_benefits: newBenefits
                      }));
                    }}
                  />
                  {subscriptionSettings.subscription_benefits.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newBenefits = subscriptionSettings.subscription_benefits.filter((_, i) => i !== index);
                        setSubscriptionSettings(prev => ({
                          ...prev,
                          subscription_benefits: newBenefits
                        }));
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              
              <Button
                variant="outline"
                onClick={() => setSubscriptionSettings(prev => ({
                  ...prev,
                  subscription_benefits: [...prev.subscription_benefits, '']
                }))}
                className="mt-2"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Benefit
              </Button>
            </div>

            {/* Sample Benefits */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2">💡 Popular Benefit Ideas:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                <div>• Free monthly consultation calls</div>
                <div>• Exclusive live workout sessions</div>
                <div>• Custom workout/meal plans</div>
                <div>• Priority message responses</div>
                <div>• Access to private community</div>
                <div>• Weekly form check videos</div>
                <div>• Personalized nutrition guidance</div>
                <div>• Competition prep coaching</div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={() => setShowSubscriptionEditor(false)}>
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  try {
                    const cleanBenefits = subscriptionSettings.subscription_benefits.filter(b => b.trim() !== '');

                    // 0 is a valid price (free). Only a blank field means "unset",
                    // so never use a falsy fallback here.
                    const rawPrice = String(subscriptionSettings.subscription_price).trim();
                    let priceToSave = null;
                    if (rawPrice !== '') {
                      const parsed = Number(rawPrice);
                      if (Number.isNaN(parsed)) {
                        toast.error('Please enter a valid price');
                        return;
                      }
                      if (parsed < 0) {
                        toast.error('Subscription price cannot be negative');
                        return;
                      }
                      priceToSave = parsed;
                    }

                    await axios.put(`${BACKEND_URL}/api/users/subscription-settings`, {
                      subscription_price: priceToSave,
                      subscription_tier_name: subscriptionSettings.subscription_tier_name || null,
                      subscription_benefits: cleanBenefits
                    });
                    
                    // Refresh profile data + the canonical current user (sidebar)
                    await fetchProfileData();
                    if (refreshCurrentUser) await refreshCurrentUser();
                    setShowSubscriptionEditor(false);
                    toast.success(
                      priceToSave === 0
                        ? 'Subscription is now free'
                        : 'Subscription settings updated successfully!'
                    );
                  } catch (error) {
                    console.error('Failed to update subscription settings:', error);
                    toast.error('Failed to update subscription settings');
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                Save Settings
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Media Lightbox */}
      <MediaLightbox 
        images={lightbox.images}
        initialIndex={lightbox.index}
        open={lightbox.open}
        onClose={closeLightbox}
      />
    </div>
  );
};

// AI Recommendations Component
const AIRecommendations = () => {
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    user_goals: '',
    fitness_level: 'beginner',
    available_time: '30 minutes',
    equipment: 'home'
  });

  const getRecommendations = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/recommendations`, formData);
      setRecommendations(response.data);
      toast.success('AI recommendations generated!');
    } catch (error) {
      console.error('Error getting recommendations:', error);
      toast.error('Failed to get AI recommendations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-blue-100">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Target className="h-5 w-5 text-blue-600" />
          <span>AI Fitness Coach</span>
        </CardTitle>
        <CardDescription>Get personalized recommendations</CardDescription>
      </CardHeader>
      
      <CardContent>
        <div className="space-y-4">
          <Input
            placeholder="Your fitness goals..."
            value={formData.user_goals}
            onChange={(e) => setFormData({...formData, user_goals: e.target.value})}
          />
          
          <Select value={formData.fitness_level} onValueChange={(value) => setFormData({...formData, fitness_level: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="beginner">Beginner</SelectItem>
              <SelectItem value="intermediate">Intermediate</SelectItem>
              <SelectItem value="advanced">Advanced</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={formData.available_time} onValueChange={(value) => setFormData({...formData, available_time: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="15 minutes">15 minutes</SelectItem>
              <SelectItem value="30 minutes">30 minutes</SelectItem>
              <SelectItem value="1 hour">1 hour</SelectItem>
              <SelectItem value="2+ hours">2+ hours</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={formData.equipment} onValueChange={(value) => setFormData({...formData, equipment: value})}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="home">Home / Minimal</SelectItem>
              <SelectItem value="gym">Full Gym</SelectItem>
              <SelectItem value="outdoor">Outdoor</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            onClick={getRecommendations} 
            disabled={loading || !formData.user_goals}
            className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white"
          >
            {loading ? 'Generating...' : 'Get AI Recommendations'}
          </Button>
          
          {recommendations && (
            <div className="mt-4 space-y-3" data-testid="ai-coach-results">
              {/* Warm-up */}
              {recommendations.warmup && (
                <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-100">
                  <h4 className="font-semibold text-xs text-amber-800 mb-1 uppercase tracking-wide">Warm-up</h4>
                  <p className="text-xs text-amber-700">{recommendations.warmup}</p>
                </div>
              )}

              {/* Exercises */}
              {recommendations.exercises?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm text-blue-900 mb-2">Workout Plan</h4>
                  <div className="space-y-1.5">
                    {recommendations.exercises.map((ex, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg text-xs" data-testid={`exercise-${idx}`}>
                        <span className="w-5 h-5 flex-shrink-0 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-[10px]">{idx + 1}</span>
                        <span className="flex-1 font-medium text-gray-800">{ex.name}</span>
                        <span className="text-gray-500 tabular-nums whitespace-nowrap">
                          {ex.sets} x {ex.reps}
                        </span>
                        {ex.rest && <span className="text-gray-400 text-[10px] whitespace-nowrap">rest {ex.rest}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Cool-down */}
              {recommendations.cooldown && (
                <div className="p-2.5 bg-teal-50 rounded-lg border border-teal-100">
                  <h4 className="font-semibold text-xs text-teal-800 mb-1 uppercase tracking-wide">Cool-down</h4>
                  <p className="text-xs text-teal-700">{recommendations.cooldown}</p>
                </div>
              )}

              {/* Progression */}
              {recommendations.progression && (
                <div className="p-2.5 bg-purple-50 rounded-lg border border-purple-100">
                  <h4 className="font-semibold text-xs text-purple-800 mb-1 uppercase tracking-wide">Next Week</h4>
                  <p className="text-xs text-purple-700">{recommendations.progression}</p>
                </div>
              )}

              {/* Nutrition tip */}
              {recommendations.nutrition_tip && (
                <div className="p-2.5 bg-green-50 rounded-lg border border-green-100">
                  <h4 className="font-semibold text-xs text-green-800 mb-1 uppercase tracking-wide">Nutrition Tip</h4>
                  <p className="text-xs text-green-700">{recommendations.nutrition_tip}</p>
                </div>
              )}

              {/* Legacy format support (workouts array of strings) */}
              {!recommendations.exercises && recommendations.workouts && (
                <div>
                  <h4 className="font-semibold text-sm text-blue-900 mb-2">Workouts:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {recommendations.workouts.map((w, idx) => (
                      <li key={idx} className="flex items-start space-x-1"><span className="text-blue-500">&bull;</span><span>{w}</span></li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Motivation */}
              {recommendations.motivation && (
                <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-900 font-medium">{recommendations.motivation}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Auth Handler Component
const AuthHandler = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const handleAuth = async () => {
      // Check both URL parameters and hash for session_id
      const urlParams = new URLSearchParams(location.search);
      const hashParams = new URLSearchParams(location.hash.substring(1));
      const sessionId = urlParams.get('session_id') || hashParams.get('session_id');

      if (sessionId) {
        try {
          const response = await axios.post(`${API}/auth/session-data`, {}, {
            headers: { 'X-Session-ID': sessionId }
          });

          const { user, session_token } = response.data;
          login(user, session_token);

          toast.success('Login successful!');

          // Show brief success animation before redirect
          setTimeout(() => {
            // Clean URL
            window.history.replaceState({}, document.title, '/dashboard');
            navigate('/dashboard', { replace: true });
          }, 1500);

        } catch (error) {
          toast.error(error.response?.data?.detail || 'Authentication failed');
          setIsProcessing(false);
          setTimeout(() => navigate('/', { replace: true }), 2000);
        }
      } else {
        // If no session_id, redirect to home
        toast.error('No authentication session found');
        setIsProcessing(false);
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    };

    handleAuth();
  }, [location.search, location.hash, login, navigate]);

  if (!isProcessing) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-cyan-50 flex items-center justify-center">
      <div className="text-center space-y-6">
        <div className="relative">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto animate-pulse">
            <Dumbbell className="h-8 w-8 text-white animate-bounce" />
          </div>
          <div className="absolute -inset-2 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl blur-lg opacity-30 animate-pulse"></div>
        </div>
        <div className="space-y-2">
          <h3 className="text-xl font-semibold text-gray-900">Welcome to OnlyMass!</h3>
          <p className="text-gray-600">Setting up your fitness journey...</p>
          <div className="w-32 h-1 bg-blue-100 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
            <Dumbbell className="h-6 w-6 text-white" />
          </div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? children : <Navigate to="/" replace />;
};

// PageErrorBoundary — catches any uncaught render exception inside a routed
// page so the whole app doesn't blank-screen. Shows a clean fallback with a
// Retry button and surfaces the actual error message + stack (gated behind
// a "Show details" toggle) for debugging in production without exposing the
// full React stack to casual visitors.
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, showDetails: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // Use error-level so it shows up in production browser console
    // and (via standard error pipelines) in any monitoring tooling.
    try {
      console.error('[PageErrorBoundary] Caught render error:', error?.message, error?.name);
    } catch (e) { /* noop */ }
    this.setState({ info });
  }
  reset = () => {
    this.setState({ hasError: false, error: null, info: null, showDetails: false });
  };
  render() {
    if (!this.state.hasError) return this.props.children;
    const errMsg = this.state.error?.message || 'An unexpected error occurred';
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6" data-testid="page-error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-red-100 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="h-7 w-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Something went wrong</h1>
          <p className="text-sm text-slate-600 mb-4">
            We couldn&apos;t render this page. Please try again. If the issue persists,
            email <a href="mailto:support@onlymass.net" className="text-blue-600 underline">support@onlymass.net</a>.
          </p>
          <div className="flex gap-2 justify-center mb-4">
            <Button onClick={this.reset} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white" data-testid="page-error-retry-btn">
              Try again
            </Button>
            <Button variant="outline" onClick={() => (window.location.href = '/dashboard')} data-testid="page-error-home-btn">
              Back to dashboard
            </Button>
          </div>
          <button
            onClick={() => this.setState(s => ({ showDetails: !s.showDetails }))}
            className="text-xs text-slate-400 hover:text-slate-600 underline"
            data-testid="page-error-details-toggle"
          >
            {this.state.showDetails ? 'Hide details' : 'Show details'}
          </button>
          {this.state.showDetails && (
            <pre className="mt-3 text-[10px] text-left text-slate-500 bg-slate-50 p-3 rounded overflow-auto max-h-40">
              {errMsg}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

// AdminRoute — requires logged-in user AND backend-verified admin.
// Hits GET /api/admin/check; on 403 or non-admin, renders a clean access-denied
// screen with a back-to-dashboard CTA (no raw error JSON).
const AdminRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ checking: true, allowed: false });

  React.useEffect(() => {
    if (authLoading) return;
    if (!user) { setState({ checking: false, allowed: false }); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/check`);
        if (!cancelled) setState({ checking: false, allowed: data?.is_admin === true });
      } catch (e) {
        if (!cancelled) setState({ checking: false, allowed: false });
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, user]);

  if (authLoading || state.checking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center animate-pulse">
            <Dumbbell className="h-6 w-6 text-white" />
          </div>
          <p className="text-gray-600">Verifying access…</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/" replace />;

  if (!state.allowed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-6" data-testid="admin-access-denied">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-100 p-8 text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-7 w-7 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access denied</h1>
          <p className="text-sm text-slate-600 mb-6">
            This area is restricted to OnlyMass administrators. If you believe this is a mistake,
            email <a href="mailto:support@onlymass.net" className="text-blue-600 underline">support@onlymass.net</a>.
          </p>
          <Button onClick={() => navigate('/dashboard', { replace: true })} className="bg-[#0f1d3d] hover:bg-[#1a2d55] text-white" data-testid="admin-access-denied-back-btn">
            Back to dashboard
          </Button>
        </div>
      </div>
    );
  }

  return children;
};


// AppRouter - Handles OAuth callback before normal routing
const PUBLIC_PATHS = ['/', '/about', '/careers', '/support', '/terms', '/privacy', '/community-guidelines', '/copyright', '/cookies', '/invest'];

const AppRouter = () => {
  const location = useLocation();
  const { user, sessionToken } = useAuth();
  
  // Scroll to top on route change + scope dark mode to authenticated app only
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const isPublic = PUBLIC_PATHS.includes(location.pathname) || location.pathname.startsWith('/support/');
    if (isPublic) {
      document.documentElement.classList.remove('dark');
    } else {
      const saved = localStorage.getItem('onlymass-theme');
      if (saved === 'dark') document.documentElement.classList.add('dark');
    }
  }, [location.pathname]);
  
  // Check for session_id in URL hash (synchronous check before routing)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Normal routing
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/signup" element={<LandingPage />} />
      <Route path="/auth-callback" element={<AuthHandler />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <PageErrorBoundary>
            <Dashboard />
          </PageErrorBoundary>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard user={user} token={sessionToken} />
        </AdminRoute>
      } />
      <Route path="/profile/:userId" element={<UserProfilePage />} />
      <Route path="/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/payment-cancelled" element={<PaymentCancelledPage />} />
      <Route path="/subscription-success" element={<SubscriptionSuccessPage />} />
      <Route path="/subscription-cancelled" element={<SubscriptionCancelledPage />} />
      <Route path="/tip-success" element={<TipSuccessPage />} />
      <Route path="/tip-cancelled" element={<TipCancelledPage />} />
      <Route path="/my-subscribers" element={
        <ProtectedRoute><PageErrorBoundary><MySubscribersPage /></PageErrorBoundary></ProtectedRoute>
      } />
      <Route path="/my-subscriptions" element={
        <ProtectedRoute><PageErrorBoundary><MySubscriptionsPage /></PageErrorBoundary></ProtectedRoute>
      } />
      <Route path="/purchase-history" element={
        <ProtectedRoute><PageErrorBoundary><PurchaseHistoryPage /></PageErrorBoundary></ProtectedRoute>
      } />
      <Route path="/connections" element={
        <ProtectedRoute><PageErrorBoundary><FollowersFollowingPage /></PageErrorBoundary></ProtectedRoute>
      } />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/support/:slug" element={<SupportArticlePage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/copyright" element={<CopyrightPage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/invest" element={<InvestPage />} />
      <Route path="/creator/settings" element={<Navigate to="/" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

// Main App Component
function App() {
  // Initialize theme from localStorage or system preference
  React.useEffect(() => {
    const saved = localStorage.getItem('onlymass-theme');
    if (saved === 'dark') {
      document.documentElement.classList.add('dark');
    } else if (saved === 'light') {
      document.documentElement.classList.remove('dark');
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      document.documentElement.classList.add('dark');
    }
    // Initialize accent theme
    const accent = localStorage.getItem('onlymass-accent') || 'blue';
    document.documentElement.setAttribute('data-accent', accent);
  }, []);
  if (BACKEND_MISCONFIGURED) {
    return (
      <div data-testid="backend-misconfigured-banner" style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#18181b', color: '#fafafa', fontFamily: 'system-ui, sans-serif'
      }}>
        <div style={{ textAlign: 'center', maxWidth: 480, padding: 32 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#9888;</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Backend Misconfigured</h1>
          <p style={{ color: '#a1a1aa', lineHeight: 1.6 }}>
            The app is pointing to a preview URL instead of production.<br/>
            <code style={{ background: '#27272a', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>
              {BACKEND_URL || '(empty)'}
            </code>
          </p>
          <p style={{ marginTop: 16, color: '#ef4444', fontWeight: 600 }}>Contact admin to fix deployment config.</p>
        </div>
      </div>
    );
  }

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <SeoManager />
          <AppRouter />
          <Toaster position="top-right" />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;